import { db } from "./dbClient";

import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { downloadCSV, printToPDF } from "./exportUtils";
import { useTheme } from "./theme";
import { useAuditLog } from "./useAuditLog";
import { callAI } from "./aiClient";
import { AMLModel } from "./AMLModel";
import { AMLModelInsights } from "./AMLModelInsights";
import { AMLModelManager } from "./AMLModelManager";
import { loadCompanyKeywords, analyzeTransactionForRedFlags } from "./keywordMatcher";

const PROXY = (import.meta.env.VITE_PROXY_URL || "http://127.0.0.1:3001").replace(/\/+$/, "");
const VERCEL_BYPASS_SECRET = import.meta.env.VITE_VERCEL_BYPASS_SECRET || import.meta.env.VERCEL_BYPASS_SECRET;

// Helper function to add bypass secret to API headers
function getApiHeaders(baseHeaders = {}) {
  const headers = { ...baseHeaders };
  if (VERCEL_BYPASS_SECRET) {
    headers['x-vercel-protection-bypass'] = VERCEL_BYPASS_SECRET;
  }
  return headers;
}

// ─── ENTITY RESOLUTION (for crypto enrichment & customer matching) ────────────
const _entityCache = new Map();
let _customerDatabase = []; // Global customer cache

// Fuzzy match entity names against customer database
function matchCustomerEntity(entityName) {
  if (!entityName || entityName.length < 2) return null;

  const name = entityName.toLowerCase().trim();

  // Exact match (highest priority)
  const exact = _customerDatabase.find(c => c.full_name && c.full_name.toLowerCase() === name);
  if (exact) return { label: exact.full_name, type: "customer", riskScore: exact.risk_score || 0, customerId: exact.id };

  // Partial match (name contains entity or entity contains name)
  const partial = _customerDatabase.find(c =>
    c.full_name && (c.full_name.toLowerCase().includes(name) || name.includes(c.full_name.toLowerCase()))
  );
  if (partial) return { label: partial.full_name, type: "customer", riskScore: partial.risk_score || 0, customerId: partial.id, matched: "partial" };

  return null;
}

async function resolveWalletEntity(addr) {
  if (!addr || addr.length < 10) return { label: "Unknown", type: "unknown", riskScore: 40 };
  const key = addr.toLowerCase();
  if (_entityCache.has(key)) return _entityCache.get(key);

  const MIXER_KEYWORDS    = ["tornado","mixer","tumbler","chipmixer","wasabi","coinjoin","sinbad","blender"];
  const DARKNET_KEYWORDS  = ["hydra","alphabay","silkroad","darknet","darkweb","illicit"];
  const EXCHANGE_KEYWORDS = ["binance","coinbase","kraken","huobi","okx","bybit","gemini","kucoin","bitfinex","ftx"];
  const SANCTIONS_TYPES   = ["sanctioned","ofac","sdnlist"];

  const classify = (label = "", entityType = "") => {
    const l = (label + " " + entityType).toLowerCase();
    if (SANCTIONS_TYPES.some(k => l.includes(k)))   return { type: "sanctioned",  riskScore: 100 };
    if (MIXER_KEYWORDS.some(k => l.includes(k)))     return { type: "mixer",       riskScore: 95  };
    if (DARKNET_KEYWORDS.some(k => l.includes(k)))   return { type: "darknet",     riskScore: 95  };
    if (EXCHANGE_KEYWORDS.some(k => l.includes(k)))  return { type: "exchange",    riskScore: 15  };
    if (l.includes("unhosted") || l.includes("self-custod")) return { type: "unhosted", riskScore: 45 };
    if (l.includes("defi") || l.includes("protocol") || l.includes("smart contract")) return { type: "defi", riskScore: 30 };
    return null;
  };

  if (PROXY) {
    // 1. eth-labels lookup
    try {
      const r = await fetch(`${PROXY}/api/labels/${key}`, { signal: AbortSignal.timeout(4000) });
      if (r.ok) {
        const d = await r.json();
        const rawLabel = d.nameTag || d.label || d.labelType || "";
        if (rawLabel) {
          const classified = classify(rawLabel, d.labelType || "");
          const result = { label: rawLabel, type: classified?.type || "known", riskScore: classified?.riskScore ?? 30 };
          _entityCache.set(key, result);
          return result;
        }
      }
    } catch {}

    // 2. Chainalysis sanctions check
    try {
      const r = await fetch(`${PROXY}/api/chainalysis/${key}`, { signal: AbortSignal.timeout(4000) });
      if (r.ok) {
        const d = await r.json();
        const ids = d.identifications || [];
        if (ids.length > 0) {
          const result = { label: ids[0].name || "OFAC Sanctioned", type: "sanctioned", riskScore: 100 };
          _entityCache.set(key, result);
          return result;
        }
        const result = { label: "Untagged Wallet", type: "unhosted", riskScore: 40 };
        _entityCache.set(key, result);
        return result;
      }
    } catch {}
  }

  const result = { label: "Untagged Wallet", type: "unhosted", riskScore: 40 };
  _entityCache.set(key, result);
  return result;
}

async function enrichCryptoTx(tx) {
  if (tx.enriched) return tx;
  try {
    // Handle crypto transactions
    if (tx.type === "Crypto") {
      const [fromInfo, toInfo] = await Promise.all([
        resolveWalletEntity(tx.walletFrom || ""),
        resolveWalletEntity(tx.walletTo   || ""),
      ]);
      const counterpartyLabel = toInfo.label !== "Untagged Wallet" ? toInfo.label : fromInfo.label;
      return {
        ...tx,
        fromLabel:       fromInfo.label,
        fromEntityType:  fromInfo.type,
        fromRiskScore:   fromInfo.riskScore,
        toLabel:         toInfo.label,
        toEntityType:    toInfo.type,
        toRiskScore:     toInfo.riskScore,
        cryptoEntity:    counterpartyLabel,
        entity:          tx.entity || counterpartyLabel || tx.walletTo || tx.walletFrom || "Unknown Wallet",
        enriched:        true,
        enrichmentError: null,
      };
    }

    // Handle regular transactions - match entity name against customers
    if (tx.entity) {
      const customerMatch = matchCustomerEntity(tx.entity);
      if (customerMatch) {
        return {
          ...tx,
          entity:          customerMatch.label,
          entityType:      customerMatch.type,
          riskScore:       customerMatch.riskScore,
          customerId:      customerMatch.customerId,
          entityMatched:   true,
          enriched:        true,
          enrichmentError: null,
        };
      }
    }

    return { ...tx, enriched: true, enrichmentError: null };
  } catch (e) {
    return { ...tx, enriched: true, enrichmentError: e.message };
  }
}

// ─── DEFAULT RULE SETS ────────────────────────────────────────────────────────
const DEFAULT_RULES = [
  // ── BANK / TRADITIONAL FI ──
  {
    id: "BANK-001", category: "Bank", enabled: true, severity: "HIGH",
    name: "Large Cash Transaction",
    description: "Single cash transaction at or above CTR reporting threshold ($10,000+)",
    logic: "amount >= threshold AND payment_method IN ['cash','ATM']",
    threshold: 10000, thresholdCurrency: "USD",
    fields: [{ key: "threshold", label: "Amount Threshold (USD)", type: "number" }],
    reference: "FinCEN 31 CFR 1010.311 / FATF R.29",
    tags: ["cash", "CTR"],
  },
  {
    id: "BANK-002", category: "Bank", enabled: true, severity: "HIGH",
    name: "Suspicious Structuring Pattern",
    description: "Multiple cash/wire transactions just below reporting threshold within 7 days, total >= $20,000",
    logic: "SUM(amount, window_days) >= structuring_total AND MAX(amount) < threshold AND COUNT >= min_count",
    threshold: 9500, thresholdCurrency: "USD",
    structuringTotal: 20000, windowDays: 7, minCount: 3,
    fields: [
      { key: "threshold", label: "Single Tx Ceiling (USD)", type: "number" },
      { key: "structuringTotal", label: "Aggregate Total (USD)", type: "number" },
      { key: "windowDays", label: "Rolling Window (days)", type: "number" },
      { key: "minCount", label: "Min Transaction Count", type: "number" },
    ],
    reference: "31 USC 5324 / FATF R.29",
    tags: ["structuring", "smurfing"],
  },
  {
    id: "BANK-003", category: "Bank", enabled: false, severity: "LOW",
    name: "Round Dollar Amounts (DISABLED)",
    description: "⚠️ DISABLED: Too many false positives. Round amounts are common in legitimate business.",
    logic: "amount % round_multiple == 0 AND amount >= min_amount",
    roundMultiple: 5000, minAmount: 50000,
    fields: [
      { key: "roundMultiple", label: "Round Multiple (USD)", type: "number" },
      { key: "minAmount", label: "Minimum Amount (USD)", type: "number" },
    ],
    reference: "JMLSG Part I 6.32",
    tags: ["round amounts", "layering"],
  },
  {
    id: "BANK-004", category: "Bank", enabled: true, severity: "HIGH",
    name: "OFAC Sanctioned Jurisdiction",
    description: "Transaction to/from OFAC-sanctioned countries (Iran, North Korea, Syria, Cuba, Belarus, Russia)",
    logic: "destination_country IN high_risk_countries AND amount >= threshold",
    threshold: 0,
    highRiskCountries: [
      "Iran", "North Korea", "Syria", "Cuba", "Belarus", "Russia", "Myanmar"
    ],
    fields: [
      { key: "threshold", label: "Min Amount (USD)", type: "number" },
      { key: "highRiskCountries", label: "Sanctioned Countries", type: "tags" },
    ],
    reference: "OFAC SDN List",
    tags: ["OFAC", "sanctions"],
  },
  {
    id: "BANK-005", category: "Bank", enabled: false, severity: "MEDIUM",
    name: "Large Single Transaction (DISABLED)",
    description: "⚠️ DISABLED: Redundant with other rules. Enable only for specific high-risk entities.",
    logic: "amount >= maxVolume",
    maxCount: 20, windowHours: 24, maxVolume: 250000,
    fields: [
      { key: "maxVolume", label: "Max Volume Per Transaction (USD)", type: "number" },
    ],
    reference: "FATF R.10 / JMLSG 5.3.7",
    tags: ["large transaction", "volume"],
  },
  {
    id: "BANK-006", category: "Bank", enabled: true, severity: "HIGH",
    name: "PEP Transaction Above Threshold",
    description: "Transaction by Politically Exposed Person >= $10,000 requires enhanced due diligence",
    logic: "customer_pep == true AND amount >= threshold",
    threshold: 10000,
    fields: [{ key: "threshold", label: "PEP Alert Threshold (USD)", type: "number" }],
    reference: "FATF R.12 / FCA SYSC 6.3",
    tags: ["PEP", "EDD"],
  },
  {
    id: "BANK-007", category: "Bank", enabled: false, severity: "MEDIUM",
    name: "Dormant Account Sudden Activity (DISABLED)",
    description: "⚠️ DISABLED: Often legitimate (seasonal businesses, reactivated accounts). Enable for high-risk customers only.",
    logic: "days_since_last_tx >= dormant_days AND amount >= threshold",
    dormantDays: 180, threshold: 25000,
    fields: [
      { key: "dormantDays", label: "Inactivity Period (days)", type: "number" },
      { key: "threshold", label: "Credit Threshold (USD)", type: "number" },
    ],
    reference: "JMLSG 5.3.23 / FATF R.10",
    tags: ["dormant", "unusual activity"],
  },
  {
    id: "BANK-008", category: "Bank", enabled: false, severity: "MEDIUM",
    name: "Multiple Cross-Border Transfers Same Day",
    description: "Three or more international transfers in a single day",
    logic: "COUNT(international_transfers, 1 day) >= min_count",
    minCount: 3,
    fields: [{ key: "minCount", label: "Min Cross-Border Count", type: "number" }],
    reference: "FATF R.16 / EU AMLD5 Art.18",
    tags: ["cross-border", "international"],
  },

  // ── FINTECH ──
  {
    id: "FT-001", category: "Fintech", enabled: false, severity: "MEDIUM",
    name: "Rapid Fund Movement (DISABLED)",
    description: "⚠️ DISABLED: Too many false positives with legitimate market-making/trading. Enable only for specific entities.",
    logic: "time_between_in_out_minutes <= max_minutes AND amount >= threshold",
    maxMinutes: 5, threshold: 50000,
    fields: [
      { key: "maxMinutes", label: "Max Minutes Between In/Out", type: "number" },
      { key: "threshold", label: "Min Amount (USD)", type: "number" },
    ],
    reference: "FATF R.29 / FCA TR-2020",
    tags: ["layering", "rapid movement"],
  },
  {
    id: "FT-002", category: "Fintech", enabled: false, severity: "LOW",
    name: "Unusual P2P Payment Pattern (DISABLED)",
    description: "⚠️ DISABLED: Too broad for marketplace/payroll use cases. Enable only for unusual combinations.",
    logic: "COUNT(unique_recipients, window_days) > max_recipients AND AVG(amount) < avg_threshold",
    maxRecipients: 25, windowDays: 7, avgThreshold: 200,
    fields: [
      { key: "maxRecipients", label: "Max Unique Recipients", type: "number" },
      { key: "windowDays", label: "Window (days)", type: "number" },
      { key: "avgThreshold", label: "Avg Tx Ceiling (USD)", type: "number" },
    ],
    reference: "FATF R.10 / FCA PS19/26",
    tags: ["P2P", "money mule"],
  },
  {
    id: "FT-003", category: "Fintech", enabled: true, severity: "HIGH",
    name: "New Account High-Value Activity",
    description: "Account less than 14 days old with transaction >= $25,000",
    logic: "account_age_days <= max_age AND amount >= threshold",
    maxAge: 14, threshold: 25000,
    fields: [
      { key: "maxAge", label: "Max Account Age (days)", type: "number" },
      { key: "threshold", label: "Min Amount (USD)", type: "number" },
    ],
    reference: "FATF R.10 / FCA SYSC 6.3",
    tags: ["new account", "KYC"],
  },
  {
    id: "FT-004", category: "Fintech", enabled: false, severity: "MEDIUM",
    name: "Device / IP Anomaly (DISABLED)",
    description: "⚠️ DISABLED: Requires IP geolocation integration. Enable only with supporting systems.",
    logic: "ip_country != kyc_country AND amount >= threshold",
    threshold: 10000,
    fields: [{ key: "threshold", label: "Min Transaction Amount (USD)", type: "number" }],
    reference: "FCA SYSC 6.1 / EBA GL on ICT Risk",
    tags: ["IP anomaly", "fraud"],
  },
  {
    id: "FT-005", category: "Fintech", enabled: false, severity: "LOW",
    name: "Multiple Failed Payment Retries (DISABLED)",
    description: "⚠️ DISABLED: Requires transaction attempt data. Enable with payment processor integration.",
    logic: "failed_attempts_before_success >= min_failures",
    minFailures: 10,
    fields: [{ key: "minFailures", label: "Min Failed Attempts", type: "number" }],
    reference: "FCA FG18/4",
    tags: ["fraud", "account testing"],
  },

  // ── CRYPTO ──
  {
    id: "CRYPTO-001", category: "Crypto", enabled: true, severity: "CRITICAL",
    name: "Interaction with Mixer / Tumbler",
    description: "Transaction to/from known cryptocurrency mixing service (Tornado Cash, ChipMixer, Sinbad, etc.)",
    logic: "counterparty_address IN mixer_addresses OR entity_label IN ['mixer','tumbler','obfuscation']",
    knownMixers: ["Tornado Cash", "ChipMixer", "Sinbad", "Blender.io", "Wasabi Wallet CJ"],
    fields: [{ key: "knownMixers", label: "Known Mixer Labels (comma-separated)", type: "tags" }],
    reference: "FATF R.15 / FinCEN FIN-2022-Alert001 / OFAC SDN",
    tags: ["mixer", "obfuscation", "OFAC"],
  },
  {
    id: "CRYPTO-002", category: "Crypto", enabled: true, severity: "CRITICAL",
    name: "OFAC/Sanctioned Address",
    description: "Transaction involving an address on OFAC SDN or equivalent sanctions list",
    logic: "address IN ofac_sdn_list OR chainalysis_risk == 'severe'",
    fields: [],
    reference: "OFAC SDN / FinCEN / FATF R.6",
    tags: ["OFAC", "sanctions", "SDN"],
  },
  {
    id: "CRYPTO-003", category: "Crypto", enabled: true, severity: "HIGH",
    name: "Darknet Market Exposure",
    description: "Funds traceable to or from known darknet marketplace addresses",
    logic: "chainalysis_category IN ['darknet market','illicit actor'] AND hops <= max_hops",
    maxHops: 3,
    fields: [{ key: "maxHops", label: "Max Hops from Darknet (0 = direct)", type: "number" }],
    reference: "FATF R.15 / FinCEN CVC Guidance 2019",
    tags: ["darknet", "illicit"],
  },
  {
    id: "CRYPTO-004", category: "Crypto", enabled: true, severity: "HIGH",
    name: "Whale Transaction Above Threshold",
    description: "Single crypto transaction above large-value threshold requiring enhanced review",
    logic: "amount_usd >= threshold",
    threshold: 100000,
    fields: [{ key: "threshold", label: "Threshold (USD equivalent)", type: "number" }],
    reference: "FATF R.16 Travel Rule / MiCA Art.68",
    tags: ["large value", "whale"],
  },
  {
    id: "CRYPTO-005", category: "Crypto", enabled: false, severity: "HIGH",
    name: "Cross-Chain Bridge Rapid Movement (DISABLED)",
    description: "⚠️ DISABLED: Legitimate for liquidity/arbitrage. Enable for suspicious patterns only.",
    logic: "bridge_count >= min_bridges AND time_window_hours <= max_hours",
    minBridges: 2, maxHours: 24,
    fields: [
      { key: "minBridges", label: "Min Bridge Hops", type: "number" },
      { key: "maxHours", label: "Window (hours)", type: "number" },
    ],
    reference: "FATF R.15 / FinCEN CVC 2019",
    tags: ["bridge", "cross-chain", "layering"],
  },
  {
    id: "CRYPTO-006", category: "Crypto", enabled: false, severity: "MEDIUM",
    name: "NFT Wash Trading Pattern (DISABLED)",
    description: "⚠️ DISABLED: Too noisy for general monitoring. Enable only for suspected wash trading.",
    logic: "buyer == seller OR linked_wallets_traded AND price_increase_pct >= threshold",
    threshold: 100,
    fields: [{ key: "threshold", label: "Min Price Increase % per Cycle", type: "number" }],
    reference: "FATF R.15 / FinCEN NFT Guidance 2022",
    tags: ["NFT", "wash trading", "layering"],
  },
  {
    id: "CRYPTO-007", category: "Crypto", enabled: true, severity: "HIGH",
    name: "Unhosted Wallet Large Transfer",
    description: "Transfer above threshold to/from unhosted (self-custodied) wallet",
    logic: "counterparty_type == 'unhosted' AND amount_usd >= threshold",
    threshold: 3000,
    fields: [{ key: "threshold", label: "Threshold (USD)", type: "number" }],
    reference: "EU TFR 2023 / FinCEN NPRM 2020 / FATF R.16",
    tags: ["unhosted wallet", "Travel Rule"],
  },
  {
    id: "CRYPTO-008", category: "Crypto", enabled: false, severity: "MEDIUM",
    name: "Rapid DeFi Protocol Cycling",
    description: "Funds cycled through 3+ DeFi protocols within short window — layering via DeFi",
    logic: "defi_protocol_hops >= min_hops AND time_hours <= max_hours",
    minHops: 3, maxHours: 6,
    fields: [
      { key: "minHops", label: "Min Protocol Hops", type: "number" },
      { key: "maxHours", label: "Window (hours)", type: "number" },
    ],
    reference: "FATF R.15 Updated Guidance 2021",
    tags: ["DeFi", "layering"],
  },

  // ── PAYMENTS / E-MONEY ──
  {
    id: "PAY-001", category: "Payments", enabled: false, severity: "MEDIUM",
    name: "Card Testing Pattern (DISABLED)",
    description: "⚠️ DISABLED: Requires card data & processor integration. Enable with payment systems only.",
    logic: "first_tx_amount < test_threshold AND second_tx_amount >= large_threshold AND time_gap_minutes <= max_gap",
    testThreshold: 2, largeThreshold: 1000, maxGap: 30,
    fields: [
      { key: "testThreshold", label: "Test Amount Ceiling (USD)", type: "number" },
      { key: "largeThreshold", label: "Large Tx Floor (USD)", type: "number" },
      { key: "maxGap", label: "Max Time Gap (minutes)", type: "number" },
    ],
    reference: "FCA FG18/4 / PCI DSS",
    tags: ["card fraud", "testing"],
  },
  {
    id: "PAY-002", category: "Payments", enabled: false, severity: "LOW",
    name: "Aggregated Micro-Transactions (DISABLED)",
    description: "⚠️ DISABLED: Too broad for e-commerce/marketplace. Enable for suspicious payment chains only.",
    logic: "COUNT(transactions, window_hours) >= min_count AND each_amount < micro_ceiling AND SUM >= aggregate_threshold",
    minCount: 100, microCeiling: 100, aggregateThreshold: 50000, windowHours: 24,
    fields: [
      { key: "minCount", label: "Min Transaction Count", type: "number" },
      { key: "microCeiling", label: "Micro-Tx Ceiling (USD)", type: "number" },
      { key: "aggregateThreshold", label: "Aggregate Threshold (USD)", type: "number" },
      { key: "windowHours", label: "Window (hours)", type: "number" },
    ],
    reference: "FATF R.29 / EU AMLD5",
    tags: ["micro-transactions", "structuring"],
  },

  // ── INVESTMENT / SECURITIES ──
  {
    id: "INV-001", category: "Investment", enabled: false, severity: "MEDIUM",
    name: "Rapid In-and-Out Securities Trades (DISABLED)",
    description: "⚠️ DISABLED: Legitimate for traders/market-makers. Enable only for high-risk entities with poor economic motive.",
    logic: "buy_to_sell_hours <= max_hours AND profit_pct <= max_profit AND amount >= threshold",
    maxHours: 4, maxProfit: 2, threshold: 500000,
    fields: [
      { key: "maxHours", label: "Max Hours Buy→Sell", type: "number" },
      { key: "maxProfit", label: "Max Profit % (economic motive check)", type: "number" },
      { key: "threshold", label: "Min Trade Value (USD)", type: "number" },
    ],
    reference: "FATF R.26 / FCA MAR / SEC Rule 10b-5",
    tags: ["capital markets", "layering"],
  },
  {
    id: "INV-002", category: "Investment", enabled: false, severity: "MEDIUM",
    name: "Third-Party Payment for Investment",
    description: "Investment account funded by third party not matching account holder — possible nominee arrangement",
    logic: "payer_name != account_holder_name AND amount >= threshold",
    threshold: 10000,
    fields: [{ key: "threshold", label: "Min Amount (USD)", type: "number" }],
    reference: "FATF R.10 / JMLSG Part II 1.7.33",
    tags: ["third-party", "nominee"],
  },
];

// ─── SAMPLE TRANSACTIONS ──────────────────────────────────────────────────────
const SAMPLE_TRANSACTIONS = [
  { id: "TX-001", date: "2024-03-08", entity: "Orion Tech Ltd", amount: 9800, currency: "USD", type: "Wire", direction: "out", country: "UAE", paymentMethod: "wire", accountAge: 120, pepFlag: false, description: "Supplier payment" },
  { id: "TX-002", date: "2024-03-08", entity: "Orion Tech Ltd", amount: 9700, currency: "USD", type: "Wire", direction: "out", country: "UAE", paymentMethod: "wire", accountAge: 120, pepFlag: false, description: "Supplier payment" },
  { id: "TX-003", date: "2024-03-08", entity: "Orion Tech Ltd", amount: 9600, currency: "USD", type: "Wire", direction: "out", country: "UAE", paymentMethod: "wire", accountAge: 120, pepFlag: false, description: "Supplier payment" },
  { id: "TX-004", date: "2024-03-07", entity: "Marco Fernandez", amount: 48200, currency: "USD", type: "Wire", direction: "in", country: "Colombia", paymentMethod: "wire", accountAge: 45, pepFlag: false, description: "Business receipts" },
  { id: "TX-005", date: "2024-03-06", entity: "BlueStar Holdings", amount: 2100000, currency: "USD", type: "Cash", direction: "in", country: "BVI", paymentMethod: "cash", accountAge: 200, pepFlag: false, description: "Cash deposit" },
  { id: "TX-006", date: "2024-03-06", entity: "Chen Wei", amount: 9800, currency: "USD", type: "Wire", direction: "in", country: "China", paymentMethod: "wire", accountAge: 300, pepFlag: true, description: "Consulting fee" },
  { id: "TX-007", date: "2024-03-05", entity: "Apex Logistics SA", amount: 67000, currency: "USD", type: "Wire", direction: "out", country: "Panama", paymentMethod: "wire", accountAge: 500, pepFlag: false, description: "Regular supplier" },
  { id: "TX-008", date: "2024-03-08", type: "Crypto", network: "ethereum",
    txHash: "0x3d7b2e1f4a8c9b0e5d6f2a1c4e7b0d3f6a9c2e5b",
    walletFrom: "0x7f2e8c3d1a4b5e9f2c6a0d8e3b7f1a4c9e2d5b8f",
    walletTo:   "0x905b93a13a68ededee4b4787ba96b5b38b03157b",
    amount: 485000, currency: "ETH", direction: "out", country: "Unknown",
    paymentMethod: "crypto", accountAge: 30, pepFlag: false,
    entity: "Wallet 0x7f2e", description: "DeFi interaction",
    fromLabel: null, toLabel: null, fromEntityType: null, toEntityType: null,
    fromRiskScore: null, toRiskScore: null, enriched: false },
  { id: "TX-009", date: "2024-03-07", type: "Crypto", network: "ethereum",
    txHash: "0xa1b2c3d4e5f6789012345678901234567890abcd",
    walletFrom: "0xab5801a7d398351b8be11c439e05c5b3259aec9b",
    walletTo:   "0x1234567890123456789012345678901234567890",
    amount: 12560000, currency: "USDC", direction: "in", country: "Unknown",
    paymentMethod: "crypto", accountAge: 60, pepFlag: false,
    entity: "WhaleWallet", description: "Large accumulation",
    fromLabel: null, toLabel: null, fromEntityType: null, toEntityType: null,
    fromRiskScore: null, toRiskScore: null, enriched: false },
  { id: "TX-010", date: "2024-03-05", entity: "Nadia Al-Hassan", amount: 5000, currency: "USD", type: "Wire", direction: "out", country: "Jordan", paymentMethod: "wire", accountAge: 180, pepFlag: false, description: "Personal transfer" },
];

// ─── ITEM 10: CURRENCY_TO_USD constant (canonical name per spec) ──────────────
const CURRENCY_TO_USD = { USD:1, EUR:1.08, GBP:1.27, CAD:0.74, AUD:0.65, CHF:1.11, JPY:0.0067, MXN:0.058, BRL:0.20, CNY:0.14, SGD:0.74, HKD:0.13, INR:0.012, AED:0.27, SAR:0.27 };

// ─── E-019 / F-010: MULTI-CURRENCY USD NORMALIZATION ─────────────────────────
// Static reference rates (approximate). For production, replace with live FX feed.
const FX_TO_USD = {
  USD: 1, EUR: 1.09, GBP: 1.27, CHF: 1.12, CAD: 0.74, AUD: 0.65, JPY: 0.0067,
  CNY: 0.138, HKD: 0.128, SGD: 0.74, AED: 0.272, SAR: 0.267, NOK: 0.092,
  SEK: 0.093, DKK: 0.146, NZD: 0.60, MXN: 0.058, BRL: 0.20, INR: 0.012,
  KRW: 0.00075, ZAR: 0.054, TRY: 0.031, PLN: 0.25, CZK: 0.044, HUF: 0.0028,
  // Crypto (approximate at time of implementation — use live feed in production)
  BTC: 65000, ETH: 3500, USDT: 1, USDC: 1, BNB: 580, SOL: 165,
  XRP: 0.52, ADA: 0.46, DOGE: 0.16, MATIC: 0.70, AVAX: 38, DOT: 7.5,
};
function toUSD(amount, currency) {
  if (!currency || currency === "USD") return amount;
  const rate = FX_TO_USD[(currency || "").toUpperCase()];
  return rate ? amount * rate : amount; // fall back to nominal if unknown
}

// ─── RULE ENGINE ──────────────────────────────────────────────────────────────
// entityHistory: { [entityKeyLower]: tx[] } — all transactions grouped by entity, sorted by date
// pepNames / watchlistNames: Sets of lowercase names from Supabase
function evaluateTransaction(tx, rules, pepNames = new Set(), watchlistNames = new Set(), entityHistory = {}) {
  const alerts = [];
  const enabledRules = rules.filter(r => r.enabled);
  const entityKey = (tx.entity || "unknown").toLowerCase().trim();
  const entityTxs = entityHistory[entityKey] || [];
  // E-019: Normalize transaction amount to USD for threshold comparisons
  const txAmountUSD = toUSD(tx.amount || 0, tx.currency);

  // ── Velocity helper: get other transactions for this entity within N days before tx.date ──
  function txsInWindow(days) {
    const txDate = tx.date ? new Date(tx.date) : new Date();
    const windowStart = new Date(txDate.getTime() - days * 86400000);
    return entityTxs.filter(h => {
      if (h.id === tx.id) return false;
      const d = h.date ? new Date(h.date) : null;
      return d && d >= windowStart && d <= txDate;
    });
  }

  enabledRules.forEach(rule => {
    let triggered = false;
    let detail = "";

    switch (rule.id) {
      case "BANK-001":
        if ((tx.paymentMethod === "cash" || tx.type === "Cash") && txAmountUSD >= (rule.threshold || 10000)) {
          triggered = true; detail = `Cash transaction of ${tx.currency !== "USD" ? `${tx.currency} ${tx.amount?.toLocaleString()} (~$${Math.round(txAmountUSD).toLocaleString()} USD)` : `$${txAmountUSD.toLocaleString()}`} exceeds $${(rule.threshold||10000).toLocaleString()} CTR threshold`;
        }
        break;

      case "BANK-002": {
        // TRUE STRUCTURING: aggregate transactions below CTR threshold within rolling window
        const ceiling = rule.threshold || 9500;
        const floor   = rule.structureFloor || 3000;
        const winDays = rule.windowDays || 7;
        const minCnt  = rule.minCount || 3;
        const minTotal= rule.structuringTotal || 25000;
        if (txAmountUSD >= floor && txAmountUSD < ceiling &&
            (tx.paymentMethod === "wire" || tx.paymentMethod === "cash" || tx.type === "Cash" || tx.type === "Wire")) {
          const prior = txsInWindow(winDays).filter(h => toUSD(h.amount, h.currency) >= floor && toUSD(h.amount, h.currency) < ceiling);
          const windowTotal = prior.reduce((s, h) => s + toUSD(h.amount, h.currency), 0) + txAmountUSD;
          const windowCount = prior.length + 1;
          if (windowTotal >= minTotal && windowCount >= minCnt) {
            triggered = true;
            detail = `Structuring: ${windowCount} transactions totaling ~$${Math.round(windowTotal).toLocaleString()} USD in ${winDays} days — each below $${ceiling.toLocaleString()} reporting threshold`;
            // Store related transaction IDs so alert displays all transactions in the pattern
            rule.relatedTransactionIds = [...prior.map(h => h.id), tx.id];
          } else if (windowCount === 1) {
            // Even alone it's suspicious if near the threshold
            triggered = txAmountUSD >= (ceiling - 1000);
            if (triggered) detail = `Single transaction of ~$${Math.round(txAmountUSD).toLocaleString()} USD — just below $${ceiling.toLocaleString()} reporting threshold — monitor for pattern`;
          }
        }
        break;
      }

      case "BANK-003":
        if (txAmountUSD >= (rule.minAmount || 5000) && Math.round(txAmountUSD) % (rule.roundMultiple || 1000) === 0) {
          triggered = true; detail = `Round amount transaction: ~$${Math.round(txAmountUSD).toLocaleString()} USD`;
        }
        break;

      case "BANK-004": {
        const hrc = rule.highRiskCountries || [];
        if (hrc.some(c => (tx.country || "").toLowerCase().includes(c.toLowerCase())) && tx.amount >= (rule.threshold || 1000)) {
          triggered = true; detail = `Transaction to/from high-risk jurisdiction: ${tx.country} — $${tx.amount.toLocaleString()}`;
        }
        break;
      }

      case "BANK-005": {
        // TRUE VELOCITY: count + volume across a time window for the same entity
        const winHrs  = rule.windowHours || 24;
        const winDays = winHrs / 24;
        const maxCnt  = rule.maxCount  || 20;
        const maxVol  = rule.maxVolume || 50000;
        const prior   = txsInWindow(winDays);
        const totalVol = prior.reduce((s, h) => s + h.amount, 0) + tx.amount;
        const totalCnt = prior.length + 1;
        if (totalVol >= maxVol || totalCnt > maxCnt) {
          triggered = true;
          detail = `Velocity alert for "${tx.entity}": ${totalCnt} transactions totaling $${totalVol.toLocaleString()} in ${winHrs}hrs — exceeds ${totalCnt > maxCnt ? `count limit of ${maxCnt}` : `volume limit of $${maxVol.toLocaleString()}`}`;
        }
        break;
      }

      case "BANK-006": {
        const eKey = (tx.entity || "").toLowerCase().trim();
        const isPep = tx.pepFlag === true || tx.pepFlag === "true"
          || pepNames.has(eKey) || watchlistNames.has(eKey)
          || [...pepNames].some(n => n && eKey.includes(n))
          || [...watchlistNames].some(n => n && eKey.includes(n));
        if (isPep && txAmountUSD >= (rule.threshold || 5000)) {
          const src = tx.pepFlag ? "CSV flag" : pepNames.has(eKey) ? "KYC/Customer record" : "Internal watchlist";
          triggered = true; detail = `PEP/watchlist entity "${tx.entity}" (~$${Math.round(txAmountUSD).toLocaleString()} USD) — source: ${src} — EDD required`;
        }
        break;
      }

      case "BANK-007":
        if ((tx.accountAge || 0) >= (rule.dormantDays || 90) && txAmountUSD >= (rule.threshold || 5000)) {
          triggered = true; detail = `Dormant account (${tx.accountAge} days inactive) — sudden activity: ~$${Math.round(txAmountUSD).toLocaleString()} USD`;
        }
        break;

      case "BANK-008": {
        // TRUE MULTI-CROSS-BORDER: count international wires on the same day for this entity
        const txDay = (tx.date || "").slice(0, 10);
        const isIntl = tx.type === "Wire" || tx.paymentMethod === "wire";
        const isAbroad = tx.country && !["US","USA","United States"].includes(tx.country.trim());
        if (isIntl && isAbroad) {
          const sameDayCrossBorder = txsInWindow(1).filter(h =>
            (h.date || "").slice(0,10) === txDay &&
            h.country && !["US","USA","United States"].includes(h.country.trim())
          );
          const total = sameDayCrossBorder.length + 1;
          if (total >= (rule.minCount || 3)) {
            triggered = true;
            detail = `${total} cross-border wires in one day by "${tx.entity}" — possible rapid international fund movement`;
          }
        }
        break;
      }

      case "FT-001":
        if (tx.type === "Wire" && txAmountUSD >= (rule.threshold || 2000)) {
          triggered = true; detail = `Rapid fund movement: ~$${Math.round(txAmountUSD).toLocaleString()} USD wire — potential layering indicator`;
        }
        break;

      case "FT-002": {
        // P2P pattern: many outbound wires in a window, each below avg threshold
        const winDays = rule.windowDays || 7;
        const avgCeil = rule.avgThreshold || 500;
        const maxRec  = rule.maxRecipients || 10;
        if (tx.amount < avgCeil && (tx.type === "Wire" || tx.paymentMethod === "wire") && (tx.direction || "").toLowerCase() === "out") {
          const priorSmallOutbound = txsInWindow(winDays).filter(h =>
            h.amount < avgCeil && (h.type === "Wire" || h.paymentMethod === "wire") && (h.direction || "").toLowerCase() === "out"
          );
          const uniqueRecipients = new Set([tx.entity, ...priorSmallOutbound.map(h => h.entity)]).size;
          if (uniqueRecipients >= maxRec) {
            triggered = true;
            detail = `P2P mule pattern: ${uniqueRecipients} recipients received small outbound wires in ${winDays} days from "${tx.entity}"`;
          }
        }
        break;
      }

      case "FT-003":
        if ((tx.accountAge || 999) <= (rule.maxAge || 30) && tx.amount >= (rule.threshold || 5000)) {
          triggered = true; detail = `New account (${tx.accountAge} days old) — high-value transaction: $${tx.amount.toLocaleString()}`;
        }
        break;

      case "FT-004":
        if (tx.amount >= (rule.threshold || 1000) && tx.country && ["Iran","North Korea","Russia","Belarus","Syria","Myanmar","Cuba"].some(c => (tx.country||"").includes(c))) {
          triggered = true; detail = `Transaction from high-risk country ${tx.country}: $${tx.amount.toLocaleString()}`;
        }
        break;

      case "FT-005":
        // failedAttempts field in transaction CSV — flag if >= minFailures
        if ((tx.failedAttempts || 0) >= (rule.minFailures || 5)) {
          triggered = true; detail = `${tx.failedAttempts} failed payment attempts before success — possible account testing`;
        }
        break;
      case "CRYPTO-001": { // Mixer / tumbler
        const mixerTypes = ["mixer", "tumbler"];
        const knownMixers = rule.knownMixers || ["Tornado Cash", "ChipMixer", "Sinbad", "Blender", "Wasabi"];
        const isMixer = mixerTypes.includes(tx.fromEntityType) || mixerTypes.includes(tx.toEntityType)
          || knownMixers.some(m => (tx.fromLabel || "").includes(m) || (tx.toLabel || "").includes(m) || (tx.cryptoEntity || "").includes(m));
        if (tx.type === "Crypto" && isMixer) {
          triggered = true;
          detail = `Crypto transaction involving mixer service: ${tx.toLabel || tx.fromLabel || tx.cryptoEntity} — $${tx.amount.toLocaleString()}`;
        }
        break;
      }
      case "CRYPTO-002": { // OFAC/Sanctioned address
        if (tx.type === "Crypto" && (tx.fromEntityType === "sanctioned" || tx.toEntityType === "sanctioned" || (tx.fromRiskScore || 0) >= 90 || (tx.toRiskScore || 0) >= 90)) {
          triggered = true;
          detail = `Transaction involves OFAC/sanctioned address: ${tx.toLabel || tx.fromLabel} — riskScore: ${Math.max(tx.fromRiskScore || 0, tx.toRiskScore || 0)}`;
        }
        break;
      }
      case "CRYPTO-003": { // Darknet marketplace
        if (tx.type === "Crypto" && (tx.fromEntityType === "darknet" || tx.toEntityType === "darknet")) {
          triggered = true;
          detail = `Transaction linked to darknet marketplace: ${tx.toLabel || tx.fromLabel}`;
        }
        break;
      }
      case "CRYPTO-004": { // Whale / large crypto
        if (tx.type === "Crypto" && tx.amount >= (rule.threshold || 100000)) {
          triggered = true;
          detail = `Large crypto transaction: $${tx.amount.toLocaleString()} — Travel Rule review required${tx.toLabel ? ` (to: ${tx.toLabel})` : ""}`;
        }
        break;
      }
      case "CRYPTO-007": { // Unhosted wallet
        const isUnhosted = tx.fromEntityType === "unhosted" || tx.toEntityType === "unhosted"
          || (tx.cryptoEntity || "").toLowerCase().includes("unhosted");
        if (tx.type === "Crypto" && isUnhosted && tx.amount >= (rule.threshold || 3000)) {
          triggered = true;
          detail = `Unhosted wallet transfer: $${tx.amount.toLocaleString()} — EU TFR / Travel Rule compliance required${tx.toLabel ? ` (${tx.toLabel})` : ""}`;
        }
        break;
      }
      case "CRYPTO-HIGH_RISK": { // High risk score wallet
        const maxRisk = Math.max(tx.fromRiskScore || 0, tx.toRiskScore || 0);
        if (tx.type === "Crypto" && maxRisk >= 75) {
          triggered = true;
          detail = `High-risk wallet detected (risk score: ${maxRisk}/100): ${tx.toLabel || tx.fromLabel || "Unknown"} — enhanced review required`;
        }
        break;
      }
      case "CRYPTO-005":
        // Disabled: Cross-chain bridge transactions are common for legitimate liquidity/arbitrage
        break;

      case "CRYPTO-006":
        // Disabled: NFT trading patterns too noisy for general monitoring
        break;

      case "CRYPTO-008":
        // Disabled: DeFi activity is normal for crypto users; only enable for suspicious patterns
        break;

      case "PAY-001": {
        // Card testing: tiny transaction followed by larger one from same entity
        const testCeil  = rule.testThreshold || 5;
        const largeFl   = rule.largeThreshold || 500;
        const maxGapMin = rule.maxGap || 60;
        if (tx.amount >= largeFl) {
          const recentTiny = txsInWindow(maxGapMin / 1440).filter(h => h.amount < testCeil);
          if (recentTiny.length > 0) {
            triggered = true; detail = `Card testing pattern: small test transaction of $${recentTiny[0].amount} followed by $${tx.amount.toLocaleString()} — fraud indicator`;
          }
        }
        break;
      }

      case "PAY-002": {
        // TRUE AGGREGATED MICRO-TRANSACTIONS: count + sum in window
        const winHrs   = rule.windowHours || 24;
        const microCeil= rule.microCeiling || 100;
        const minCnt   = rule.minCount || 50;
        const aggThr   = rule.aggregateThreshold || 10000;
        if (tx.amount < microCeil && tx.type !== "Crypto") {
          const priorMicro = txsInWindow(winHrs / 24).filter(h => h.amount < microCeil && h.type !== "Crypto");
          const totalAmt = priorMicro.reduce((s, h) => s + h.amount, 0) + tx.amount;
          const totalCnt = priorMicro.length + 1;
          if (totalCnt >= minCnt && totalAmt >= aggThr) {
            triggered = true; detail = `Micro-transaction aggregation: ${totalCnt} transactions totaling $${totalAmt.toLocaleString()} in ${winHrs}hrs — potential structuring`;
          }
        }
        break;
      }

      case "INV-001": {
        // Rapid in-and-out: large tx with same entity transacting in opposite direction recently
        const minAmt = rule.threshold || 50000;
        if (tx.amount >= minAmt) {
          const priorOpposite = txsInWindow((rule.maxHours || 48) / 24).filter(h =>
            h.amount >= minAmt && h.direction && tx.direction && h.direction !== tx.direction
          );
          if (priorOpposite.length > 0) {
            triggered = true; detail = `Rapid in-and-out: $${tx.amount.toLocaleString()} ${tx.direction} — matching opposite direction transaction of $${priorOpposite[0].amount.toLocaleString()} within ${rule.maxHours || 48}hrs — possible capital markets layering`;
          }
        }
        break;
      }

      case "INV-002":
        // Third-party payment: flag if payer name field differs from entity
        if (tx.payerName && tx.entity && tx.payerName.toLowerCase().trim() !== tx.entity.toLowerCase().trim() && tx.amount >= (rule.threshold || 10000)) {
          triggered = true; detail = `Third-party payment: payer "${tx.payerName}" funding account of "${tx.entity}" — $${tx.amount.toLocaleString()} — possible nominee arrangement`;
        }
        break;

      default: {
        // Custom rules — evaluate all configured conditions including velocity
        const amt = txAmountUSD; // USD-normalized for threshold comparisons (E-019)
        const thr = parseFloat(rule.threshold || rule.minAmount || rule.aggregateThreshold || 0);
        if (thr <= 0) break;

        // 1. Transaction type
        const typeMatch = !rule.txType || rule.txType === "Any"
          || (tx.type || "").toLowerCase() === rule.txType.toLowerCase()
          || (tx.paymentMethod || "").toLowerCase() === rule.txType.toLowerCase();

        // 2. Country
        const hrc = Array.isArray(rule.highRiskCountries)
          ? rule.highRiskCountries
          : (rule.highRiskCountries || "").split(",").map(s => s.trim()).filter(Boolean);
        const countryMatch = hrc.length === 0
          || hrc.some(c => (tx.country || "").toLowerCase().includes(c.toLowerCase()));

        // 3. Direction
        const dirMatch = !rule.direction || rule.direction === "any"
          || (tx.direction || "").toLowerCase() === rule.direction.toLowerCase();

        // 4. PEP-only
        const eKey = (tx.entity || "").toLowerCase().trim();
        const pepCheck = !rule.pepOnly || tx.pepFlag === true || tx.pepFlag === "true"
          || pepNames.has(eKey) || watchlistNames.has(eKey);

        if (!typeMatch || !countryMatch || !dirMatch || !pepCheck) break;

        // 5. Velocity / aggregation (optional)
        const winDays = rule.velocityWindowDays || 0;
        if (winDays > 0 && (rule.velocityMaxCount || rule.velocityMaxVolume)) {
          const prior = txsInWindow(winDays).filter(h =>
            (!rule.txType || rule.txType === "Any" || (h.type||"").toLowerCase() === rule.txType.toLowerCase()) &&
            (hrc.length === 0 || hrc.some(c => (h.country||"").toLowerCase().includes(c.toLowerCase())))
          );
          const windowCount = prior.length + 1;
          const windowVol   = prior.reduce((s, h) => s + h.amount, 0) + amt;
          const countBreached = rule.velocityMaxCount && windowCount > rule.velocityMaxCount;
          const volBreached   = rule.velocityMaxVolume && windowVol >= rule.velocityMaxVolume;
          if (countBreached || volBreached) {
            triggered = true;
            const reasons = [];
            if (countBreached) reasons.push(`${windowCount} transactions in ${winDays}d exceeds limit of ${rule.velocityMaxCount}`);
            if (volBreached)   reasons.push(`$${windowVol.toLocaleString()} total in ${winDays}d exceeds $${rule.velocityMaxVolume.toLocaleString()}`);
            if (hrc.length)    reasons.push(`country: ${tx.country}`);
            detail = `Custom rule "${rule.name}" [velocity]: ${reasons.join(" · ")}`;
          }
        } else if (amt >= thr) {
          // Simple single-transaction threshold check
          triggered = true;
          const reasons = [`$${amt.toLocaleString()} ≥ $${thr.toLocaleString()}`];
          if (hrc.length) reasons.push(`country: ${tx.country}`);
          if (rule.direction && rule.direction !== "any") reasons.push(`direction: ${tx.direction}`);
          if (rule.pepOnly) reasons.push("PEP/watchlist match");
          detail = `Custom rule "${rule.name}": ${reasons.join(" · ")}`;
        }
        break;
      }
    }

    if (triggered) {
      alerts.push({
        ruleId: rule.id,
        ruleName: rule.name,
        ruleCategory: rule.category,
        severity: rule.severity,
        detail,
        reference: rule.reference,
        tags: rule.tags,
        relatedTransactionIds: rule.relatedTransactionIds || [],
      });
    }
  });

  return alerts;
}

// ─── SMALL COMPONENTS ─────────────────────────────────────────────────────────
function Btn({ children, onClick, variant = "primary", disabled = false, small = false, style: ex = {} }) {
  const C = useTheme();
  const base = {
    primary: { background: C.accent, color: C.bg, border: "none" },
    ghost:   { background: "transparent", color: C.textMid, border: `1px solid ${C.border}` },
    danger:  { background: "rgba(255,68,68,0.1)", color: C.danger, border: `1px solid ${C.danger}` },
    success: { background: "rgba(0,230,118,0.1)", color: C.success, border: `1px solid ${C.success}` },
    subtle:  { background: C.surfaceAlt, color: C.textMid, border: `1px solid ${C.border}` },
    warn:    { background: "rgba(255,140,0,0.1)", color: C.warn, border: `1px solid ${C.warn}` },
    critical:{ background: "rgba(255,68,68,0.15)", color: "#FF6B6B", border: `1px solid #FF4444` },
  }[variant] || {};
  return (
    <button onClick={onClick} disabled={disabled} style={{ ...base, padding: small ? "5px 11px" : "9px 18px", borderRadius: 7, cursor: disabled ? "not-allowed" : "pointer", fontFamily: "'DM Mono', monospace", fontWeight: 600, fontSize: small ? 11 : 13, opacity: disabled ? 0.4 : 1, transition: "all 0.12s", display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", ...ex }}
      onMouseDown={e => { if (!disabled) e.currentTarget.style.transform = "scale(0.97)"; }}
      onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
    >{children}</button>
  );
}

function SeverityBadge({ sev }) {
  const C = useTheme();
  const map = {
    CRITICAL: { color: "#FF6B6B", bg: "rgba(255,68,68,0.15)", border: "rgba(255,68,68,0.4)" },
    HIGH:     { color: C.danger,  bg: "rgba(255,68,68,0.1)",  border: "rgba(255,68,68,0.3)" },
    MEDIUM:   { color: C.warn,    bg: "rgba(255,140,0,0.1)",  border: "rgba(255,140,0,0.3)" },
    LOW:      { color: C.success, bg: "rgba(0,230,118,0.1)",  border: "rgba(0,230,118,0.3)" },
  }[sev] || {};
  return (
    <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, background: map.bg, color: map.color, border: `1px solid ${map.border}`, fontFamily: "'DM Mono', monospace", fontWeight: 700, letterSpacing: "0.06em" }}>
      {sev}
    </span>
  );
}

function CategoryBadge({ cat }) {
  const C = useTheme();
  const map = {
    Bank:       { color: C.accent,   bg: C.accentGlow },
    Crypto:     { color: C.gold,     bg: "rgba(240,180,41,0.1)" },
    Fintech:    { color: C.purple,   bg: "rgba(167,139,250,0.1)" },
    Payments:   { color: C.success,  bg: "rgba(0,230,118,0.1)" },
    Investment: { color: "#FF6B9D",  bg: "rgba(255,107,157,0.1)" },
  }[cat] || { color: C.textMid, bg: C.surfaceAlt };
  return (
    <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, background: map.bg, color: map.color, fontFamily: "'DM Mono', monospace", fontWeight: 600, border: "none" }}>{cat}</span>
  );
}

// ─── TAB: QUANTITATIVE MODEL ──────────────────────────────────────────────────
function ModelTab({
  transactions,
  company,
  amlModel,
  txScores,
  selectedTxForModel,
  setSelectedTxForModel,
  scoreTransactionWithModel,
  handleModelFeedback,
}) {
  const C = useTheme();
  const [scoring, setScoring] = useState(false);
  const [modelTab, setModelTab] = useState("scoring"); // "scoring" or "math"

  const handleScoreTransaction = async (tx) => {
    setScoring(true);
    try {
      await scoreTransactionWithModel(tx);
      setSelectedTxForModel(tx);
    } catch (error) {
      console.error('Scoring failed:', error);
    } finally {
      setScoring(false);
    }
  };

  const handleScoreAll = async () => {
    setScoring(true);
    try {
      for (const tx of transactions) {
        await scoreTransactionWithModel(tx);
      }
      if (transactions.length > 0) {
        setSelectedTxForModel(transactions[0]);
      }
      alert(`✓ Scored all ${transactions.length} transactions`);
    } catch (error) {
      console.error('Batch scoring failed:', error);
      alert('Error scoring some transactions');
    } finally {
      setScoring(false);
    }
  };

  const selectedScore = selectedTxForModel ? txScores[selectedTxForModel.id] : null;

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Model Manager Dashboard */}
      <div style={{ marginBottom: 32 }}>
        <AMLModelManager companyId={company?.id} />
      </div>

      {/* Model Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, borderBottom: `1px solid ${C.border}`, paddingBottom: 12 }}>
        <button
          onClick={() => setModelTab("scoring")}
          style={{
            background: modelTab === "scoring" ? C.accent : "transparent",
            color: modelTab === "scoring" ? C.bg : C.text,
            border: "none",
            padding: "8px 16px",
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.2s",
          }}
        >
          📊 Score Transactions
        </button>
        <button
          onClick={() => setModelTab("math")}
          style={{
            background: modelTab === "math" ? C.accent : "transparent",
            color: modelTab === "math" ? C.bg : C.text,
            border: "none",
            padding: "8px 16px",
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.2s",
          }}
        >
          🧮 Model Math
        </button>
      </div>

      {modelTab === "scoring" && (
      <div>
      {/* Transaction Scoring Section */}
      <div
        style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 12,
          padding: 24,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: 0 }}>
            📍 Score Individual Transactions
          </h2>
          <button
            onClick={handleScoreAll}
            disabled={scoring || transactions.length === 0}
            style={{
              background: C.accent,
              color: C.bg,
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              fontSize: 12,
              fontWeight: 600,
              cursor: scoring ? 'not-allowed' : 'pointer',
              opacity: scoring ? 0.6 : 1,
            }}
          >
            {scoring ? '⏳ Scoring...' : '⚡ Score All'}
          </button>
        </div>

        {/* Transactions List */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 12,
            marginBottom: 24,
            maxHeight: 500,
            overflowY: 'auto',
          }}
        >
          {transactions.map(tx => (
            <div
              key={tx.id}
              onClick={() => !scoring && handleScoreTransaction(tx)}
              style={{
                background: selectedTxForModel?.id === tx.id ? C.accentGlow : C.bg,
                border: `1px solid ${
                  selectedTxForModel?.id === tx.id ? C.accent : C.border
                }`,
                borderRadius: 8,
                padding: 12,
                cursor: scoring ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: scoring ? 0.6 : 1,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontWeight: 600, color: C.text, fontSize: 13 }}>
                  {tx.entity}
                </div>
                {txScores[tx.id] && (
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color:
                        txScores[tx.id].riskScore >= 70
                          ? '#FF6B6B'
                          : txScores[tx.id].riskScore >= 50
                            ? '#FFA500'
                            : '#4CAF50',
                    }}
                  >
                    {txScores[tx.id].riskScore}
                  </div>
                )}
              </div>

              <div style={{ fontSize: 11, color: C.textDim, marginBottom: 8 }}>
                {tx.type} • ${tx.amount?.toLocaleString() || 0}
              </div>

              {txScores[tx.id] ? (
                <div
                  style={{
                    fontSize: 10,
                    color: C.textDim,
                    fontStyle: 'italic',
                  }}
                >
                  ✓ Scored
                </div>
              ) : (
                <div style={{ fontSize: 10, color: C.textDim }}>
                  Click to score
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Model Insights for Selected Transaction */}
        {selectedTxForModel && selectedScore && (
          <div style={{ marginTop: 24 }}>
            <AMLModelInsights
              transaction={selectedTxForModel}
              score={selectedScore}
              onFeedback={handleModelFeedback}
            />
          </div>
        )}

        {selectedTxForModel && !selectedScore && scoring && (
          <div
            style={{
              textAlign: 'center',
              padding: 32,
              color: C.textDim,
            }}
          >
            ⏳ Scoring transaction...
          </div>
        )}

        {!selectedTxForModel && transactions.length > 0 && (
          <div
            style={{
              textAlign: 'center',
              padding: 32,
              color: C.textDim,
              background: C.bg,
              borderRadius: 8,
              border: `1px solid ${C.border}`,
            }}
          >
            Select a transaction to see model risk assessment
          </div>
        )}

        {transactions.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              padding: 32,
              color: C.textDim,
            }}
          >
            No transactions to score. Import transactions in the Transactions tab.
          </div>
        )}
      </div>
      </div>
      )}

      {modelTab === "math" && (
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
        <h2 style={{ color: C.text, marginBottom: 20, fontSize: 16, fontWeight: 700 }}>🧮 Model Mathematics</h2>

        {/* Formula */}
        <div style={{ marginBottom: 32, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: 16 }}>
          <h3 style={{ color: C.accent, marginBottom: 12, fontSize: 13, fontWeight: 600 }}>Risk Score Formula</h3>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: C.text, lineHeight: 1.8 }}>
            <div style={{ marginBottom: 8 }}>Risk Score = Σ(Component Score × Weight) / Σ(Weights) × 100</div>
            <div style={{ marginBottom: 8 }}>
              <span style={{ color: C.accent }}>Threshold:</span> Score ≥ 70 = ALERT | Score ≥ 50 = FLAGGED
            </div>
          </div>
        </div>

        {/* Weights */}
        <div style={{ marginBottom: 32 }}>
          <h3 style={{ color: C.accent, marginBottom: 12, fontSize: 13, fontWeight: 600 }}>Component Weights</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {amlModel && Object.entries(amlModel.weights).map(([key, weight]) => (
              <div key={key} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: 12 }}>
                <div style={{ fontSize: 11, color: C.textDim, marginBottom: 4 }}>
                  {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.accent }}>
                  {(weight * 100).toFixed(0)}%
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Component Descriptions */}
        <div>
          <h3 style={{ color: C.accent, marginBottom: 12, fontSize: 13, fontWeight: 600 }}>How Each Component Is Calculated</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>Amount Normalized</div>
              <div style={{ fontSize: 11, color: C.textDim }}>Normalized transaction amount (amount / 50,000 capped at 100)</div>
            </div>
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>Wallet Risk</div>
              <div style={{ fontSize: 11, color: C.textDim }}>Maximum risk score of sending/receiving wallets (0-100)</div>
            </div>
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>Jurisdiction Risk</div>
              <div style={{ fontSize: 11, color: C.textDim }}>High-risk countries (Iran, North Korea, Syria, Cuba) = 100</div>
            </div>
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>Velocity Score</div>
              <div style={{ fontSize: 11, color: C.textDim }}>Transaction frequency past 24h / baseline frequency</div>
            </div>
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>Outlier Score</div>
              <div style={{ fontSize: 11, color: C.textDim }}>Z-score based on deviation from historical average (1-3+ std devs)</div>
            </div>
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>Structuring Score</div>
              <div style={{ fontSize: 11, color: C.textDim }}>Multiple small transactions in short period (≥3 txs) = 40</div>
            </div>
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>Round Amount Score</div>
              <div style={{ fontSize: 11, color: C.textDim }}>Suspicious round numbers (multiples of 5000/1000) = 50-75</div>
            </div>
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>New Entity Score</div>
              <div style={{ fontSize: 11, color: C.textDim }}>First transaction with counterparty = 40</div>
            </div>
          </div>
        </div>

        {/* Model Learning */}
        <div style={{ marginTop: 24, padding: 16, background: C.accentGlow, borderRadius: 8, border: `1px solid ${C.accent}` }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.accent, marginBottom: 8 }}>📚 Model Learning</div>
          <div style={{ fontSize: 11, color: C.text, lineHeight: 1.6 }}>
            The model learns from analyst feedback. When you mark an alert as a true or false positive, the weights adjust by ±5% to improve future predictions. Higher confidence scores reflect agreement between multiple risk indicators.
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

// ─── TAB: TRANSACTIONS ────────────────────────────────────────────────────────
function TransactionsTab({ transactions, setTransactions, rules, onAlertsGenerated, realtimeScreening, onRunNow, company, log, companyKeywords }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [importing, setImporting]     = useState(false);
  const [apiUrl, setApiUrl]           = useState("");
  const [apiKey, setApiKey]           = useState("");
  const [showApiConfig, setShowApiConfig] = useState(false);
  const [running, setRunning]         = useState(false);
  const [search, setSearch]           = useState("");
  const [filterType, setFilterType]   = useState("All");
  const [dragOver, setDragOver]       = useState(false);
  const fileRef = useRef(null);

  const [txPage, setTxPage] = useState(1);
  const TX_PAGE_SIZE = 25;
  const [clearTxConfirm, setClearTxConfirm] = useState(false);
  const [clearTxInput, setClearTxInput] = useState("");
  const [txSortBy, setTxSortBy] = useState("date");
  const [txSortDir, setTxSortDir] = useState("desc");

  const filtered = transactions.filter(tx => {
    if (search) {
      const searchLower = search.toLowerCase();
      const entityMatch = (tx.entity || "").toLowerCase().includes(searchLower);
      const counterpartyMatch = (tx.counterparty_name || "").toLowerCase().includes(searchLower);
      const idMatch = (tx.id || "").toLowerCase().includes(searchLower);
      const descMatch = (tx.description || "").toLowerCase().includes(searchLower);
      if (!entityMatch && !counterpartyMatch && !idMatch && !descMatch) return false;
    }
    if (filterType !== "All" && tx.type !== filterType) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    let aVal, bVal;
    switch (txSortBy) {
      case "date":
        aVal = new Date(a.date || 0).getTime();
        bVal = new Date(b.date || 0).getTime();
        break;
      case "amount":
        aVal = a.amount || 0;
        bVal = b.amount || 0;
        break;
      case "description":
        aVal = (a.description || "").toLowerCase();
        bVal = (b.description || "").toLowerCase();
        break;
      case "entity":
        aVal = (a.entity || "").toLowerCase();
        bVal = (b.entity || "").toLowerCase();
        break;
      case "direction":
        aVal = (a.direction || "").toLowerCase();
        bVal = (b.direction || "").toLowerCase();
        break;
      case "country":
        aVal = (a.country || "").toLowerCase();
        bVal = (b.country || "").toLowerCase();
        break;
      default:
        return 0;
    }
    if (aVal < bVal) return txSortDir === "asc" ? -1 : 1;
    if (aVal > bVal) return txSortDir === "asc" ? 1 : -1;
    return 0;
  });

  useEffect(() => setTxPage(1), [search, filterType, txSortBy, txSortDir]);

  const totalTxPages = Math.max(1, Math.ceil(sorted.length / TX_PAGE_SIZE));
  const paginatedTx = sorted.slice((txPage - 1) * TX_PAGE_SIZE, txPage * TX_PAGE_SIZE);

  function parseCSV(text) {
    const lines = text.split("\n").filter(l => l.trim());
    if (lines.length < 2) return [];

    // Auto-detect delimiter (tab or comma)
    const firstLine = lines[0];
    const hasTab = firstLine.includes("\t");
    const delimiter = hasTab ? "\t" : ",";

    const headers = lines[0].split(delimiter).map(h => h.trim().toLowerCase().replace(/[\s_-]+/g,""));

    return lines.slice(1).filter(l => l.trim()).map((line, i) => {
      const vals = line.split(delimiter).map(v => v.trim().replace(/^"|"$/g,""));
      const get = (keys) => {
        for (const k of keys) {
          // Normalize key the same way as headers
          const normalizedKey = k.toLowerCase().replace(/[\s_-]+/g,"");
          const idx = headers.indexOf(normalizedKey);
          if (idx > -1) {
            const val = vals[idx]?.trim();
            return val || "";
          }
        }
        return "";
      };

      // Try to extract entity with multiple fallback patterns
      // Prioritize customer_name, then entity, then name, etc. (avoid counterparty)
      let entity = get(["customer_name","customername","entity","name","account","accountname","company","organization","payer","payee","sender","recipient","merchant","vendor","beneficiary","originator"]);
      // Last resort: use description if it's reasonably short
      if (!entity) {
        const desc = get(["description","notes","memo"]);
        if (desc && desc.length < 50 && !desc.match(/^[0-9a-f]{40,}$/)) {
          entity = desc;
        }
      }
      // If still no entity, try first non-empty non-numeric column
      if (!entity && vals.length > 0) {
        for (let j = 0; j < vals.length; j++) {
          const v = vals[j]?.trim();
          if (v && !v.match(/^-?\d+(\.\d+)?$/) && !v.match(/^(USD|ETH|EUR|GBP|JPY|BTC)/i) && v.length > 2 && !v.match(/^(wire|cash|transfer|crypto|out|in|2026|2025|2024)/i)) {
            entity = v;
            break;
          }
        }
      }

      // Extract counterparty (other party in transaction)
      const counterparty = get(["counterparty","counterparty_name","recipient","payee","beneficiary"]) || "";

      return {
        id: get(["id","txid","transaction_id"]) || `TX-IMPORT-${Date.now()}-${i}`,
        date: get(["date","tx_date","transaction_date"]) || new Date().toISOString().slice(0,10),
        entity: entity || "Unknown",
        counterparty_name: counterparty,
        amount: parseFloat(get(["amount","value","sum"])) || 0,
        currency: get(["currency","ccy"]) || "USD",
        type: get(["type","tx_type","transaction_type","category"]) || "Wire",
        direction: get(["direction","flow"]) || "out",
        country: get(["country","jurisdiction"]) || "Unknown",
        paymentMethod: get(["payment_method","method","paymentmethod","channel"]) || "wire",
        cryptoEntity: get(["crypto_entity","cryptoentity","address_label"]) || "",
        txHash:    get(["tx_hash","txhash","hash","transaction_hash"]) || "",
        walletFrom: get(["wallet_from","from_address","from_wallet","walletfrom","sender"]) || "",
        walletTo:   get(["wallet_to","to_address","to_wallet","walletto","recipient"]) || "",
        network:    get(["network","chain","blockchain"]) || (get(["type","tx_type","category"])?.toLowerCase() === "crypto" ? "ethereum" : ""),
        enriched:   false,
        fromLabel: null, toLabel: null, fromEntityType: null, toEntityType: null,
        fromRiskScore: null, toRiskScore: null,
        accountAge: parseInt(get(["account_age","accountage"])) || 365,
        pepFlag: ["true","yes","1"].includes(get(["pep","pep_flag","pepflag"])?.toLowerCase()),
        description: get(["description","notes","memo"]) || "",
      };
    });
  }


  async function saveTransactionsToSupabase(newTxs) {
    if (!company?.id || !newTxs?.length) return;
    const token = localStorage.getItem('amlguard_token');
    if (!token) return console.error("[TM] No auth token, cannot save transactions");

    try {
      const response = await fetch('/api/tm/transactions', {
        method: 'POST',
        headers: getApiHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }),
        body: JSON.stringify({
          company_id: company.id,
          transactions: newTxs.map(tx => ({
            tx_reference: tx.id,
            sender: tx.entity || '',
            receiver: tx.counterparty_name || '',
            amount: parseFloat(tx.amount) || 0,
            currency: tx.currency || 'USD',
            type: tx.type || 'Wire',
            risk_score: tx.riskScore || 0,
          })),
        }),
      });

      const data = await response.json();
      if (response.ok) {
        console.log(`[TM] Saved ${data.saved} transactions to Neon database`);
      } else {
        console.error('[TM] Failed to save transactions:', data.error);
      }
    } catch (error) {
      console.error('[TM] Transaction save error:', error);
    }
  }

  function handleFileUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const parsed = parseCSV(ev.target.result);
      if (parsed.length) {
        setTransactions(prev => {
          const existingIds = new Set(prev.map(t => t.id));
          const fresh = parsed.filter(t => !existingIds.has(t.id));
          return fresh.length ? [...prev, ...fresh] : prev;
        });
        saveTransactionsToSupabase(parsed);
        setTimeout(() => onRunNow?.(), 800);
        alert(t('tm.importedCount', { count: parsed.length }));
      } else { alert(t('tm.couldNotParseCsv')); }
    };
    reader.readAsText(file);
    e.target.value = "";
  }

  function handleDrop(e) {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const parsed = parseCSV(ev.target.result);
      if (parsed.length) {
        setTransactions(prev => {
          const existingIds = new Set(prev.map(t => t.id));
          const fresh = parsed.filter(t => !existingIds.has(t.id));
          return fresh.length ? [...prev, ...fresh] : prev;
        });
        saveTransactionsToSupabase(parsed);
        alert(t('tm.importedCount', { count: parsed.length }));
        setTimeout(() => onRunNow?.(), 800);
      } else alert(t('tm.couldNotParseCsv'));
    };
    reader.readAsText(file);
  }

  async function fetchFromAPI() {
    if (!apiUrl) return;
    setImporting(true);
    try {
      const resp = await fetch(apiUrl, { headers: apiKey ? { Authorization: `Bearer ${apiKey}` } : {} });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const txs = Array.isArray(data) ? data : data.transactions || data.data || [];
      setTransactions(prev => {
        const existingIds = new Set(prev.map(t => t.id));
        const fresh = txs.filter(t => !existingIds.has(t.id));
        return fresh.length ? [...prev, ...fresh] : prev;
      });
      saveTransactionsToSupabase(txs);
      alert(t('tm.importedFromApi', { count: txs.length }));
      setTimeout(() => onRunNow?.(), 800);
    } catch (err) { alert(`API Error: ${err.message}`); }
    setImporting(false);
  }

  async function runScreening() {
    setRunning(true);

    // Log enabled rules for debugging
    const enabledRules = rules.filter(r => r.enabled);
    console.log(`[TM] Screening starting with ${enabledRules.length} enabled rules:`, enabledRules.map(r => `${r.id}(${r.severity})`).join(", "));

    // ── Load PEP and watchlist names from Supabase for cross-referencing ────
    let pepNames = new Set();
    let watchlistNames = new Set();
    if (company?.id) {
      const [kycRes, wlRes] = await Promise.all([
        db.from("kyc_submissions").select("full_name,entity_name").eq("company_id", company.id).eq("pep_declaration", true),
        db.from("internal_watchlist").select("full_name").eq("company_id", company.id),
      ]);
      (kycRes.data || []).forEach(r => {
        if (r.full_name) pepNames.add(r.full_name.toLowerCase().trim());
        if (r.entity_name) pepNames.add(r.entity_name.toLowerCase().trim());
      });
      (wlRes.data || []).forEach(r => { if (r.full_name) watchlistNames.add(r.full_name.toLowerCase().trim()); });
    }

    // Enrich any unenriched crypto transactions first
    let txsToScreen = transactions;
    const cryptoUnenriched = transactions.filter(tx => tx.type === "Crypto" && !tx.enriched);
    if (cryptoUnenriched.length > 0) {
      const enriched = await Promise.all(cryptoUnenriched.map(enrichCryptoTx));
      const enrichedMap = new Map(enriched.map(tx => [tx.id, tx]));
      txsToScreen = transactions.map(tx => enrichedMap.get(tx.id) || tx);
      setTransactions(txsToScreen); // persist enriched data
    }

    // ── Build entity history for velocity / aggregation rules ───────────────
    // Load full Supabase history for all entities in this batch so velocity
    // rules can see transactions from prior upload sessions (cross-session coverage).
    const entityHistory = {};
    if (company?.id) {
      const entityNames = [...new Set(txsToScreen.map(tx => tx.entity || "Unknown").filter(Boolean))];
      if (entityNames.length > 0) {
        try {
          const token = localStorage.getItem('amlguard_token');
          if (token && company?.id) {
            const res = await fetch(`/api/tm/transactions?company_id=${company.id}&limit=5000`, {
              headers: { 'Authorization': `Bearer ${token}` },
            });
            if (res.ok) {
              const { transactions: allTxs } = await res.json();
              const currentIds = new Set(txsToScreen.map(tx => tx.id));
              (allTxs || []).forEach(row => {
                if (currentIds.has(row.tx_reference)) return;
                const entity = row.entity || row.sender || row.receiver || "unknown";
                const key = entity.toLowerCase().trim();
                if (!entityHistory[key]) entityHistory[key] = [];
                entityHistory[key].push({
                  id: row.tx_reference || row.id,
                  entity,
                  amount: parseFloat(row.amount) || 0,
                  type: row.transaction_type || row.type || "Wire",
                  direction: row.direction === "inbound" ? "in" : "out",
                  date: row.created_at ? new Date(row.created_at).toISOString().slice(0, 10) : null,
                  country: row.country || "",
                  currency: row.currency || "USD",
                  paymentMethod: (row.transaction_type || row.type || "wire").toLowerCase(),
                });
              });
            }
          }
        } catch (error) {
          console.warn('[TM] Failed to load transaction history:', error);
        }
      }
    }
    // Add current session transactions
    txsToScreen.forEach(tx => {
      const key = (tx.entity || "unknown").toLowerCase().trim();
      if (!entityHistory[key]) entityHistory[key] = [];
      entityHistory[key].push(tx);
    });
    Object.values(entityHistory).forEach(txs =>
      txs.sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0))
    );

    await new Promise(r => setTimeout(r, 400));
    const newAlerts = [];
    txsToScreen.forEach(tx => {
      const hits = evaluateTransaction(tx, rules, pepNames, watchlistNames, entityHistory);

      // Analyze transaction for red flags (custom keywords)
      let redFlagAnalysis = null;
      if (companyKeywords) {
        redFlagAnalysis = analyzeTransactionForRedFlags(tx, companyKeywords);
        if (redFlagAnalysis?.hasRedFlags) {
          console.log(`[TM] Red flags detected in ${tx.entity}:`, redFlagAnalysis.redFlags.map(f => f.keyword).join(', '));
        }
      }

      hits.forEach(hit => {
        const alert = {
          id: `TM-${Date.now()}-${Math.random().toString(36).slice(2,7).toUpperCase()}`,
          txId: tx.id,
          txReference: tx.id,
          entity: tx.entity,
          counterparty: tx.counterparty_name || tx.receiver || tx.sender,
          amount: tx.amount,
          type: hit.ruleName,
          ruleId: hit.ruleId,
          ruleCategory: hit.ruleCategory,
          risk: hit.severity === "CRITICAL" ? "HIGH" : hit.severity,
          severity: hit.severity,
          status: "open",
          date: tx.date,
          txCount: 1,
          country: tx.country,
          notes: hit.detail,
          txDescription: tx.description,
          ruleName: hit.ruleName,
          assignee: "",
          reference: hit.reference,
          tags: hit.tags,
          source: "TM Engine",
          redFlags: redFlagAnalysis?.redFlags || [],
          riskIncrease: redFlagAnalysis?.riskIncrease || 0,
          requiresEscalation: redFlagAnalysis?.hasEscalation || false,
        };

        // Increase risk if red flags were found
        if (redFlagAnalysis?.riskIncrease > 0) {
          const riskMap = { CRITICAL: 3, HIGH: 2, MEDIUM: 1.5, LOW: 1 };
          const multiplier = riskMap[alert.severity] || 1;
          alert.riskIncrease = redFlagAnalysis.riskIncrease * multiplier;
        }

        newAlerts.push(alert);
      });
    });
    // E-020 / F-009: Explicit internal watchlist match alerts
    // DISABLED: Watchlist alerts create too many duplicates. Better to flag at rule level with PEP/watchlist checks.
    // If re-enabling, ensure no alert exists for tx before adding watchlist alert
    /*
    if (watchlistNames.size > 0) {
      const wlEntries = company?.id
        ? (await db.from("internal_watchlist").select("full_name,list_type,reason").eq("company_id", company.id)).data || []
        : [];
      txsToScreen.forEach(tx => {
        const eKey = (tx.entity || "").toLowerCase().trim();
        const hit = wlEntries.find(w => {
          const n = (w.full_name || "").toLowerCase().trim();
          return n && (eKey === n || eKey.includes(n) || n.includes(eKey));
        });
        if (hit) {
          // Only add watchlist alert if NO alert exists for this tx (not even other types)
          const alreadyFlagged = newAlerts.some(a => a.txId === tx.id);
          if (!alreadyFlagged) {
            newAlerts.push({
              id: `TM-WL-${Date.now()}-${Math.random().toString(36).slice(2,7).toUpperCase()}`,
              txId: tx.id, entity: tx.entity,
              amount: `${tx.currency} ${tx.amount.toLocaleString()}`,
              type: "Internal Watchlist Match",
              ruleId: "WATCHLIST-MATCH", ruleCategory: "Watchlist",
              risk: "HIGH", severity: "HIGH", status: "open",
              date: tx.date, txCount: 1, country: tx.country,
              notes: `Entity "${tx.entity}" matched internal watchlist entry "${hit.full_name}" (${hit.list_type || "watchlist"}) — ${hit.reason || "review required"}`,
              tags: ["watchlist", "internal"], source: "TM Engine — Watchlist Check",
            });
          }
        }
      });
    }
    */

    onAlertsGenerated(newAlerts);
    setRunning(false);
    alert(t('tm.screeningComplete', { count: newAlerts.length, plural: newAlerts.length !== 1 ? "s" : "" }));
  }

  function downloadTemplate() {
    const csv = ["id,date,entity,amount,currency,type,direction,country,payment_method,tx_hash,wallet_from,wallet_to,network,account_age,pep,description",
      "TX-SAMPLE-1,2024-03-01,ACME Corp,15000,USD,Wire,out,US,wire,,,,,365,false,Invoice payment",
      "TX-SAMPLE-2,2024-03-02,John Smith,9800,USD,Cash,in,UK,cash,,,,,200,false,Cash deposit",
      "TX-SAMPLE-3,2024-03-03,,250000,ETH,Crypto,out,Unknown,crypto,0x3d7b2e1f...,0x7f2e8c3d...,0x905b93a1...,ethereum,90,false,DeFi transfer",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "transactions_template.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  const types = ["All", ...Array.from(new Set(transactions.map(t => t.type)))];

  return (
    <div>
      {/* Action bar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        <Btn onClick={() => setShowApiConfig(v => !v)} variant={showApiConfig ? "primary" : "ghost"}>⚡ {t('tm.apiImport')}</Btn>
        <Btn variant="ghost" onClick={() => fileRef.current?.click()}>⬆ {t('tm.uploadCSV')}</Btn>
        <input ref={fileRef} type="file" accept=".csv" onChange={handleFileUpload} style={{ display: "none" }} />
        <Btn variant="ghost" onClick={downloadTemplate}>⬇ {t('tm.template')}</Btn>
          <Btn variant="ghost" onClick={() => downloadCSV(sorted.map(tx => ({ ID: tx.id, Date: tx.date, "Primary Party": tx.entity, Counterparty: tx.counterparty_name || tx.receiver || tx.sender || "", Amount: tx.amount, Currency: tx.currency, Type: tx.type, Direction: tx.direction, Country: tx.country, PEP: tx.pepFlag ? "Yes" : "", Description: tx.description || "" })), `transactions_${new Date().toISOString().slice(0,10)}.csv`)}>⬇ {t('tm.exportCSV')}</Btn>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <span style={{ color: C.textDim, fontSize: 12, alignSelf: "center" }}>{t('tm.transactionsLoaded', { count: transactions.length })}</span>
          <Btn onClick={runScreening} disabled={running || transactions.length === 0} variant={running ? "subtle" : "primary"} style={{ minWidth: 160 }}>
            {running ? t('tm.screeningRunning') : t('tm.runScreening')}
          </Btn>
        </div>
      </div>

      {/* API config panel */}
      {showApiConfig && (
        <div style={{ background: C.surface, border: `1px solid ${C.accent}`, borderRadius: 10, padding: 20, marginBottom: 20 }}>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 14, marginBottom: 14, fontFamily: "'Syne', sans-serif" }}>{t('tm.apiIntegration')}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div>
              <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>{t('tm.apiEndpoint')}</div>
              <input value={apiUrl} onChange={e => setApiUrl(e.target.value)} placeholder="https://core-banking.example.com/api/transactions" style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "9px 12px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
            </div>
            <div>
              <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>{t('tm.apiKey')}</div>
              <input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="sk-..." style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "9px 12px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Btn onClick={fetchFromAPI} disabled={!apiUrl || importing}>{importing ? t('tm.importing') : t('tm.importFromApi')}</Btn>
            <Btn variant="ghost" onClick={() => setShowApiConfig(false)}>{t('common.cancel')}</Btn>
          </div>
          <div style={{ marginTop: 12, padding: 12, background: C.bg, borderRadius: 6, border: `1px solid ${C.border}` }}>
            <div style={{ color: C.textDim, fontSize: 11, lineHeight: 1.7 }}>
              Expected JSON format: <code style={{ color: C.accent }}>{"[{\"id\":\"...\",\"entity\":\"...\",\"amount\":0,\"currency\":\"USD\",...}]"}</code>
              <br />or <code style={{ color: C.accent }}>{"{ \"transactions\": [...] }"}</code>
            </div>
          </div>
        </div>
      )}

      {/* Drop zone when no transactions */}
      {transactions.length === 0 && (
        <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop}
          style={{ border: `2px dashed ${dragOver ? C.accent : C.border}`, borderRadius: 12, padding: "60px 20px", textAlign: "center", marginBottom: 20, background: dragOver ? C.accentGlow : "transparent", transition: "all 0.15s", cursor: "pointer" }}
          onClick={() => fileRef.current?.click()}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📂</div>
          <div style={{ color: C.text, fontWeight: 600, marginBottom: 4 }}>{t('tm.dropZone')}</div>
          <div style={{ color: C.textDim, fontSize: 12 }}>{t('tm.dropZoneHint')}</div>
        </div>
      )}

      {/* Search/filter */}
      {transactions.length > 0 && (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "center" }}>
            <input type="text" placeholder={t('tm.searchTransactions')} value={search} onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, maxWidth: 300, padding: "8px 12px", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, fontFamily: "'DM Mono', monospace", outline: "none" }} />
            <select value={filterType} onChange={e => setFilterType(e.target.value)}
              style={{ padding: "8px 12px", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, fontFamily: "'DM Mono', monospace", outline: "none" }}>
              {types.map(t => <option key={t}>{t}</option>)}
            </select>
            <span style={{ color: C.textDim, fontSize: 12, marginLeft: "auto" }}>{sorted.length} of {transactions.length}</span>
            <Btn small variant="danger" onClick={() => { setClearTxConfirm(true); setClearTxInput(""); }}>
              {t('tm.clearAll')}
            </Btn>
          </div>

          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: "1200px" }}>
                <thead>
                  <tr style={{ background: C.surfaceAlt, borderBottom: `1px solid ${C.border}` }}>
                    {[
                      { label: t('tm.headers.id'), key: null, width: "120px" },
                      { label: t('common.date'), key: "date", width: "140px" },
                      { label: "Primary Party", key: "entity", width: "180px" },
                      { label: "Counterparty", key: "counterparty_name", width: "180px" },
                      { label: t('common.amount'), key: "amount", width: "120px" },
                      { label: t('common.type'), key: null, width: "100px" },
                      { label: t('tm.headers.direction'), key: "direction", width: "100px" },
                      { label: t('common.country'), key: "country", width: "100px" },
                      { label: t('tm.headers.pep'), key: null, width: "60px" },
                      { label: t('tm.headers.description'), key: "description", width: "250px" }
                    ].map(col => {
                      const isSorted = txSortBy === col.key;
                      const sortIcon = isSorted ? (txSortDir === "asc" ? " ↑" : " ↓") : "";
                      const cursor = col.key ? "pointer" : "default";
                      return (
                        <th key={col.label}
                          onClick={() => col.key && (txSortBy === col.key ? setTxSortDir(txSortDir === "asc" ? "desc" : "asc") : setTxSortBy(col.key))}
                          style={{
                            padding: "10px 14px",
                            textAlign: "left",
                            color: isSorted ? C.accent : C.textDim,
                            fontWeight: isSorted ? 700 : 600,
                            fontSize: 10,
                            letterSpacing: "0.08em",
                            width: col.width,
                            minWidth: col.width,
                            cursor,
                            background: isSorted ? `${C.accentGlow}33` : "transparent",
                            userSelect: "none"
                          }}>
                          {col.label}{sortIcon}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {paginatedTx.map((tx, i) => (
                    <tr key={tx.id} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.1)" }}>
                      <td style={{ padding: "10px 14px", color: C.accent, fontFamily: "'DM Mono', monospace", width: "120px", minWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tx.id}</td>
                      <td style={{ padding: "10px 14px", color: C.textMid, width: "140px", minWidth: "140px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tx.date}</td>
                      <td style={{ padding: "10px 14px", color: C.text, fontWeight: 500, width: "180px", minWidth: "180px", maxWidth: "180px", overflow: "hidden" }}>
                        {tx.type === "Crypto" ? (
                          <div>
                            <div style={{ fontSize: 11, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>
                              {tx.entity ? tx.entity : (tx.toLabel || tx.fromLabel || tx.toAddr?.slice(0,10) || tx.fromAddr?.slice(0,10) || tx.walletTo?.slice(0,10) || tx.walletFrom?.slice(0,10) || "Unknown")}
                            </div>
                            {tx.walletFrom && <div style={{ fontSize: 9, color: C.textDim, fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>From: {tx.walletFrom.slice(0,14)}&hellip;</div>}
                            {tx.walletTo   && <div style={{ fontSize: 9, color: C.textDim, fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>To: {tx.walletTo.slice(0,14)}&hellip;</div>}
                            {tx.enriched && (tx.toLabel || tx.fromLabel) && (
                              <div style={{ fontSize: 9, color: (tx.toRiskScore >= 75 || tx.fromRiskScore >= 75) ? "#FF6B6B" : "#00E676", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                ◈ {tx.toLabel || tx.fromLabel}{tx.toRiskScore ? ` (${Math.max(tx.toRiskScore, tx.fromRiskScore || 0)}/100)` : ""}
                              </div>
                            )}
                            {!tx.enriched && <div style={{ fontSize: 9, color: "#F0B429", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>&#9203; {t('tm.notEnriched')}</div>}
                          </div>
                        ) : (
                          <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tx.entity || "—"}</span>
                        )}
                      </td>
                      <td style={{ padding: "10px 14px", color: C.textMid, fontWeight: 500, width: "180px", minWidth: "180px", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {tx.counterparty_name || tx.receiver || tx.sender || tx.counterpartyLabel || "—"}
                      </td>
                      <td style={{ padding: "10px 14px", color: C.text, fontFamily: "'DM Mono', monospace", width: "120px", minWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tx.currency} {tx.amount?.toLocaleString()}</td>
                      <td style={{ padding: "10px 14px", width: "100px", minWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <span style={{ color: tx.type === "Crypto" ? C.gold : tx.type === "Cash" ? C.danger : C.textMid, fontSize: 11 }}>{tx.type}</span>
                      </td>
                      <td style={{ padding: "10px 14px", width: "100px", minWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <span style={{ color: tx.direction === "in" ? C.success : C.warn, fontSize: 11 }}>{tx.direction?.toUpperCase()}</span>
                      </td>
                      <td style={{ padding: "10px 14px", color: C.textMid, width: "100px", minWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tx.country}</td>
                      <td style={{ padding: "10px 14px", width: "60px", minWidth: "60px", textAlign: "center" }}>
                        {tx.pepFlag && <span style={{ color: C.danger, fontSize: 10, fontWeight: 700 }}>PEP</span>}
                      </td>
                      <td style={{ padding: "10px 14px", color: C.textDim, width: "250px", minWidth: "250px", maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tx.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalTxPages > 1 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "14px 0 4px", flexWrap: "wrap" }}>
                  <button onClick={() => setTxPage(p => Math.max(1, p - 1))} disabled={txPage === 1}
                    style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: txPage === 1 ? C.textDim : C.text, cursor: txPage === 1 ? "default" : "pointer", fontFamily: "'DM Mono',monospace", fontSize: 12 }}>←</button>
                  {Array.from({ length: totalTxPages }, (_, i) => i + 1).map(p => (
                    <button key={p} onClick={() => setTxPage(p)}
                      style={{ padding: "5px 10px", borderRadius: 6, border: `1px solid ${p === txPage ? C.accent : C.border}`, background: p === txPage ? C.accentGlow : "transparent", color: p === txPage ? C.accent : C.textMid, cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: p === txPage ? 700 : 400 }}>{p}</button>
                  ))}
                  <button onClick={() => setTxPage(p => Math.min(totalTxPages, p + 1))} disabled={txPage === totalTxPages}
                    style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: txPage === totalTxPages ? C.textDim : C.text, cursor: txPage === totalTxPages ? "default" : "pointer", fontFamily: "'DM Mono',monospace", fontSize: 12 }}>→</button>
                  <span style={{ color: C.textDim, fontSize: 11, marginLeft: 6 }}>{sorted.length} total · page {txPage} of {totalTxPages}</span>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Clear All Transactions — Regulatory Warning Modal ─────────────── */}
      {clearTxConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#1a1f2e", border: "1px solid #ff4444", borderRadius: 12, padding: 28, maxWidth: 500, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
            <div style={{ color: "#ff6b6b", fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 10 }}>
              ⚠ Record Retention Warning
            </div>
            <div style={{ background: "rgba(255,68,68,0.08)", border: "1px solid rgba(255,68,68,0.3)", borderRadius: 8, padding: "12px 16px", marginBottom: 16, color: "#8899aa", fontSize: 12, fontFamily: "'DM Mono',monospace", lineHeight: 1.6 }}>
              AML regulations require transaction records to be retained for <strong style={{ color: "#ff6b6b" }}>5–7 years</strong> (FinCEN 31 CFR 1020.410, FATF R.11, EU AMLD5 Art.40). Permanently deleting transactions may constitute a compliance violation and could impair future SAR/STR investigations.
            </div>
            <div style={{ color: "#8899aa", fontSize: 12, marginBottom: 8, fontFamily: "'DM Mono',monospace" }}>
              If you understand this risk, type <strong style={{ color: "#ff6b6b" }}>DELETE</strong> to confirm:
            </div>
            <input value={clearTxInput} onChange={e => setClearTxInput(e.target.value)}
              placeholder="Type DELETE to confirm"
              style={{ width: "100%", background: "#0d1117", border: `1px solid ${clearTxInput === "DELETE" ? "#ff4444" : "#2a3045"}`, borderRadius: 7, color: "#e8eaf6", padding: "10px 12px", fontFamily: "'DM Mono',monospace", fontSize: 13, outline: "none", marginBottom: 16, boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setClearTxConfirm(false)}
                style={{ flex: 1, padding: "9px 18px", borderRadius: 7, cursor: "pointer", fontFamily: "'DM Mono',monospace", fontWeight: 600, fontSize: 13, background: "transparent", color: "#8899aa", border: "1px solid #2a3045" }}>
                Cancel
              </button>
              <button disabled={clearTxInput !== "DELETE"}
                onClick={async () => {
                  const count = transactions.length;
                  setClearTxConfirm(false);
                  setTransactions([]);
                  if (company?.id) {
                    await db.from("transactions").delete().eq("company_id", company.id);
                    await log({ action: "transactions_cleared_all", module: "transaction_monitoring", entityName: "All transactions deleted", meta: { count, confirmed_delete: true } });
                  }
                }}
                style={{ flex: 1, padding: "9px 18px", borderRadius: 7, cursor: clearTxInput === "DELETE" ? "pointer" : "not-allowed", fontFamily: "'DM Mono',monospace", fontWeight: 600, fontSize: 13, background: "rgba(255,68,68,0.1)", color: "#ff6b6b", border: "1px solid #ff4444", opacity: clearTxInput === "DELETE" ? 1 : 0.4 }}>
                Permanently Delete All Transactions
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TAB: RULES ───────────────────────────────────────────────────────────────

// ─── AI Threshold Optimizer ───────────────────────────────────────────────────
function AIThresholdOptimizer({ rules, setRules, transactions, tmAlerts, cases }) {
  const C = useTheme();
  const { t } = useTranslation();
  const PROXY = import.meta.env.VITE_PROXY_URL || "http://127.0.0.1:3001";
  const [analyzing, setAnalyzing] = useState(false);
  const [suggestions, setSuggestions] = useState(() => {
    try { return JSON.parse(localStorage.getItem("crg_ai_suggestions") || "[]"); } catch { return []; }
  });
  const [lastRun, setLastRun] = useState(() => localStorage.getItem("crg_ai_last_run") || null);
  const [expanded, setExpanded] = useState(false);
  const [applying, setApplying] = useState(null);

  // Auto-run daily
  useEffect(() => {
    if (!lastRun) return;
    const diff = Date.now() - new Date(lastRun).getTime();
    if (diff > 24 * 60 * 60 * 1000) analyze();
  }, []);

  async function analyze() {
    if (analyzing) return;
    setAnalyzing(true);
    setSuggestions([]);

    const alertSummary = tmAlerts.slice(-50).map(a => ({
      rule: a.ruleId, risk: a.risk, amount: a.amount, type: a.type,
    }));
    const ruleSummary = rules.filter(r => r.enabled).map(r => ({
      id: r.id, name: r.name, threshold: r.threshold, severity: r.severity,
      alertCount: tmAlerts.filter(a => a.ruleId === r.id).length,
    }));
    const txSummary = {
      total: transactions.length,
      avgAmount: transactions.length ? Math.round(transactions.reduce((s, t) => s + (parseFloat(String(t.amount).replace(/[^0-9.]/g,"")) || 0), 0) / transactions.length) : 0,
      highRiskCount: tmAlerts.filter(a => a.risk === "HIGH" || a.risk === "CRITICAL").length,
      falsePositiveEstimate: tmAlerts.filter(a => a.status === "closed").length,
    };

    // Build case outcome metrics
    const caseMetrics = {
      totalCases: (cases || []).length,
      falsePositives: (cases || []).filter(c => 
        c.status === "closed" && c.notes && c.notes.toLowerCase().includes("false positive")
      ).map(c => ({ type: c.type, risk: c.risk, amount: c.amount, country: c.country })),
      truePositives: (cases || []).filter(c => 
        c.status === "escalated" || c.review_action === "escalate"
      ).map(c => ({ type: c.type, risk: c.risk, amount: c.amount, country: c.country })),
      pendingReview: (cases || []).filter(c => c.status === "pending_review").length,
      byType: (cases || []).reduce((acc, c) => {
        acc[c.type] = acc[c.type] || { total: 0, falsePositive: 0, escalated: 0 };
        acc[c.type].total++;
        if (c.status === "closed" && c.notes?.toLowerCase().includes("false positive")) acc[c.type].falsePositive++;
        if (c.status === "escalated") acc[c.type].escalated++;
        return acc;
      }, {}),
    };

    try {
      const res = await fetch(`${PROXY}/api/ai`, {
        method: "POST",
        signal: AbortSignal.timeout(90000),
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 2000,
          system: `You are an AML compliance AI that analyzes transaction monitoring rule performance using both rule metrics AND case management outcomes to suggest threshold optimizations.
Respond ONLY with a valid JSON array of suggestions. No markdown, no explanation outside JSON.
Each suggestion: { ruleId, ruleName, currentThreshold, suggestedThreshold, reason, impact, confidence, caseInsight }
- confidence: "high"|"medium"|"low"
- impact: "reduce false positives"|"catch more suspicious activity"|"improve precision"
- caseInsight: one sentence referencing specific case outcome data (e.g. "3 of 5 Round Amount cases were closed as false positives")
Suggest 4-7 optimizations. Prioritize rules where case data shows high false positive rates.`,
          messages: [{
            role: "user",
            content: `Analyze TM rules using both rule metrics and case management outcomes:

RULES: ${JSON.stringify(ruleSummary)}
RECENT TM ALERTS (last 50): ${JSON.stringify(alertSummary)}
TRANSACTION SUMMARY: ${JSON.stringify(txSummary)}
CASE MANAGEMENT OUTCOMES: ${JSON.stringify(caseMetrics)}

Cross-reference alert types with case outcomes to identify which rules generate false positives vs true positives.
Return JSON array only.`
          }]
        })
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || "[]";
      const clean = text.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setSuggestions(parsed);
      localStorage.setItem("crg_ai_suggestions", JSON.stringify(parsed));
      const now = new Date().toISOString();
      setLastRun(now);
      localStorage.setItem("crg_ai_last_run", now);
    } catch (e) {
      console.error("AI analysis failed:", e);
    }
    setAnalyzing(false);
  }

  function applySuggestion(s) {
    setApplying(s.ruleId);
    setRules(prev => prev.map(r => r.id === s.ruleId ? { ...r, threshold: s.suggestedThreshold } : r));
    setSuggestions(prev => prev.filter(x => x.ruleId !== s.ruleId));
    localStorage.setItem("crg_ai_suggestions", JSON.stringify(suggestions.filter(x => x.ruleId !== s.ruleId)));
    setTimeout(() => setApplying(null), 800);
  }

  const confidenceColor = { high: C.success, medium: C.warn, low: C.textDim };
  const impactIcon = { "reduce false positives": "🎯", "catch more suspicious activity": "🔍", "improve precision": "⚡" };

  return (
    <div style={{ marginBottom: 24, background: "rgba(0,212,255,0.04)", border: `1px solid ${C.borderLight}`, borderRadius: 10, overflow: "hidden" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", cursor: "pointer", borderBottom: expanded ? `1px solid ${C.border}` : "none" }} onClick={() => setExpanded(e => !e)}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>🤖</span>
          <div>
            <div style={{ color: C.accent, fontSize: 13, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>{t('tm.aiOptimizer.title')}</div>
            <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>
              {lastRun ? `${t('tm.aiOptimizer.lastRun')} ${new Date(lastRun).toLocaleString()}` : t('tm.aiOptimizer.noSuggestions').split('.')[0]}
              {suggestions.length > 0 && <span style={{ color: C.warn, marginLeft: 8 }}>● {suggestions.length} {t('tm.aiOptimizer.suggestions').toLowerCase()}</span>}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={e => { e.stopPropagation(); analyze(); }}
            disabled={analyzing}
            style={{ padding: "7px 16px", background: analyzing ? C.border : C.accent, border: "none", borderRadius: 6, color: analyzing ? C.textDim : C.bg, fontSize: 12, fontWeight: 700, cursor: analyzing ? "wait" : "pointer", fontFamily: "'DM Mono', monospace" }}>
            {analyzing ? t('tm.aiOptimizer.analyzing') : t('tm.aiOptimizer.analyze')}
          </button>
          <span style={{ color: C.textDim, fontSize: 16 }}>{expanded ? "▲" : "▼"}</span>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: "16px 18px" }}>
          {analyzing && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 0", justifyContent: "center" }}>
              <div style={{ width: 20, height: 20, border: `2px solid ${C.accent}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
              <span style={{ color: C.textMid, fontSize: 13 }}>{t('tm.aiOptimizer.analyzing')} {transactions.length} transactions and {rules.filter(r => r.enabled).length} active rules…</span>
            </div>
          )}
          {!analyzing && suggestions.length === 0 && (
            <div style={{ color: C.textDim, fontSize: 13, textAlign: "center", padding: "20px 0" }}>
              {t('tm.aiOptimizer.noSuggestions')}
            </div>
          )}
          {!analyzing && suggestions.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {suggestions.map((s, i) => (
                <div key={i} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 16px" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <span style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{s.ruleName}</span>
                        <span style={{ color: confidenceColor[s.confidence], fontSize: 10, fontFamily: "'DM Mono', monospace", textTransform: "uppercase", background: `${confidenceColor[s.confidence]}18`, padding: "2px 7px", borderRadius: 4 }}>{s.confidence} {t('tm.aiOptimizer.suggestions').toLowerCase()}</span>
                        <span style={{ fontSize: 11 }}>{impactIcon[s.impact]}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <span style={{ color: C.textDim, fontSize: 12, fontFamily: "'DM Mono', monospace" }}>${s.currentThreshold?.toLocaleString()}</span>
                        <span style={{ color: C.textDim }}>→</span>
                        <span style={{ color: C.accent, fontSize: 13, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>${s.suggestedThreshold?.toLocaleString()}</span>
                        <span style={{ color: s.suggestedThreshold < s.currentThreshold ? C.danger : C.success, fontSize: 11 }}>
                          {s.suggestedThreshold < s.currentThreshold ? "▼" : "▲"} {Math.abs(Math.round((s.suggestedThreshold - s.currentThreshold) / s.currentThreshold * 100))}%
                        </span>
                      </div>
                      <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.5 }}>{s.reason}</div>
                      {s.caseInsight && <div style={{ color: C.gold || "#B8A164", fontSize: 11, marginTop: 5, fontStyle: "italic" }}>📋 {s.caseInsight}</div>}
                    </div>
                    <button
                      onClick={() => applySuggestion(s)}
                      disabled={applying === s.ruleId}
                      style={{ padding: "8px 16px", background: applying === s.ruleId ? C.success : C.accentGlow, border: `1px solid ${applying === s.ruleId ? C.success : C.accent}`, borderRadius: 6, color: applying === s.ruleId ? C.success : C.accent, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap", flexShrink: 0 }}>
                      {applying === s.ruleId ? `✓ ${t('tm.aiOptimizer.apply')}d` : t('tm.aiOptimizer.apply')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Rule builder constants ────────────────────────────────────────────────────
const HIGH_RISK_COUNTRIES = [
  "Afghanistan","Albania","Barbados","Burkina Faso","Cameroon","Cayman Islands",
  "Democratic Republic of Congo","Gibraltar","Haiti","Iran","Jamaica","Jordan",
  "Mali","Morocco","Mozambique","Myanmar","Nicaragua","Nigeria","North Korea",
  "Pakistan","Panama","Philippines","Russia","Senegal","South Africa","South Sudan",
  "Sudan","Syria","Tanzania","Türkiye","Uganda","United Arab Emirates",
  "Venezuela","Vietnam","Yemen",
];

const RULE_TEMPLATES = [
  {
    label: "CTR / Large Cash",
    icon: "💵",
    description: "Flag any cash transaction at or above the CTR reporting threshold.",
    form: { name: "Large Cash Deposit / Withdrawal", category: "Bank", severity: "HIGH", threshold: 10000, txType: "Cash", direction: "any", description: "Flags cash transactions meeting CTR filing threshold (31 USC 5313). Review for CTR obligation.", reference: "31 USC 5313, FinCEN CTR", velocityWindowDays: 0, velocityMaxCount: 0, velocityMaxVolume: 0, selectedCountries: [], pepOnly: false },
  },
  {
    label: "Structuring (Velocity)",
    icon: "🔁",
    description: "Multiple transactions just below reporting thresholds from the same entity within a short window.",
    form: { name: "Structuring / Smurfing Pattern", category: "Bank", severity: "CRITICAL", threshold: 3000, txType: "Any", direction: "any", description: "Detects potential structuring: multiple transactions below CTR threshold within 7 days by same entity. (31 USC 5324)", reference: "31 USC 5324, FATF R.10", velocityWindowDays: 7, velocityMaxCount: 3, velocityMaxVolume: 9000, selectedCountries: [], pepOnly: false },
  },
  {
    label: "High-Risk Country Wire",
    icon: "🌍",
    description: "Wire transfers to/from FATF high-risk or sanctioned jurisdictions.",
    form: { name: "High-Risk Jurisdiction Wire Transfer", category: "Bank", severity: "HIGH", threshold: 1000, txType: "Wire", direction: "any", description: "Flags wire transfers involving FATF high-risk or OFAC-sanctioned jurisdictions above minimum threshold.", reference: "FATF R.19, 31 CFR 1010.820", velocityWindowDays: 0, velocityMaxCount: 0, velocityMaxVolume: 0, selectedCountries: ["Iran","North Korea","Russia","Syria","Myanmar","Sudan"], pepOnly: false },
  },
  {
    label: "PEP Transaction",
    icon: "🏛",
    description: "Any transaction above threshold involving a politically exposed person or watchlisted entity.",
    form: { name: "PEP / Watchlist Transaction", category: "Bank", severity: "HIGH", threshold: 5000, txType: "Any", direction: "any", description: "Flags transactions from/to politically exposed persons or internally watchlisted entities requiring EDD.", reference: "FATF R.12, BSA EDD requirements", velocityWindowDays: 0, velocityMaxCount: 0, velocityMaxVolume: 0, selectedCountries: [], pepOnly: true },
  },
  {
    label: "Rapid Fund Movement",
    icon: "⚡",
    description: "High-volume wire or crypto movement suggesting layering.",
    form: { name: "Rapid Fund Movement / Layering", category: "Fintech", severity: "HIGH", threshold: 10000, txType: "Wire", direction: "any", description: "Detects rapid layering behavior: large wire transfers that may indicate placement/layering stage of money laundering.", reference: "FATF R.29, FinCEN SAR guidance", velocityWindowDays: 0, velocityMaxCount: 0, velocityMaxVolume: 0, selectedCountries: [], pepOnly: false },
  },
  {
    label: "Crypto Large Transfer",
    icon: "₿",
    description: "Large crypto transactions requiring Travel Rule compliance review.",
    form: { name: "Large Crypto Transfer — Travel Rule", category: "Crypto", severity: "HIGH", threshold: 3000, txType: "Crypto", direction: "any", description: "Flags large crypto transfers requiring Travel Rule / FATF R.16 compliance and VASP due diligence.", reference: "FATF R.16, EU TFR, FinCEN Travel Rule", velocityWindowDays: 0, velocityMaxCount: 0, velocityMaxVolume: 0, selectedCountries: [], pepOnly: false },
  },
];

function RulesTab({ rules, setRules, transactions, tmAlerts, cases, log }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [filterCat, setFilterCat]         = useState("All");
  const [filterSev, setFilterSev]         = useState("All");
  const [editingRule, setEditingRule]     = useState(null);
  const [editForm, setEditForm]           = useState({});
  const [showNewRule, setShowNewRule]     = useState(false);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [search, setSearch]              = useState("");
  // E-016 / F-008: Rule version history (E-017: audit trail via log())
  const [ruleHistory, setRuleHistory]     = useState(() => {
    try { return JSON.parse(localStorage.getItem("crg_tm_rule_history") || "{}"); } catch { return {}; }
  });
  const [showHistoryFor, setShowHistoryFor] = useState(null);
  useEffect(() => {
    try { localStorage.setItem("crg_tm_rule_history", JSON.stringify(ruleHistory)); } catch {}
  }, [ruleHistory]);

  const categories = ["All", "Bank", "Fintech", "Crypto", "Payments", "Investment"];
  const severities = ["All", "CRITICAL", "HIGH", "MEDIUM", "LOW"];

  const filtered = rules.filter(r => {
    if (filterCat !== "All" && r.category !== filterCat) return false;
    if (filterSev !== "All" && r.severity !== filterSev) return false;
    if (search && !r.name.toLowerCase().includes(search.toLowerCase()) && !r.description.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: rules.length,
    enabled: rules.filter(r => r.enabled).length,
    critical: rules.filter(r => r.severity === "CRITICAL" && r.enabled).length,
    high: rules.filter(r => r.severity === "HIGH" && r.enabled).length,
  };

  function exportRulesPDF() {
    const rows = rules.map(r => `<tr><td>${r.name}</td><td>${r.category}</td><td><span class="badge badge-${(r.severity||"").toLowerCase()}">${r.severity}</span></td><td>${r.enabled ? "Enabled" : "Disabled"}</td><td>${r.description}</td></tr>`).join("");
    printToPDF("TM Rules Configuration", `<div class="section"><div class="section-title">Transaction Monitoring Rules (${rules.length} total, ${rules.filter(r=>r.enabled).length} enabled)</div><table><thead><tr><th>Rule Name</th><th>Category</th><th>Severity</th><th>Status</th><th>Description</th></tr></thead><tbody>${rows}</tbody></table></div>`);
  }
  async function toggleRule(id) {
    const rule = rules.find(r => r.id === id);
    const newEnabled = !rule?.enabled;
    setRules(prev => prev.map(r => r.id === id ? { ...r, enabled: newEnabled } : r));
    await log({ action: newEnabled ? "tm_rule_enabled" : "tm_rule_disabled", module: "transaction_monitoring", entityName: rule?.name || id, meta: { ruleId: id, category: rule?.category, severity: rule?.severity } });
  }

  function startEdit(rule) {
    setEditingRule(rule.id);
    setEditForm({ ...rule });
  }

  async function saveEdit() {
    const oldRule = rules.find(r => r.id === editingRule);
    const newVersion = (oldRule?._version || 1) + 1;
    const updatedRule = { ...editForm, _version: newVersion, _lastEditedAt: new Date().toISOString() };
    setRules(prev => prev.map(r => r.id === editingRule ? updatedRule : r));
    if (oldRule) {
      setRuleHistory(prev => {
        const hist = [...(prev[editingRule] || [])];
        hist.push({ ...oldRule, _savedAt: new Date().toISOString() });
        return { ...prev, [editingRule]: hist.slice(-10) };
      });
    }
    setEditingRule(null);
    await log({ action: "tm_rule_edited", module: "transaction_monitoring", entityName: editForm.name, oldValue: oldRule, newValue: updatedRule, meta: { ruleId: editingRule, version: newVersion } });
  }

  async function deleteRule(id) {
    if (!window.confirm(t('tm.rules.deleteConfirm'))) return;
    const rule = rules.find(r => r.id === id);
    setRules(prev => prev.filter(r => r.id !== id));
    await log({ action: "tm_rule_deleted", module: "transaction_monitoring", entityName: rule?.name || id, meta: { ruleId: id, category: rule?.category, severity: rule?.severity } });
  }

  async function addNewRule() {
    if (!editForm.name?.trim()) return;
    if (!parseFloat(editForm.threshold)) { alert("A threshold amount is required for the rule to fire."); return; }
    const countries = editForm.selectedCountries?.length
      ? editForm.selectedCountries
      : (editForm.countriesRaw || "").split(",").map(s => s.trim()).filter(Boolean);
    const newRule = {
      id: editForm.id?.trim() || `CUSTOM-${Date.now()}`,
      category: editForm.category || "Bank",
      enabled: true,
      severity: editForm.severity || "MEDIUM",
      name: editForm.name.trim(),
      description: editForm.description || "",
      threshold: parseFloat(editForm.threshold) || 0,
      txType: editForm.txType || "Any",
      direction: editForm.direction || "any",
      highRiskCountries: countries,
      pepOnly: !!editForm.pepOnly,
      velocityWindowDays: parseInt(editForm.velocityWindowDays) || 0,
      velocityMaxCount: parseInt(editForm.velocityMaxCount) || 0,
      velocityMaxVolume: parseFloat(editForm.velocityMaxVolume) || 0,
      reference: editForm.reference || "",
      tags: countries.length ? ["custom", ...countries.slice(0,2)] : ["custom"],
      fields: [{ key: "threshold", label: "Amount Threshold (USD)", type: "number" }],
    };
    const versionedRule = { ...newRule, _version: 1, _createdAt: new Date().toISOString() };
    setRules(prev => [...prev, versionedRule]);
    setShowNewRule(false);
    setEditForm({});
    await log({ action: "tm_rule_created", module: "transaction_monitoring", entityName: versionedRule.name, newValue: versionedRule, meta: { ruleId: versionedRule.id } });
  }

  async function resetToDefaults() {
    if (!window.confirm(t('tm.rules.resetDefaultConfirm'))) return;
    const prevCount = rules.length;
    setRules(DEFAULT_RULES);
    await log({ action: "tm_rules_reset_to_defaults", module: "transaction_monitoring", entityName: "All TM rules reset to defaults", meta: { previous_rule_count: prevCount, default_rule_count: DEFAULT_RULES.length } });
  }

  return (
    <div>
      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: t('tm.rules.totalRules'), value: stats.total, color: C.accent },
          { label: t('tm.rules.activeRules'), value: stats.enabled, color: C.success },
          { label: t('tm.rules.criticalActive'), value: stats.critical, color: "#FF6B6B" },
          { label: t('tm.rules.highActive'), value: stats.high, color: C.danger },
        ].map(s => (
          <div key={s.label} style={{ background: C.surface, border: `1px solid ${C.border}`, borderTop: `2px solid ${s.color}`, borderRadius: 10, padding: "14px 18px" }}>
            <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 6 }}>{s.label.toUpperCase()}</div>
            <div style={{ color: s.color, fontFamily: "'Syne', sans-serif", fontSize: 26, fontWeight: 700 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <AIThresholdOptimizer rules={rules} setRules={setRules} transactions={transactions || []} tmAlerts={tmAlerts || []} cases={cases || []} />
        <input type="text" placeholder={t('tm.rules.searchRules')} value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, maxWidth: 260, padding: "8px 12px", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, fontFamily: "'DM Mono', monospace", outline: "none" }} />
        <div style={{ display: "flex", gap: 6 }}>
          {categories.map(c => (
            <button key={c} onClick={() => setFilterCat(c)} style={{ padding: "5px 12px", borderRadius: 6, fontSize: 11, cursor: "pointer", background: filterCat === c ? C.accentGlow : "transparent", border: `1px solid ${filterCat === c ? C.accent : C.border}`, color: filterCat === c ? C.accent : C.textMid, fontFamily: "'DM Mono', monospace" }}>{c}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {severities.map(s => (
            <button key={s} onClick={() => setFilterSev(s)} style={{ padding: "5px 12px", borderRadius: 6, fontSize: 11, cursor: "pointer", background: filterSev === s ? C.accentGlow : "transparent", border: `1px solid ${filterSev === s ? C.accent : C.border}`, color: filterSev === s ? C.accent : C.textMid, fontFamily: "'DM Mono', monospace" }}>{s}</button>
          ))}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Btn small variant="ghost" onClick={resetToDefaults}>↺ {t('tm.rules.resetDefault')}</Btn>
          <Btn small variant="ghost" onClick={exportRulesPDF}>⬇ {t('common.pdf')}</Btn>
          <Btn small onClick={() => { setShowNewRule(true); setEditForm({}); }}>+ {t('tm.rules.addRule')}</Btn>
        </div>
      </div>

      {/* New Rule Modal */}
      {showNewRule && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => { setShowNewRule(false); setShowCountryPicker(false); }}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 32, width: 620, maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{t('tm.rules.addCustomRule')}</div>
            <div style={{ color: C.textDim, fontSize: 12, fontFamily: "'DM Mono',monospace", marginBottom: 18 }}>Start from a template or configure from scratch.</div>

            {/* ── Template Picker ── */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 8 }}>START FROM A TEMPLATE</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
                {RULE_TEMPLATES.map(tpl => (
                  <button key={tpl.label} onClick={() => setEditForm({ ...tpl.form, selectedCountries: [...(tpl.form.selectedCountries || [])] })}
                    style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", cursor: "pointer", textAlign: "left", transition: "border-color 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = C.accent}
                    onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
                    <div style={{ fontSize: 18, marginBottom: 4 }}>{tpl.icon}</div>
                    <div style={{ color: C.text, fontSize: 12, fontWeight: 600, marginBottom: 3 }}>{tpl.label}</div>
                    <div style={{ color: C.textDim, fontSize: 10, fontFamily: "'DM Mono',monospace", lineHeight: 1.4 }}>{tpl.description}</div>
                  </button>
                ))}
              </div>
            </div>

            <div style={{ borderTop: `1px solid ${C.border}`, marginBottom: 18 }} />

            {/* ── Row 1: Name + Category + Severity ── */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>{t('tm.rules.ruleName').toUpperCase()} *</div>
              <input value={editForm.name || ""} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. High Cash Deposit"
                style={{ width: "100%", background: C.bg, border: `1px solid ${editForm.name ? C.border : C.danger}`, borderRadius: 7, color: C.text, padding: "9px 12px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
              <div>
                <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>{t('tm.rules.category').toUpperCase()}</div>
                <select value={editForm.category || "Bank"} onChange={e => setEditForm(p => ({ ...p, category: e.target.value }))}
                  style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "9px 12px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none" }}>
                  {["Bank","Fintech","Crypto","Payments","Investment"].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>{t('tm.rules.severity').toUpperCase()}</div>
                <select value={editForm.severity || "MEDIUM"} onChange={e => setEditForm(p => ({ ...p, severity: e.target.value }))}
                  style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "9px 12px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none" }}>
                  {["CRITICAL","HIGH","MEDIUM","LOW"].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>

            {/* ── Description ── */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>{t('tm.rules.description').toUpperCase()}</div>
              <textarea value={editForm.description || ""} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} rows={2}
                style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "9px 12px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
            </div>

            {/* ── Threshold + Type + Direction ── */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
              <div>
                <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>AMOUNT THRESHOLD (USD) *</div>
                <input type="number" value={editForm.threshold || ""} onChange={e => setEditForm(p => ({ ...p, threshold: e.target.value }))} placeholder="e.g. 10000"
                  style={{ width: "100%", background: C.bg, border: `1px solid ${C.accent}60`, borderRadius: 7, color: C.text, padding: "9px 12px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div>
                <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>TRANSACTION TYPE</div>
                <select value={editForm.txType || "Any"} onChange={e => setEditForm(p => ({ ...p, txType: e.target.value }))}
                  style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "9px 12px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none" }}>
                  {["Any","Wire","Cash","Crypto","P2P","SWIFT"].map(tt => <option key={tt}>{tt}</option>)}
                </select>
              </div>
              <div>
                <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>DIRECTION</div>
                <select value={editForm.direction || "any"} onChange={e => setEditForm(p => ({ ...p, direction: e.target.value }))}
                  style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "9px 12px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none" }}>
                  <option key="any" value="any">Any Direction</option>
                  <option key="in" value="in">Inbound Only</option>
                  <option key="out" value="out">Outbound Only</option>
                </select>
              </div>
            </div>

            {/* ── High-Risk Countries Multi-Select ── */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 6 }}>HIGH-RISK COUNTRIES (leave empty = flag all countries)</div>
              {/* Selected tags */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {(editForm.selectedCountries || []).map(c => (
                  <span key={c} style={{ display: "flex", alignItems: "center", gap: 4, background: `${C.accent}22`, border: `1px solid ${C.accent}55`, borderRadius: 4, padding: "3px 8px", fontSize: 11, color: C.accent, fontFamily: "'DM Mono',monospace" }}>
                    {c}
                    <button onClick={() => setEditForm(p => ({ ...p, selectedCountries: (p.selectedCountries||[]).filter(x => x !== c) }))}
                      style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", padding: 0, lineHeight: 1, fontSize: 13 }}>×</button>
                  </span>
                ))}
                {!(editForm.selectedCountries?.length) && (
                  <span style={{ color: C.textDim, fontSize: 11, fontFamily: "'DM Mono',monospace", fontStyle: "italic" }}>None selected — rule applies to all countries</span>
                )}
              </div>
              <button onClick={() => setShowCountryPicker(p => !p)}
                style={{ padding: "7px 14px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMid, fontSize: 12, fontFamily: "'DM Mono',monospace", cursor: "pointer" }}>
                {showCountryPicker ? "▲ Hide country list" : "▼ Select high-risk countries"}
              </button>
              {showCountryPicker && (
                <div style={{ marginTop: 8, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px", maxHeight: 180, overflowY: "auto" }}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <button onClick={() => setEditForm(p => ({ ...p, selectedCountries: [...HIGH_RISK_COUNTRIES] }))}
                      style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${C.accent}`, borderRadius: 5, color: C.accent, fontSize: 10, fontFamily: "'DM Mono',monospace", cursor: "pointer" }}>Select All</button>
                    <button onClick={() => setEditForm(p => ({ ...p, selectedCountries: [] }))}
                      style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 5, color: C.textDim, fontSize: 10, fontFamily: "'DM Mono',monospace", cursor: "pointer" }}>Clear All</button>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4 }}>
                    {HIGH_RISK_COUNTRIES.map(country => {
                      const selected = (editForm.selectedCountries || []).includes(country);
                      return (
                        <label key={country} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "4px 6px", borderRadius: 5, background: selected ? `${C.accent}15` : "transparent" }}>
                          <input type="checkbox" checked={selected}
                            onChange={() => setEditForm(p => {
                              const prev = p.selectedCountries || [];
                              return { ...p, selectedCountries: selected ? prev.filter(x => x !== country) : [...prev, country] };
                            })}
                            style={{ accentColor: C.accent, cursor: "pointer" }} />
                          <span style={{ fontSize: 11, color: selected ? C.accent : C.textMid, fontFamily: "'DM Mono',monospace" }}>{country}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* ── PEP Only ── */}
            <div style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
              <input type="checkbox" id="pepOnlyNew" checked={!!editForm.pepOnly} onChange={e => setEditForm(p => ({ ...p, pepOnly: e.target.checked }))}
                style={{ width: 16, height: 16, accentColor: C.accent, cursor: "pointer" }} />
              <label htmlFor="pepOnlyNew" style={{ color: C.textMid, fontSize: 12, fontFamily: "'DM Mono',monospace", cursor: "pointer" }}>
                Only flag transactions where entity is a PEP or on the internal watchlist
              </label>
            </div>

            {/* ── Velocity / Aggregation ── */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 6 }}>VELOCITY / AGGREGATION <span style={{ fontSize: 9, fontStyle: "italic" }}>(optional — leave blank for single-transaction check)</span></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <div>
                  <div style={{ color: C.textDim, fontSize: 10, marginBottom: 4 }}>WINDOW (DAYS)</div>
                  <select value={editForm.velocityWindowDays || ""} onChange={e => setEditForm(p => ({ ...p, velocityWindowDays: e.target.value ? parseInt(e.target.value) : 0 }))}
                    style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "9px 10px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none" }}>
                    <option key="" value="">None (single tx)</option>
                    <option key="1" value="1">1 Day</option>
                    <option key="7" value="7">7 Days (week)</option>
                    <option key="30" value="30">30 Days (month)</option>
                    <option key="90" value="90">90 Days (quarter)</option>
                    <option key="365" value="365">365 Days (year)</option>
                  </select>
                </div>
                <div>
                  <div style={{ color: C.textDim, fontSize: 10, marginBottom: 4 }}>MAX TX COUNT IN WINDOW</div>
                  <input type="number" value={editForm.velocityMaxCount || ""} onChange={e => setEditForm(p => ({ ...p, velocityMaxCount: e.target.value ? parseInt(e.target.value) : 0 }))} placeholder="e.g. 5"
                    style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "9px 10px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                </div>
                <div>
                  <div style={{ color: C.textDim, fontSize: 10, marginBottom: 4 }}>MAX TOTAL VOLUME</div>
                  <input type="number" value={editForm.velocityMaxVolume || ""} onChange={e => setEditForm(p => ({ ...p, velocityMaxVolume: e.target.value ? parseFloat(e.target.value) : 0 }))} placeholder="e.g. 50000"
                    style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "9px 10px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                </div>
              </div>
            </div>

            {/* ── Rule Preview ── */}
            <div style={{ marginBottom: 18, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, padding: "10px 14px" }}>
              <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 4 }}>RULE PREVIEW</div>
              <div style={{ color: C.accent, fontFamily: "'DM Mono',monospace", fontSize: 11, lineHeight: 1.5 }}>
                {editForm.velocityWindowDays
                  ? `Alert when same entity exceeds ${editForm.velocityMaxCount ? `${editForm.velocityMaxCount} transactions` : ""}${editForm.velocityMaxCount && editForm.velocityMaxVolume ? " OR " : ""}${editForm.velocityMaxVolume ? `$${Number(editForm.velocityMaxVolume).toLocaleString()} total` : ""} within ${editForm.velocityWindowDays} days${editForm.txType && editForm.txType !== "Any" ? ` (${editForm.txType} only)` : ""}${editForm.selectedCountries?.length ? ` in [${editForm.selectedCountries.slice(0,3).join(", ")}${editForm.selectedCountries.length > 3 ? ` +${editForm.selectedCountries.length - 3} more` : ""}]` : ""}`
                  : `Alert when amount ≥ $${Number(editForm.threshold || 0).toLocaleString()}${editForm.txType && editForm.txType !== "Any" ? ` AND type = ${editForm.txType}` : ""}${editForm.direction && editForm.direction !== "any" ? ` AND direction = ${editForm.direction}` : ""}${editForm.selectedCountries?.length ? ` AND country IN [${editForm.selectedCountries.slice(0,3).join(", ")}${editForm.selectedCountries.length > 3 ? ` +${editForm.selectedCountries.length - 3} more` : ""}]` : ""}${editForm.pepOnly ? " AND entity is PEP/watchlisted" : ""}`
                }
              </div>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <Btn variant="ghost" onClick={() => { setShowNewRule(false); setShowCountryPicker(false); }} style={{ flex: 1 }}>{t('common.cancel')}</Btn>
              <Btn onClick={addNewRule} disabled={!editForm.name} style={{ flex: 2 }}>{t('tm.rules.addRule')}</Btn>
            </div>
          </div>
        </div>
      )}

      {/* Rules list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtered.map(rule => (
          <div key={rule.id} style={{
            background: C.surface, border: `1px solid ${rule.enabled ? C.border : "rgba(74,88,112,0.4)"}`,
            borderLeft: `3px solid ${rule.enabled ? (rule.severity === "CRITICAL" ? "#FF6B6B" : rule.severity === "HIGH" ? C.danger : rule.severity === "MEDIUM" ? C.warn : C.success) : C.textDim}`,
            borderRadius: 10, padding: "16px 20px",
            opacity: rule.enabled ? 1 : 0.6,
          }}>
            {editingRule === rule.id ? (
              <div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                  <div>
                    <div style={{ color: C.textDim, fontSize: 9, letterSpacing: "0.08em", marginBottom: 4 }}>{t('tm.rules.ruleName').toUpperCase()}</div>
                    <input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                      style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, padding: "7px 10px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                  </div>
                  <div>
                    <div style={{ color: C.textDim, fontSize: 9, letterSpacing: "0.08em", marginBottom: 4 }}>{t('tm.rules.category').toUpperCase()}</div>
                    <select value={editForm.category} onChange={e => setEditForm(p => ({ ...p, category: e.target.value }))}
                      style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, padding: "7px 10px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none" }}>
                      {["Bank","Fintech","Crypto","Payments","Investment"].map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{ color: C.textDim, fontSize: 9, letterSpacing: "0.08em", marginBottom: 4 }}>{t('tm.rules.severity').toUpperCase()}</div>
                    <select value={editForm.severity} onChange={e => setEditForm(p => ({ ...p, severity: e.target.value }))}
                      style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, padding: "7px 10px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none" }}>
                      {["CRITICAL","HIGH","MEDIUM","LOW"].map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{ color: C.textDim, fontSize: 9, letterSpacing: "0.08em", marginBottom: 4 }}>{t('tm.rules.thresholdUsd').toUpperCase()}</div>
                    <input type="number" value={editForm.threshold || ""} onChange={e => setEditForm(p => ({ ...p, threshold: parseFloat(e.target.value) || 0 }))}
                      style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, padding: "7px 10px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                  </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ color: C.textDim, fontSize: 9, letterSpacing: "0.08em", marginBottom: 4 }}>{t('tm.rules.description').toUpperCase()}</div>
                  <textarea value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} rows={2}
                    style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, padding: "7px 10px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
                </div>
                {rule.fields && rule.fields.length > 0 && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 10, marginBottom: 10 }}>
                    {rule.fields.map(f => (
                      <div key={f.key}>
                        <div style={{ color: C.textDim, fontSize: 9, letterSpacing: "0.08em", marginBottom: 4 }}>{f.label.toUpperCase()}</div>
                        <input type={f.type === "number" ? "number" : "text"} value={editForm[f.key] ?? ""} onChange={e => setEditForm(p => ({ ...p, [f.key]: f.type === "number" ? parseFloat(e.target.value) || 0 : e.target.value }))}
                          style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.accent, padding: "7px 10px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn small variant="success" onClick={saveEdit}>✓ {t('tm.rules.saveRule')}</Btn>
                  <Btn small variant="ghost" onClick={() => setEditingRule(null)}>{t('common.cancel')}</Btn>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                <div onClick={() => toggleRule(rule.id)} style={{ cursor: "pointer", marginTop: 3 }}>
                  <div style={{ width: 36, height: 20, borderRadius: 10, background: rule.enabled ? C.accent : C.border, position: "relative", transition: "background 0.2s" }}>
                    <div style={{ position: "absolute", top: 3, left: rule.enabled ? 18 : 3, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>{rule.name}</span>
                    <CategoryBadge cat={rule.category} />
                    <SeverityBadge sev={rule.severity} />
                    <span style={{ color: C.textDim, fontSize: 10, fontFamily: "'DM Mono', monospace", marginLeft: 4 }}>{rule.id}</span>
                  </div>
                  <div style={{ color: C.textMid, fontSize: 12, marginBottom: 6 }}>{rule.description}</div>
                  <code style={{ color: C.accent, fontSize: 11, background: C.bg, padding: "4px 10px", borderRadius: 4, display: "inline-block", marginBottom: 6 }}>{rule.logic}</code>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    {rule.threshold > 0 && <span style={{ color: C.textDim, fontSize: 11 }}>{t('tm.rules.thresholdLabel')}: <span style={{ color: C.text }}>${rule.threshold?.toLocaleString()}</span></span>}
                    <span style={{ color: C.textDim, fontSize: 11 }}>{t('tm.rules.reference')}: <span style={{ color: C.textMid }}>{rule.reference}</span></span>
                  </div>
                  {rule.tags && (
                    <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
                      {rule.tags.map(tag => (
                        <span key={tag} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 3, background: C.bg, color: C.textDim, border: `1px solid ${C.border}` }}>{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {ruleHistory[rule.id]?.length > 0 && (
                    <Btn small variant="ghost" onClick={() => setShowHistoryFor(showHistoryFor === rule.id ? null : rule.id)}>
                      📋 v{rule._version || 1}
                    </Btn>
                  )}
                  <Btn small variant="subtle" onClick={() => startEdit(rule)}>{t('tm.rules.editRule')}</Btn>
                  <Btn small variant="danger" onClick={() => deleteRule(rule.id)}>✕</Btn>
                </div>
              </div>
            )}
            {/* E-016: Version history panel */}
            {showHistoryFor === rule.id && ruleHistory[rule.id]?.length > 0 && (
              <div style={{ marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
                <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 8 }}>VERSION HISTORY</div>
                {[...ruleHistory[rule.id]].reverse().map((v, i) => (
                  <div key={i} style={{ fontSize: 11, color: C.textMid, padding: "4px 0", borderBottom: `1px solid ${C.border}`, display: "flex", gap: 12 }}>
                    <span style={{ color: C.textDim, fontFamily: "'DM Mono', monospace" }}>v{v._version || (ruleHistory[rule.id].length - i)}</span>
                    <span style={{ color: C.textDim }}>{v._savedAt ? new Date(v._savedAt).toLocaleString() : "—"}</span>
                    <span>Threshold: <span style={{ color: C.text }}>${(v.threshold || 0).toLocaleString()}</span></span>
                    <span>Severity: <span style={{ color: C.text }}>{v.severity}</span></span>
                    <span style={{ marginLeft: "auto", color: rule.enabled ? C.success : C.textDim }}>{v.enabled ? "Enabled" : "Disabled"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── TAB: GENERATED ALERTS ────────────────────────────────────────────────────
const DISMISS_REASONS = [
  { value: "false_positive_threshold",  label: "False positive — rule threshold too low" },
  { value: "false_positive_known",      label: "False positive — known/expected business activity" },
  { value: "risk_accepted",             label: "Risk accepted — reviewed and documented in case file" },
  { value: "duplicate",                 label: "Duplicate — already under active investigation" },
  { value: "escalated_external",        label: "Escalated externally — SAR/STR filed outside platform" },
  { value: "out_of_scope",              label: "Out of scope — rule misfire or data quality issue" },
];

// ─── ITEM 3: DISPOSITION OPTIONS ──────────────────────────────────────────────
const DISPOSITION_OPTIONS = [
  { value: "open",              label: "Open" },
  { value: "under_investigation", label: "Under Investigation" },
  { value: "escalated_to_sar", label: "Escalated to SAR" },
  { value: "closed_no_action", label: "Closed - No Action" },
  { value: "false_positive",   label: "False Positive" },
  { value: "sent_to_case",     label: "Sent to Case" },
];

function DispositionBadge({ disposition }) {
  const C = useTheme();
  const map = {
    open:               { color: C.warn,                   bg: "rgba(255,140,0,0.1)" },
    under_investigation:{ color: C.purple || "#A78BFA",    bg: "rgba(167,139,250,0.1)" },
    escalated_to_sar:   { color: "#FF6B6B",                bg: "rgba(255,68,68,0.1)" },
    closed_no_action:   { color: C.textDim,                bg: C.surfaceAlt },
    false_positive:     { color: C.textDim,                bg: C.surfaceAlt },
    sent_to_case:       { color: C.success,                bg: "rgba(0,230,118,0.1)" },
  }[disposition] || { color: C.textDim, bg: C.surfaceAlt };
  const label = DISPOSITION_OPTIONS.find(o => o.value === disposition)?.label || disposition;
  return (
    <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: map.bg, color: map.color, fontFamily: "'DM Mono',monospace", fontWeight: 600, whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
}

function AlertsTab({ tmAlerts, setTmAlerts, onSendToCase, onSendAllToCase, company, log }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [filter, setFilter]           = useState("All");
  const [filterSev, setFilterSev]     = useState("All");
  const [filterRule, setFilterRule]   = useState("All");
  const [searchReason, setSearchReason] = useState("");
  const [alertPage, setAlertPage] = useState(1);
  const ALERT_PAGE_SIZE = 20;
  const [bulkAlertIds, setBulkAlertIds] = useState(new Set());
  const [bulkAlertWorking, setBulkAlertWorking] = useState(false);
  const [dismissModal, setDismissModal] = useState(null); // { type: 'single'|'bulk', alertId, ids }
  const [dismissReason, setDismissReason] = useState("");
  const [clearAlertsConfirm, setClearAlertsConfirm] = useState(false);
  const [clearAlertsInput, setClearAlertsInput] = useState("");
  const [createAlertModal, setCreateAlertModal] = useState(false);
  const EMPTY_ALERT_FORM = { entity: "", amount: "", type: "", severity: "HIGH", country: "", notes: "", ruleCategory: "Manual" };
  const [createAlertForm, setCreateAlertForm] = useState(EMPTY_ALERT_FORM);
  const [createAlertWorking, setCreateAlertWorking] = useState(false);

  // ITEM 3: Assign modal state
  const [assignModal, setAssignModal] = useState(null); // alertId
  const [assignName, setAssignName]   = useState("");
  const [assignWorking, setAssignWorking] = useState(false);
  // ITEM 3: Expanded alert for notes
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");

  async function openAssignModal(alertId) {
    const alert = tmAlerts.find(a => a.id === alertId);
    setAssignName(alert?.assignedTo || "");
    setAssignModal(alertId);
  }

  async function submitAssign() {
    if (!assignModal || !assignName.trim()) return;
    setAssignWorking(true);
    const now = new Date().toISOString();
    setTmAlerts(prev => prev.map(a => a.id === assignModal ? { ...a, assignedTo: assignName.trim(), assignedAt: now } : a));
    if (company?.id) {
      const token = localStorage.getItem('amlguard_token');
      if (token) {
        try {
          await fetch('/api/tm/alerts', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              alert_id: assignModal,
              status: 'assigned',
            }),
          });
        } catch (error) {
          console.warn('[TM] Failed to assign alert:', error.message);
        }
      }
    }
    await log({ action: "alert_assigned", module: "transaction_monitoring", entityId: assignModal, entityName: `Alert ${assignModal}`, meta: { assignedTo: assignName.trim() } });
    setAssignWorking(false);
    setAssignModal(null);
  }

  async function saveInvestigationNotes(alertId, notes) {
    setTmAlerts(prev => prev.map(a => a.id === alertId ? { ...a, investigationNotes: notes } : a));
    if (company?.id) {
      const token = localStorage.getItem('amlguard_token');
      if (token) {
        try {
          await fetch('/api/tm/alerts', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              alert_id: alertId,
              status: 'under_review',
            }),
          });
        } catch (error) {
          console.warn('[TM] Failed to save notes:', error.message);
        }
      }
    }
  }

  async function changeDisposition(alertId, disposition) {
    const now = new Date().toISOString();
    setTmAlerts(prev => prev.map(a => a.id === alertId ? { ...a, status: disposition, disposition, dispositionAt: now } : a));
    if (company?.id) {
      const token = localStorage.getItem('amlguard_token');
      if (token) {
        try {
          await fetch('/api/tm/alerts', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              alert_id: alertId,
              status: disposition,
            }),
          });
        } catch (error) {
          console.warn('[TM] Failed to update alert status in Neon:', error.message);
        }
      }
    }
    await log({ action: "alert_disposition_changed", module: "transaction_monitoring", entityId: alertId, entityName: `Alert ${alertId}`, meta: { disposition } });
    // If disposition is sent_to_case, also route through case handler
    if (disposition === "sent_to_case") {
      const alert = tmAlerts.find(a => a.id === alertId);
      if (alert) onSendToCase({ ...alert, status: "sent_to_case" });
    }
  }

  // Get unique rule names for filter dropdown
  const uniqueRules = ["All", ...new Set(tmAlerts
    .map(a => a.ruleName || a.type || a.ruleCategory || "Unknown")
    .filter(r => r && r !== "Unknown")
  )];

  const filtered = tmAlerts.filter(a => {
    // Status filter
    if (filter !== "All" && a.status !== filter) return false;

    // Severity filter
    if (filterSev !== "All" && a.severity !== filterSev) return false;

    // Rule/Type filter
    const alertRule = a.ruleName || a.type || a.ruleCategory || "";
    if (filterRule !== "All" && filterRule && alertRule !== filterRule) return false;

    // Reason/Notes search
    if (searchReason.trim()) {
      const searchLower = searchReason.toLowerCase();
      const searchableText = [
        a.notes || "",
        a.ruleName || "",
        a.type || "",
        a.entity || "",
        a.ruleCategory || ""
      ].join(" ").toLowerCase();

      if (!searchableText.includes(searchLower)) return false;
    }

    return true;
  });

  useEffect(() => setAlertPage(1), [filter, filterSev, filterRule, searchReason]);

  const totalAlertPages = Math.max(1, Math.ceil(filtered.length / ALERT_PAGE_SIZE));
  const paginatedAlerts = filtered.slice((alertPage - 1) * ALERT_PAGE_SIZE, alertPage * ALERT_PAGE_SIZE);

  const stats = {
    total: tmAlerts.length,
    open: tmAlerts.filter(a => a.status === "open").length,
    under_review: tmAlerts.filter(a => a.status === "under_review").length,
    escalated: tmAlerts.filter(a => a.status === "escalated").length,
    sentToCase: tmAlerts.filter(a => a.status === "sent_to_case").length,
    dismissed: tmAlerts.filter(a => a.status === "dismissed").length,
  };

  function exportAlertsCSV() {
    downloadCSV(tmAlerts.map(a => ({ ID: a.id, Date: a.date, Entity: a.entity, Amount: a.amount, Type: a.type, Severity: a.severity, Status: a.status, Rule: a.rule || "", Country: a.country || "", Notes: a.notes || "" })), `tm_alerts_${new Date().toISOString().slice(0,10)}.csv`);
  }
  function dismiss(id) {
    setDismissModal({ type: "single", alertId: id, ids: [id] });
    setDismissReason("");
  }

  async function executeDismiss() {
    if (!dismissReason || !dismissModal) return;
    const { ids, type: modalType } = dismissModal;
    const reasonLabel = DISMISS_REASONS.find(r => r.value === dismissReason)?.label || dismissReason;
    const stamp = `[Dismissed ${new Date().toISOString().slice(0,10)}: ${reasonLabel}]`;
    setDismissModal(null);
    await log({
      action: modalType === "bulk" ? "alerts_bulk_dismissed" : "alert_dismissed",
      module: "transaction_monitoring",
      entityId: modalType === "single" ? ids[0] : null,
      entityName: modalType === "bulk" ? `${ids.length} alert(s) dismissed` : `Alert ${ids[0]}`,
      meta: { ids, dismiss_reason: dismissReason, reason_label: reasonLabel },
    });
    if (company?.id) {
      const token = localStorage.getItem('amlguard_token');
      if (token) {
        for (const id of ids) {
          try {
            await fetch('/api/tm/alerts', {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                alert_id: id,
                status: 'dismissed',
              }),
            });
          } catch (error) {
            console.warn('[TM] Failed to dismiss alert:', error.message);
          }
        }
      }
    }
    setTmAlerts(prev => prev.map(a =>
      ids.includes(a.id) ? { ...a, status: "dismissed", dismissReason, notes: [a.notes, stamp].filter(Boolean).join(" ") } : a
    ));
    if (modalType === "bulk") {
      setBulkAlertIds(new Set());
      setBulkAlertWorking(false);
    }
  }

  function sendToCase(alert) {
    setTmAlerts(prev => prev.map(a => a.id === alert.id ? { ...a, status: "sent_to_case" } : a));
    onSendToCase(alert);
  }

  // E-018 / F-007: Alert lifecycle — under_review + escalated transitions
  async function markUnderReview(id) {
    setTmAlerts(prev => prev.map(a => a.id === id ? { ...a, status: "under_review", reviewedAt: new Date().toISOString() } : a));
    if (company?.id) await db.from("alerts").update({ status: "under_review" }).eq("id", id).eq("company_id", company.id);
    await log({ action: "alert_under_review", module: "transaction_monitoring", entityId: id, entityName: `Alert ${id}` });
  }

  async function markEscalated(id) {
    setTmAlerts(prev => prev.map(a => a.id === id ? { ...a, status: "escalated", escalatedAt: new Date().toISOString() } : a));
    if (company?.id) await db.from("alerts").update({ status: "escalated" }).eq("id", id).eq("company_id", company.id);
    await log({ action: "alert_escalated", module: "transaction_monitoring", entityId: id, entityName: `Alert ${id}` });
  }

  async function markFalsePositive(id) {
    const alert = tmAlerts.find(a => a.id === id);
    setTmAlerts(prev => prev.map(a => a.id === id ? { ...a, status: "false_positive", reviewedAt: new Date().toISOString() } : a));
    if (company?.id) await db.from("alerts").update({ status: "false_positive" }).eq("id", id).eq("company_id", company.id);
    await log({ action: "alert_false_positive", module: "transaction_monitoring", entityId: id, entityName: alert?.entity || `Alert ${id}` });
  }

  function exportAlertsCSV() {
    downloadCSV(tmAlerts.map(a => ({ ID: a.id, Date: a.date, Entity: a.entity, Amount: a.amount, Type: a.type, Severity: a.severity, Status: a.status, Rule: a.rule || "", Country: a.country || "", Notes: a.notes || "" })), `tm_alerts_${new Date().toISOString().slice(0,10)}.csv`);
  }
  async function sendAllOpen() {
    const open = tmAlerts.filter(a => a.status === "open");
    if (!open.length) return;
    // Mark all as sent_to_case in local state immediately so the tab updates
    setTmAlerts(prev => prev.map(a => a.status === "open" ? { ...a, status: "sent_to_case" } : a));
    // Single batch insert (avoids 56 sequential async calls)
    if (onSendAllToCase) await onSendAllToCase(open);
    else open.forEach(a => onSendToCase(a));
  }

  function toggleBulkAlert(id) {
    setBulkAlertIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleBulkAlertAll() {
    setBulkAlertIds(bulkAlertIds.size === filtered.length ? new Set() : new Set(filtered.map(a => a.id)));
  }
  function bulkDismissAlerts() {
    if (!bulkAlertIds.size) return;
    setDismissModal({ type: "bulk", ids: [...bulkAlertIds] });
    setDismissReason("");
  }
  async function bulkEscalateAlerts() {
    if (!bulkAlertIds.size) return;
    setBulkAlertWorking(true);
    const ids = [...bulkAlertIds];
    const toEscalate = tmAlerts.filter(a => ids.includes(a.id) && a.status === "open");
    setTmAlerts(prev => prev.map(a => ids.includes(a.id) ? { ...a, status: "sent_to_case" } : a));
    if (onSendAllToCase) await onSendAllToCase(toEscalate);
    else toEscalate.forEach(a => onSendToCase(a));
    setBulkAlertIds(new Set());
    setBulkAlertWorking(false);
  }

  async function createManualAlert() {
    const f = createAlertForm;
    if (!f.entity || !f.type || !f.severity) return;
    setCreateAlertWorking(true);
    const id = `MAN-${Date.now()}`;
    const alert = {
      id,
      txId: "MANUAL",
      ruleId: "MANUAL",
      ruleCategory: f.ruleCategory || "Manual",
      date: new Date().toISOString().slice(0, 10),
      entity: f.entity.trim(),
      amount: f.amount.trim() || "—",
      country: f.country.trim(),
      severity: f.severity,
      risk: f.severity,
      status: "open",
      type: f.type.trim(),
      notes: f.notes.trim(),
      tags: ["manual"],
      source: "Manual",
    };
    let dbAlertId = id;
    if (company?.id) {
      const token = localStorage.getItem('amlguard_token');
      if (token) {
        try {
          const res = await fetch('/api/tm/alerts', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              id,
              company_id: company.id,
              alert_type: alert.type,
              severity: alert.severity,
              status: 'open',
            }),
          });
          if (!res.ok) {
            const err = await res.json();
            console.error('[TM] Failed to save alert to Neon:', err.error);
          } else {
            const data = await res.json();
            dbAlertId = data.alert?.id || id;
            alert.id = dbAlertId;
            console.log('[TM] Alert saved to Neon:', dbAlertId);
          }
        } catch (error) {
          console.error('[TM] Alert save exception:', error.message);
        }
      }
    }
    await log({ action: "alert_created_manual", module: "transaction_monitoring", entityId: dbAlertId, entityName: `${alert.entity} — ${alert.type}` });
    setTmAlerts(prev => [{ ...alert, id: dbAlertId }, ...prev]);
    setCreateAlertModal(false);
    setCreateAlertForm(EMPTY_ALERT_FORM);
    setCreateAlertWorking(false);
  }

  const severityColor = { CRITICAL: "#FF6B6B", HIGH: C.danger, MEDIUM: C.warn, LOW: C.success };

  return (
    <div>
      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Total", value: stats.total, color: C.accent },
          { label: "Open", value: stats.open, color: C.warn },
          { label: "Under Review", value: stats.under_review, color: C.purple || "#A78BFA" },
          { label: "Escalated", value: stats.escalated, color: "#FF6B6B" },
          { label: "In Case", value: stats.sentToCase, color: C.success },
          { label: "Dismissed", value: stats.dismissed, color: C.textDim },
        ].map(s => (
          <div key={s.label} style={{ background: C.surface, border: `1px solid ${C.border}`, borderTop: `2px solid ${s.color}`, borderRadius: 10, padding: "14px 18px" }}>
            <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 6 }}>{s.label.toUpperCase()}</div>
            <div style={{ color: s.color, fontFamily: "'Syne', sans-serif", fontSize: 26, fontWeight: 700 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
        {["All","open","under_review","escalated","false_positive","sent_to_case","dismissed"].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ padding: "5px 12px", borderRadius: 6, fontSize: 11, cursor: "pointer", background: filter === f ? C.accentGlow : "transparent", border: `1px solid ${filter === f ? C.accent : C.border}`, color: filter === f ? C.accent : C.textMid, fontFamily: "'DM Mono', monospace", textTransform: "capitalize" }}>{f.replace(/_/g," ")}</button>
        ))}
        {["All","CRITICAL","HIGH","MEDIUM","LOW"].map(s => (
          <button key={s} onClick={() => setFilterSev(s)} style={{ padding: "5px 12px", borderRadius: 6, fontSize: 11, cursor: "pointer", background: filterSev === s ? "rgba(255,68,68,0.1)" : "transparent", border: `1px solid ${filterSev === s ? C.danger : C.border}`, color: filterSev === s ? severityColor[s] || C.accent : C.textMid, fontFamily: "'DM Mono', monospace" }}>{s}</button>
        ))}

        {/* Rule/Type Filter */}
        {uniqueRules.length > 1 && (
          <select
            value={filterRule}
            onChange={(e) => setFilterRule(e.target.value)}
            style={{
              padding: "5px 10px",
              borderRadius: 6,
              fontSize: 11,
              cursor: "pointer",
              background: filterRule !== "All" ? `${C.accent}22` : C.bg,
              border: `1px solid ${filterRule !== "All" ? C.accent : C.border}`,
              color: filterRule !== "All" ? C.accent : C.textMid,
              fontFamily: "'DM Mono', monospace"
            }}
          >
            {uniqueRules.slice(0, 20).map(rule => (
              <option key={rule} value={rule}>{rule}</option>
            ))}
            {uniqueRules.length > 20 && <option disabled>... and {uniqueRules.length - 20} more</option>}
          </select>
        )}

        {/* Search by Reason/Notes */}
        <input
          type="text"
          placeholder="🔍 Search reason/notes"
          value={searchReason}
          onChange={(e) => setSearchReason(e.target.value)}
          style={{
            padding: "5px 10px",
            borderRadius: 6,
            fontSize: 11,
            background: C.bg,
            border: `1px solid ${searchReason ? C.accent : C.border}`,
            color: C.text,
            fontFamily: "'DM Mono', monospace",
            width: "150px"
          }}
        />

        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Btn small variant="primary" onClick={() => { setCreateAlertForm(EMPTY_ALERT_FORM); setCreateAlertModal(true); }}>
            + Create Alert
          </Btn>
          {stats.open > 0 && (
            <Btn small onClick={sendAllOpen}>{t('tm.sendAllToCase', { count: stats.open })}</Btn>
          )}
          {tmAlerts.length > 0 && (
            <Btn small variant="ghost" onClick={exportAlertsCSV}>⬇ {t('tm.exportCSV')}</Btn>
          )}
          {tmAlerts.length > 0 && (
            <Btn small variant="danger" onClick={() => { setClearAlertsConfirm(true); setClearAlertsInput(""); }}>
              🗑 {t('tm.clearAllAlerts')}
            </Btn>
          )}
        </div>
      </div>

      {/* Bulk select header + action bar */}
      {filtered.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", color: C.textDim, fontSize: 12, fontFamily: "'DM Mono',monospace" }}>
            <input type="checkbox"
              checked={filtered.length > 0 && bulkAlertIds.size === filtered.length}
              onChange={toggleBulkAlertAll}
              style={{ cursor: "pointer", accentColor: C.accent }} />
            Select all
          </label>
          {bulkAlertIds.size > 0 && (
            <>
              <span style={{ color: C.accent, fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: 700 }}>
                {bulkAlertIds.size} selected
              </span>
              <Btn small variant="danger" onClick={bulkDismissAlerts} disabled={bulkAlertWorking}>
                Dismiss Selected
              </Btn>
              <Btn small variant="primary" onClick={bulkEscalateAlerts} disabled={bulkAlertWorking}>
                → Escalate to Case
              </Btn>
              <Btn small variant="ghost" onClick={() => setBulkAlertIds(new Set())}>
                Clear
              </Btn>
            </>
          )}
        </div>
      )}

      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: C.textDim }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>◈</div>
          <div>{t('tm.noAlertsYet')}</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {paginatedAlerts.map(alert => {
            const isBulked = bulkAlertIds.has(alert.id);
            const isExpanded = expandedAlert === alert.id;
            return (
            <div key={alert.id} style={{
              background: C.surface, borderRadius: 10, padding: "16px 20px",
              border: `1px solid ${isBulked ? C.accent : alert.status === "dismissed" ? "rgba(74,88,112,0.4)" : C.border}`,
              borderLeft: `3px solid ${severityColor[alert.severity] || C.accent}`,
              opacity: alert.status === "dismissed" ? 0.55 : 1,
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                <input type="checkbox" checked={isBulked} onChange={() => toggleBulkAlert(alert.id)}
                  style={{ cursor: "pointer", accentColor: C.accent, marginTop: 4, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
                    <SeverityBadge sev={alert.severity} />
                    <CategoryBadge cat={alert.ruleCategory} />
                    <span style={{ color: C.accent, fontSize: 12, fontFamily: "'DM Mono', monospace" }}>{alert.id}</span>
                    <span style={{ color: C.textDim, fontSize: 11, marginLeft: 4 }}>→ Tx: {alert.txId}</span>
                    {/* ITEM 3: Disposition badge */}
                    <DispositionBadge disposition={alert.disposition || alert.status || "open"} />
                    {/* ITEM 3: Assigned analyst */}
                    {alert.assignedTo && (
                      <span style={{ fontSize: 10, color: C.textMid, background: C.surfaceAlt, padding: "2px 8px", borderRadius: 4, fontFamily: "'DM Mono',monospace" }}>
                        👤 {alert.assignedTo}
                      </span>
                    )}
                    {alert.status === "dismissed" && (
                      <span style={{ color: C.textDim, fontSize: 10 }}>
                        Dismissed{alert.dismissReason ? ` · ${DISMISS_REASONS.find(r => r.value === alert.dismissReason)?.label || alert.dismissReason}` : ""}
                      </span>
                    )}
                  </div>
                  {/* Alert title row — click to expand */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
                    onClick={() => {
                      if (isExpanded) { setExpandedAlert(null); }
                      else { setExpandedAlert(alert.id); setNoteDraft(alert.investigationNotes || ""); }
                    }}>
                    <div style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>{alert.type}</div>
                    <span style={{ color: C.textDim, fontSize: 12 }}>{isExpanded ? "▲" : "▼"}</span>
                  </div>
                  <div style={{ color: C.textMid, fontSize: 13, marginBottom: 6 }}>{alert.entity}{alert.counterparty ? ` → ${alert.counterparty}` : ""} · {alert.amount} · {alert.country}</div>
                  <div style={{ color: C.textDim, fontSize: 12, marginBottom: 6 }}>{alert.notes}</div>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
                    {(alert.tags || []).map(tag => (
                      <span key={tag} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 3, background: C.bg, color: C.textDim, border: `1px solid ${C.border}` }}>{tag}</span>
                    ))}
                    <span style={{ color: C.textDim, fontSize: 11, marginLeft: 4 }}>Rule: {alert.ruleId}</span>
                  </div>

                  {/* ITEM 3: Expanded investigation section */}
                  {isExpanded && (
                    <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
                      {/* Disposition dropdown */}
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 6 }}>DISPOSITION</div>
                        <select
                          value={alert.disposition || alert.status || "open"}
                          onChange={e => changeDisposition(alert.id, e.target.value)}
                          style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "8px 12px", fontFamily: "'DM Mono',monospace", fontSize: 12, outline: "none", cursor: "pointer" }}>
                          {DISPOSITION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                      {/* Notes / Investigation textarea */}
                      <div>
                        <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 6 }}>NOTES / INVESTIGATION</div>
                        <textarea
                          value={noteDraft}
                          onChange={e => setNoteDraft(e.target.value)}
                          onBlur={() => saveInvestigationNotes(alert.id, noteDraft)}
                          rows={4}
                          placeholder="Document investigation steps, findings, and rationale here…"
                          style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "9px 12px", fontFamily: "'DM Mono',monospace", fontSize: 12, outline: "none", resize: "vertical", boxSizing: "border-box" }}
                        />
                        <div style={{ color: C.textDim, fontSize: 10, marginTop: 4 }}>Notes auto-save on blur.</div>
                      </div>
                      {/* Assign button inside expanded section */}
                      <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
                        <Btn small variant="ghost" onClick={() => openAssignModal(alert.id)}>
                          👤 {alert.assignedTo ? `Re-assign (${alert.assignedTo})` : "Assign Analyst"}
                        </Btn>
                        {alert.assignedAt && <span style={{ color: C.textDim, fontSize: 10 }}>Assigned {new Date(alert.assignedAt).toLocaleDateString()}</span>}
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0, flexDirection: "column", alignItems: "flex-end" }}>
                  {/* ITEM 3: Assign button on row */}
                  <Btn small variant="ghost" onClick={() => openAssignModal(alert.id)}>
                    👤 {alert.assignedTo ? alert.assignedTo.split(" ")[0] : "Assign"}
                  </Btn>
                  {(alert.status === "open" || alert.status === "under_review" || alert.status === "escalated" || alert.status === "under_investigation") && (
                    <>
                      <div style={{ display: "flex", gap: 6 }}>
                        <Btn small variant="primary" onClick={() => onSendToCase(alert)}>→ {t('tm.sendToCase')}</Btn>
                        <Btn small variant="subtle" onClick={() => dismiss(alert.id)}>{t('tm.dismiss')}</Btn>
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        {alert.status === "open" && <Btn small variant="ghost" onClick={() => markUnderReview(alert.id)}>🔍 Under Review</Btn>}
                        {(alert.status === "open" || alert.status === "under_review") && <Btn small variant="warn" onClick={() => markEscalated(alert.id)}>⚡ Escalate</Btn>}
                        {(alert.status === "open" || alert.status === "under_review") && <Btn small variant="ghost" onClick={() => markFalsePositive(alert.id)}>✕ False Positive</Btn>}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            );
          })}
          {totalAlertPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "14px 0 4px", flexWrap: "wrap" }}>
              <button onClick={() => setAlertPage(p => Math.max(1, p - 1))} disabled={alertPage === 1}
                style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: alertPage === 1 ? C.textDim : C.text, cursor: alertPage === 1 ? "default" : "pointer", fontFamily: "'DM Mono',monospace", fontSize: 12 }}>←</button>
              {Array.from({ length: totalAlertPages }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => setAlertPage(p)}
                  style={{ padding: "5px 10px", borderRadius: 6, border: `1px solid ${p === alertPage ? C.accent : C.border}`, background: p === alertPage ? C.accentGlow : "transparent", color: p === alertPage ? C.accent : C.textMid, cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: p === alertPage ? 700 : 400 }}>{p}</button>
              ))}
              <button onClick={() => setAlertPage(p => Math.min(totalAlertPages, p + 1))} disabled={alertPage === totalAlertPages}
                style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: alertPage === totalAlertPages ? C.textDim : C.text, cursor: alertPage === totalAlertPages ? "default" : "pointer", fontFamily: "'DM Mono',monospace", fontSize: 12 }}>→</button>
              <span style={{ color: C.textDim, fontSize: 11, marginLeft: 6 }}>{filtered.length} total · page {alertPage} of {totalAlertPages}</span>
            </div>
          )}
        </div>
      )}

      {/* ── Create Alert Modal ───────────────────────────────────────────── */}
      {createAlertModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 28, maxWidth: 520, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
            <div style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Create Alert Manually</div>
            <div style={{ color: C.textDim, fontSize: 12, marginBottom: 20, fontFamily: "'DM Mono',monospace" }}>
              Create an alert without running the rule engine — e.g. for manually identified suspicious activity.
            </div>

            {/* Row 1: Entity + Amount */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>ENTITY / CUSTOMER *</div>
                <input value={createAlertForm.entity} onChange={e => setCreateAlertForm(f => ({ ...f, entity: e.target.value }))}
                  placeholder="e.g. Orion Tech Ltd"
                  style={{ width: "100%", background: C.bg, border: `1px solid ${createAlertForm.entity ? C.border : C.danger}`, borderRadius: 7, color: C.text, padding: "9px 12px", fontFamily: "'DM Mono',monospace", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div>
                <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>AMOUNT</div>
                <input value={createAlertForm.amount} onChange={e => setCreateAlertForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="e.g. $9,500"
                  style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "9px 12px", fontFamily: "'DM Mono',monospace", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
              </div>
            </div>

            {/* Row 2: Type */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>ALERT TYPE / PATTERN *</div>
              <input value={createAlertForm.type} onChange={e => setCreateAlertForm(f => ({ ...f, type: e.target.value }))}
                placeholder="e.g. Structuring / Smurfing, Rapid Movement, Unusual Cash Deposits…"
                style={{ width: "100%", background: C.bg, border: `1px solid ${createAlertForm.type ? C.border : C.danger}`, borderRadius: 7, color: C.text, padding: "9px 12px", fontFamily: "'DM Mono',monospace", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
            </div>

            {/* Row 3: Severity + Country */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>SEVERITY *</div>
                <select value={createAlertForm.severity} onChange={e => setCreateAlertForm(f => ({ ...f, severity: e.target.value }))}
                  style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "9px 12px", fontFamily: "'DM Mono',monospace", fontSize: 12, outline: "none", boxSizing: "border-box" }}>
                  {["CRITICAL","HIGH","MEDIUM","LOW"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>COUNTRY</div>
                <input value={createAlertForm.country} onChange={e => setCreateAlertForm(f => ({ ...f, country: e.target.value }))}
                  placeholder="e.g. Venezuela, Russia…"
                  style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "9px 12px", fontFamily: "'DM Mono',monospace", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
              </div>
            </div>

            {/* Row 4: Notes */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>NOTES / DESCRIPTION</div>
              <textarea value={createAlertForm.notes} onChange={e => setCreateAlertForm(f => ({ ...f, notes: e.target.value }))}
                rows={3} placeholder="Describe the suspicious activity observed…"
                style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "9px 12px", fontFamily: "'DM Mono',monospace", fontSize: 12, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <Btn variant="ghost" onClick={() => setCreateAlertModal(false)} style={{ flex: 1 }} disabled={createAlertWorking}>Cancel</Btn>
              <Btn variant="primary" onClick={createManualAlert} disabled={createAlertWorking || !createAlertForm.entity || !createAlertForm.type} style={{ flex: 2 }}>
                {createAlertWorking ? "Saving…" : "Create Alert"}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* ── Dismiss Reason Modal ──────────────────────────────────────────── */}
      {dismissModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 28, maxWidth: 480, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
            <div style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
              Dismiss {dismissModal.type === "bulk" ? `${dismissModal.ids.length} Alert${dismissModal.ids.length !== 1 ? "s" : ""}` : "Alert"}
            </div>
            <div style={{ color: C.textDim, fontSize: 12, marginBottom: 18, fontFamily: "'DM Mono',monospace" }}>
              A reason is required for audit trail purposes. This is recorded and cannot be edited.
            </div>
            <div style={{ marginBottom: 18 }}>
              <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 6 }}>DISMISSAL REASON (REQUIRED)</div>
              <select value={dismissReason} onChange={e => setDismissReason(e.target.value)}
                style={{ width: "100%", background: C.bg, border: `1px solid ${dismissReason ? C.accent : C.danger}`, borderRadius: 7, color: dismissReason ? C.text : C.textDim, padding: "10px 12px", fontFamily: "'DM Mono',monospace", fontSize: 12, outline: "none" }}>
                <option key="_default" value="">— Select a reason —</option>
                {DISMISS_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Btn variant="ghost" onClick={() => setDismissModal(null)} style={{ flex: 1 }}>Cancel</Btn>
              <Btn variant="danger" onClick={executeDismiss} disabled={!dismissReason} style={{ flex: 1 }}>
                Confirm Dismiss
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* ── Clear All Alerts — Regulatory Warning Modal ───────────────────── */}
      {clearAlertsConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: C.surface, border: `1px solid ${C.danger}`, borderRadius: 12, padding: 28, maxWidth: 500, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
            <div style={{ color: C.danger, fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 10 }}>
              ⚠ Record Retention Warning
            </div>
            <div style={{ background: "rgba(255,68,68,0.08)", border: `1px solid rgba(255,68,68,0.3)`, borderRadius: 8, padding: "12px 16px", marginBottom: 16, color: C.textMid, fontSize: 12, fontFamily: "'DM Mono',monospace", lineHeight: 1.6 }}>
              AML regulations require alert records to be retained for <strong style={{ color: C.danger }}>5–7 years</strong> (FinCEN 31 CFR 1020.320, FATF R.11, EU AMLD5 Art.40). Permanently deleting alerts may constitute a compliance violation and could result in regulatory penalties.
            </div>
            <div style={{ color: C.textDim, fontSize: 12, marginBottom: 8, fontFamily: "'DM Mono',monospace" }}>
              If you understand this risk, type <strong style={{ color: C.danger }}>DELETE</strong> to confirm:
            </div>
            <input value={clearAlertsInput} onChange={e => setClearAlertsInput(e.target.value)}
              placeholder="Type DELETE to confirm"
              style={{ width: "100%", background: C.bg, border: `1px solid ${clearAlertsInput === "DELETE" ? C.danger : C.border}`, borderRadius: 7, color: C.text, padding: "10px 12px", fontFamily: "'DM Mono',monospace", fontSize: 13, outline: "none", marginBottom: 16, boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: 10 }}>
              <Btn variant="ghost" onClick={() => setClearAlertsConfirm(false)} style={{ flex: 1 }}>Cancel</Btn>
              <Btn variant="danger" disabled={clearAlertsInput !== "DELETE"} style={{ flex: 1 }}
                onClick={async () => {
                  const count = tmAlerts.length;
                  setClearAlertsConfirm(false);
                  setTmAlerts([]);

                  // Only delete from DB if company exists
                  if (company?.id) {
                    const token = localStorage.getItem('amlguard_token');
                    if (token) {
                      try {
                        await fetch(`/api/tm/alerts?company_id=${company.id}`, {
                          method: 'DELETE',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                          },
                          body: JSON.stringify({}),
                        });
                      } catch (error) {
                        console.warn('[TM] Failed to delete all alerts:', error.message);
                      }
                    }
                    // Log only if company exists
                    await log({ action: "alerts_cleared_all", module: "transaction_monitoring", entityName: "All TM alerts deleted", meta: { count, confirmed_delete: true } });
                  }
                }}>
                Permanently Delete All Alerts
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TAB: SCHEDULE ────────────────────────────────────────────────────────────
function ScheduleTab({ schedule, setSchedule, scheduleLog, schedulerStatus, onRunNow, transactions, realtimeScreening, setRealtimeScreening }) {
  const C = useTheme();
  const { t } = useTranslation();
  const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  function toggleDay(d) {
    setSchedule(prev => {
      const days = prev.days.includes(d) ? prev.days.filter(x => x !== d) : [...prev.days, d].sort();
      return { ...prev, days };
    });
  }

  function computeNextRun(sch) {
    if (!sch.enabled || !sch.days.length) return null;
    const now = new Date();
    const [hh, mm] = sch.time.split(":").map(Number);
    for (let offset = 0; offset < 8; offset++) {
      const candidate = new Date(now);
      candidate.setDate(candidate.getDate() + offset);
      candidate.setHours(hh, mm, 0, 0);
      if (sch.days.includes(candidate.getDay()) && candidate > now) return candidate;
    }
    return null;
  }

  const nextRun = computeNextRun(schedule);
  const lastRunDate = schedule.lastRun ? new Date(schedule.lastRun) : null;

  const inputStyle = {
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7,
    color: C.text, padding: "9px 12px", fontFamily: "'DM Mono', monospace",
    fontSize: 13, outline: "none",
  };

  return (
    <div style={{ maxWidth: 680 }}>

      {/* ── Enable / master toggle ── */}
      <div style={{ background: C.surface, border: `1px solid ${schedule.enabled ? C.gold : C.border}`, borderRadius: 10, padding: 20, marginBottom: 16, transition: "border-color 0.2s" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: schedule.enabled ? 20 : 0 }}>
          <div>
            <div style={{ color: C.text, fontWeight: 700, fontFamily: "'Syne', sans-serif", fontSize: 15 }}>{t('tm.schedule.automatedDailyScreening')}</div>
            <div style={{ color: C.textDim, fontSize: 12, marginTop: 3 }}>{t('tm.schedule.automatedDailyScreeningDesc')}</div>
          </div>
          <div
            onClick={() => setSchedule(prev => ({ ...prev, enabled: !prev.enabled }))}
            style={{ width: 48, height: 26, borderRadius: 13, background: schedule.enabled ? C.gold : C.border, cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
            <div style={{ width: 20, height: 20, borderRadius: "50%", background: C.text, position: "absolute", top: 3, left: schedule.enabled ? 25 : 3, transition: "left 0.2s" }} />
          </div>
        </div>

        {schedule.enabled && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.1em", marginBottom: 7 }}>{t('tm.schedule.runTime')}</div>
              <input
                type="time"
                value={schedule.time}
                onChange={e => setSchedule(prev => ({ ...prev, time: e.target.value, lastRun: null }))}
                style={{ ...inputStyle, width: 160 }}
              />
            </div>

            <div>
              <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.1em", marginBottom: 7 }}>{t('tm.schedule.runOnDays')}</div>
              <div style={{ display: "flex", gap: 6 }}>
                {DAY_LABELS.map((label, i) => {
                  const active = schedule.days.includes(i);
                  return (
                    <button key={i} onClick={() => toggleDay(i)} style={{
                      width: 40, height: 40, borderRadius: 8, cursor: "pointer", fontSize: 11, fontWeight: 700,
                      fontFamily: "'DM Mono', monospace",
                      background: active ? `${C.gold}22` : C.surfaceAlt,
                      border: `1px solid ${active ? C.gold : C.border}`,
                      color: active ? C.gold : C.textDim,
                      transition: "all 0.15s",
                    }}>{label}</button>
                  );
                })}
              </div>
              {schedule.days.length === 0 && (
                <div style={{ color: C.danger, fontSize: 11, marginTop: 6 }}>⚠ {t('tm.schedule.selectAtLeastOneDay')}</div>
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px" }}>
                <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 4 }}>{t('tm.schedule.nextScheduledRun')}</div>
                <div style={{ color: nextRun ? C.gold : C.textDim, fontSize: 13, fontWeight: 600 }}>
                  {nextRun ? nextRun.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                </div>
              </div>
              <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px" }}>
                <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 4 }}>{t('tm.schedule.lastRun')}</div>
                <div style={{ color: lastRunDate ? C.success : C.textDim, fontSize: 13, fontWeight: 600 }}>
                  {lastRunDate ? lastRunDate.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : t('tm.schedule.never')}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Realtime screening toggle ── */}
      <div style={{ background: C.surface, border: `1px solid ${realtimeScreening ? C.accent : C.border}`, borderRadius: 10, padding: 18, marginBottom: 16, transition: "border-color 0.2s" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ color: C.text, fontWeight: 700, fontFamily: "'Syne', sans-serif", fontSize: 14, marginBottom: 4 }}>{t('tm.schedule.realtimeLabel')}</div>
            <div style={{ color: C.textDim, fontSize: 12 }}>{t('tm.schedule.realtimeDesc')}</div>
          </div>
          <div onClick={() => setRealtimeScreening(r => !r)}
            style={{ width: 48, height: 26, borderRadius: 13, background: realtimeScreening ? C.accent : C.border, cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0, marginLeft: 16 }}>
            <div style={{ width: 20, height: 20, borderRadius: "50%", background: C.text, position: "absolute", top: 3, left: realtimeScreening ? 25 : 3, transition: "left 0.2s" }} />
          </div>
        </div>
        {realtimeScreening && (
          <div style={{ marginTop: 10, padding: "8px 12px", background: `${C.accent}11`, border: `1px solid ${C.accent}33`, borderRadius: 7, color: C.accent, fontSize: 11 }}>
            ✓ {t('tm.schedule.realtimeActive')}
          </div>
        )}
      </div>

      {/* ── Manual trigger ── */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 16 }}>
        <div style={{ color: C.text, fontWeight: 700, fontFamily: "'Syne', sans-serif", fontSize: 14, marginBottom: 4 }}>{t('tm.schedule.runNow')}</div>
        <div style={{ color: C.textDim, fontSize: 12, marginBottom: 14 }}>
          {t('tm.schedule.runNowDesc', { count: transactions.length })}
        </div>
        <button
          onClick={onRunNow}
          disabled={schedulerStatus === "running" || transactions.length === 0}
          style={{
            padding: "10px 22px", borderRadius: 8, cursor: schedulerStatus === "running" || transactions.length === 0 ? "not-allowed" : "pointer",
            background: schedulerStatus === "running" ? C.border : C.accent,
            border: "none", color: C.bg, fontWeight: 700,
            fontFamily: "'DM Mono', monospace", fontSize: 13,
            display: "flex", alignItems: "center", gap: 8, transition: "background 0.15s",
          }}>
          {schedulerStatus === "running"
            ? <><span style={{ display: "inline-block", width: 14, height: 14, border: `2px solid ${C.bg}40`, borderTopColor: C.bg, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />{t('tm.schedule.running')}</>
            : `▶ ${t('tm.schedule.runNow')}`}
        </button>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>

      {/* ── Run history log ── */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ color: C.text, fontWeight: 700, fontFamily: "'Syne', sans-serif", fontSize: 14 }}>{t('tm.schedule.runHistory')}</div>
          <span style={{ color: C.textDim, fontSize: 11 }}>{scheduleLog.length} {t('auditLog.entries')}</span>
        </div>

        {scheduleLog.length === 0 ? (
          <div style={{ color: C.textDim, fontSize: 13, padding: "20px 0", textAlign: "center" }}>{t('tm.schedule.noRunsYet')}</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {scheduleLog.map((entry, i) => {
              const d = new Date(entry.timestamp);
              return (
                <div key={entry.id} style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 12, alignItems: "center", padding: "10px 0", borderBottom: i < scheduleLog.length - 1 ? `1px solid ${C.border}` : "none" }}>
                  <div>
                    <div style={{ color: C.text, fontSize: 12 }}>
                      {d.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </div>
                    <div style={{ color: C.textDim, fontSize: 10, marginTop: 2 }}>
                      {entry.triggeredBy === "scheduler" ? `⏱ ${t('tm.schedule.triggered')}` : `▶ ${t('tm.schedule.manual')}`} · {t('tm.schedule.txScreened', { count: entry.txCount })}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <span style={{
                      color: entry.alertsGenerated > 0 ? C.danger : C.success,
                      fontSize: 13, fontWeight: 700, fontFamily: "'DM Mono', monospace",
                    }}>
                      {entry.alertsGenerated > 0 ? `⚠ ${entry.alertsGenerated} ${t('tm.alertHeaders.alert').toLowerCase()}${entry.alertsGenerated !== 1 ? "s" : ""}` : `✓ ${t('tm.schedule.clean')}`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── AI AGENT TAB ─────────────────────────────────────────────────────────────
const RISK_COLORS = {
  CRITICAL: { color: "#FF6B6B", bg: "rgba(255,68,68,0.12)",  border: "rgba(255,68,68,0.35)" },
  HIGH:     { color: "#FF4444", bg: "rgba(255,68,68,0.08)",  border: "rgba(255,68,68,0.25)" },
  MEDIUM:   { color: "#FF8C00", bg: "rgba(255,140,0,0.08)",  border: "rgba(255,140,0,0.25)" },
  LOW:      { color: "#00E676", bg: "rgba(0,230,118,0.08)",  border: "rgba(0,230,118,0.25)" },
};

function AIAgentTab({ transactions, company, cases, log, onAlertsGenerated }) {
  const C = useTheme();

  // ── Sub-tabs: "run" | "history" ──────────────────────────────────────────────
  const [subTab, setSubTab]           = useState("run");

  // ── Analysis state ──────────────────────────────────────────────────────────
  const [analyzing, setAnalyzing]     = useState(false);
  const [progress, setProgress]       = useState("");
  const [findings, setFindings]       = useState([]);
  const [summary, setSummary]         = useState("");
  const [methodology, setMethodology] = useState("");
  const [error, setError]             = useState("");
  const [scope, setScope]             = useState("recent");
  const [focus, setFocus]             = useState("all");
  const [sentIds, setSentIds]         = useState(new Set());

  // ── History state ────────────────────────────────────────────────────────────
  const [history, setHistory]         = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [viewingRun, setViewingRun]   = useState(null); // full run object being inspected
  const [histSentIds, setHistSentIds] = useState(new Set());

  // ── Chat state ──────────────────────────────────────────────────────────────
  const [messages, setMessages]       = useState([]);
  const [chatInput, setChatInput]     = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const chatBottomRef = useRef(null);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Load history on mount ────────────────────────────────────────────────────
  useEffect(() => {
    if (!company?.id) return;
    loadHistory();
  }, [company?.id]);

  async function loadHistory() {
    setHistLoading(true);
    try {
      const token = localStorage.getItem('amlguard_token');
      if (!token || !company?.id) {
        setHistory([]);
        setHistLoading(false);
        return;
      }

      const filterStr = encodeURIComponent(JSON.stringify({ company_id: company.id }));
      const res = await fetch(`/api/db/tm_ai_analyses?filter=${filterStr}&order_by=created_at&order=desc&limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setHistory(Array.isArray(data) ? data : []);
      } else {
        setHistory([]);
      }
    } catch (error) {
      console.warn('[TM] Failed to load analysis history:', error);
      setHistory([]);
    } finally {
      setHistLoading(false);
    }
  }

  // ── Build compact transaction summary for AI ────────────────────────────────
  function buildTxContext() {
    let txList = [...transactions];

    if (scope === "recent") {
      const cutoff = new Date(Date.now() - 90 * 86400000);
      txList = txList.filter(t => { try { return new Date(t.date) >= cutoff; } catch { return true; } });
    } else if (scope === "flagged") {
      txList = txList.filter(t => t.flagged || t.pepFlag || (t.status && t.status !== "clear"));
    }

    // Focus filter
    const focusKeywords = {
      structuring: ["cash","wire","structur","smur"],
      layering:    ["wire","crypto","layer","rapid","bridge"],
      highRisk:    ["iran","russia","north korea","myanmar","cuba","venezuela","belarus"],
      crypto:      ["crypto","bitcoin","ethereum","wallet","defi","nft","mixer"],
      pep:         ["pep","political","watchlist"],
    }[focus] || [];

    if (focusKeywords.length) {
      txList = txList.filter(t =>
        focusKeywords.some(kw =>
          (t.description || "").toLowerCase().includes(kw) ||
          (t.type || "").toLowerCase().includes(kw) ||
          (t.paymentMethod || "").toLowerCase().includes(kw) ||
          (t.country || "").toLowerCase().includes(kw) ||
          (t.entity || "").toLowerCase().includes(kw) ||
          (t.fromLabel || "").toLowerCase().includes(kw) ||
          (t.toLabel || "").toLowerCase().includes(kw)
        )
      );
    }

    // Cap at 150 transactions to stay within token budget; take highest-amount ones
    if (txList.length > 150) {
      txList = [...txList].sort((a, b) => (b.amount || 0) - (a.amount || 0)).slice(0, 150);
    }

    // Build entity behavioral profile
    const byEntity = {};
    txList.forEach(tx => {
      const key = (tx.entity || "Unknown").trim();
      if (!byEntity[key]) byEntity[key] = [];
      byEntity[key].push(tx);
    });

    const entityProfiles = Object.entries(byEntity).map(([entity, txs]) => {
      const total = txs.reduce((s, t) => s + (t.amount || 0), 0);
      const countries = [...new Set(txs.map(t => t.country).filter(Boolean))];
      const types = [...new Set(txs.map(t => t.type).filter(Boolean))];
      const dates = txs.map(t => t.date).filter(Boolean).sort();
      return `• ${entity}: ${txs.length} tx, total ~$${Math.round(total).toLocaleString()}, countries: ${countries.join("/")||"N/A"}, types: ${types.join("/")||"N/A"}, dates: ${dates[0]||"?"}–${dates[dates.length-1]||"?"}${txs.some(t=>t.pepFlag) ? " [PEP]" : ""}`;
    }).join("\n");

    const txRows = txList.map(tx =>
      `${tx.id}|${tx.date||""}|${tx.entity||""}|${tx.currency||"USD"} ${tx.amount||0}|${tx.type||""}|${tx.direction||""}|${tx.country||""}|${tx.paymentMethod||""}|${tx.pepFlag?"PEP":""}|${tx.description||""}|${tx.fromLabel||""}→${tx.toLabel||""}`
    ).join("\n");

    return { entityProfiles, txRows, count: txList.length, txList };
  }

  // ── Run AI analysis ─────────────────────────────────────────────────────────
  async function runAnalysis() {
    if (!transactions.length) return;
    setAnalyzing(true);
    setFindings([]);
    setSummary("");
    setError("");
    setProgress("Building behavioral profiles…");

    try {
      const { entityProfiles, txRows, count, txList } = buildTxContext();

      setProgress(`Analyzing ${count} transactions with AI…`);

      const openCaseSummary = (cases || []).slice(0, 20).map(c =>
        `${c.id}: ${c.entity||""} — ${c.type||""} — ${c.risk||""} risk — ${c.status||""}`
      ).join("\n") || "No open cases.";

      const system = `You are a senior BSA/AML compliance specialist and financial crime investigator with 15+ years of experience. You think like a human analyst — spotting behavioral patterns, cross-entity connections, and contextual red flags that rule-based systems miss.

Analyze the transaction data for suspicious AML/BSA activity including: structuring/smurfing, layering, rapid fund movement, high-risk jurisdiction exposure, PEP/watchlist concerns, crypto mixer exposure, dormant account activity, new account velocity, trade-based ML, and any other red flags a seasoned examiner would note.

Output your findings using EXACTLY this format — repeat the block for each finding:

##FINDING##
RISK: HIGH
CONFIDENCE: HIGH
PATTERN: Structuring / Smurfing
ENTITIES: Orion Tech Ltd
TRANSACTIONS: TX-001, TX-002, TX-003
DESCRIPTION: Three wire transfers totaling $29,100 on the same day, each just below the $10,000 CTR reporting threshold. Classic structuring pattern indicating intentional avoidance of BSA reporting requirements.
RECOMMENDATION: File SAR within 30 days. Freeze pending wires pending EDD review.
REFS: 31 USC 5324, FinCEN FIN-2014-A005, FATF R.29
##END##

After all findings, add:
##SUMMARY##
Your overall executive summary here.
##METHODOLOGY##
Brief note on analysis approach.

Use CRITICAL/HIGH/MEDIUM/LOW for RISK. Use HIGH/MEDIUM/LOW for CONFIDENCE. Be specific — cite transaction IDs, amounts, dates. Identify up to 8 most significant findings.`;

      const userMsg = `TRANSACTION DATA (${count} transactions):

ENTITY BEHAVIORAL PROFILES:
${entityProfiles}

OPEN CASES FOR CONTEXT:
${openCaseSummary}

TRANSACTIONS (ID|Date|Entity|Amount|Type|Direction|Country|Method|PEP|Description|Counterparty):
${txRows}

Analyze for AML/BSA red flags. Prioritize by risk level.`;

      const raw = await callAI({
        system,
        messages: [{ role: "user", content: userMsg }],
        maxTokens: 3500,
      });

      setProgress("Parsing AI findings…");

      // ── Parse delimiter-based response (works with any AI model) ─────────────
      function parseDelimited(text) {
        const findings = [];
        const blocks = text.split(/##FINDING##/i).slice(1);
        blocks.forEach((block, i) => {
          const body = block.split(/##END##/i)[0] || block;
          const get = (key) => {
            const m = body.match(new RegExp(`^${key}:\\s*(.+)$`, "im"));
            return m ? m[1].trim() : "";
          };
          const risk = get("RISK").toUpperCase();
          const validRisk = ["CRITICAL","HIGH","MEDIUM","LOW"].includes(risk) ? risk : "MEDIUM";
          const txRaw = get("TRANSACTIONS");
          const entRaw = get("ENTITIES");
          findings.push({
            id: `AI-${String(i + 1).padStart(3, "0")}`,
            risk: validRisk,
            confidence: get("CONFIDENCE").toUpperCase() || "MEDIUM",
            pattern: get("PATTERN") || "Suspicious Activity",
            entities: entRaw ? entRaw.split(",").map(s => s.trim()).filter(Boolean) : [],
            transactions: txRaw ? txRaw.split(",").map(s => s.trim()).filter(Boolean) : [],
            description: get("DESCRIPTION"),
            recommendation: get("RECOMMENDATION"),
            regulatory_refs: get("REFS") ? get("REFS").split(",").map(s => s.trim()).filter(Boolean) : [],
            source: "AI Agent",
          });
        });

        const summaryMatch = text.match(/##SUMMARY##([\s\S]*?)(?:##|$)/i);
        const methodMatch  = text.match(/##METHODOLOGY##([\s\S]*?)(?:##|$)/i);
        return {
          findings: findings.filter(f => f.pattern && f.description),
          summary: summaryMatch ? summaryMatch[1].trim() : "",
          methodology: methodMatch ? methodMatch[1].trim() : "",
        };
      }

      const parsed = parseDelimited(raw);

      // If the model ignored the format entirely, surface the raw text as one finding
      if (!parsed.findings.length && raw.trim().length > 50) {
        parsed.findings = [{
          id: "AI-001",
          risk: "MEDIUM",
          confidence: "LOW",
          pattern: "AI Analysis — Manual Review Required",
          entities: [],
          transactions: [],
          description: raw.trim(),
          recommendation: "Review the AI narrative above and apply professional judgment.",
          regulatory_refs: [],
          source: "AI Agent",
        }];
        parsed.summary = "The AI provided a narrative analysis. Review findings above.";
      }

      const aiFindings = parsed.findings;
      setFindings(aiFindings);
      setSummary(parsed.summary || "");
      setMethodology(parsed.methodology || "");

      // ── Save run to Supabase ──────────────────────────────────────────────────
      if (company?.id) {
        const runRecord = {
          company_id:      company.id,
          run_at:          new Date().toISOString(),
          scope,
          focus,
          tx_count:        count,
          findings_count:  aiFindings.length,
          findings:        aiFindings,
          summary:         parsed.summary || "",
          methodology:     parsed.methodology || "",
        };
        try {
          const token = localStorage.getItem('amlguard_token');
          if (token) {
            const saveRes = await fetch('/api/db/tm_ai_analyses', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify(runRecord),
            });
            if (saveRes.ok) {
              const saved = await saveRes.json();
              if (saved) setHistory(prev => [saved, ...prev]);
            }
          }
        } catch (error) {
          console.warn('[TM] Failed to save analysis:', error);
        }
      }

      // Seed the chat with context
      setMessages([{
        role: "assistant",
        content: `Analysis complete. I identified **${aiFindings.length} finding${aiFindings.length !== 1 ? "s" : ""}** across your transaction data.\n\n${parsed.summary || ""}\n\nAsk me anything about specific findings, entities, or patterns — I can go deeper on any of them.`,
      }]);

      await log?.({
        action: "tm_ai_agent_analysis_run",
        module: "transaction_monitoring",
        entityId: "ai_agent",
        entityName: "AI Agent Analysis",
        newValue: { findings: aiFindings.length, scope, focus, txCount: count },
      });

    } catch (e) {
      setError(e.message || "Analysis failed. Please try again.");
    } finally {
      setAnalyzing(false);
      setProgress("");
    }
  }

  // ── Send finding to alerts queue ────────────────────────────────────────────
  function sendToAlerts(finding) {
    const alerts = (finding.transactions?.length ? finding.transactions : ["AI"]).map(txId => ({
      id: `TM-AI-${Date.now()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`,
      txId,
      entity: finding.entities?.[0] || "Multiple",
      amount: "",
      type: finding.pattern,
      ruleId: finding.id,
      ruleCategory: "AI Agent",
      risk: finding.risk,
      severity: finding.risk,
      status: "open",
      date: new Date().toISOString().slice(0, 10),
      country: "",
      notes: finding.description,
      reference: (finding.regulatory_refs || []).join(", "),
      tags: ["AI Agent"],
      source: "AI Agent",
      recommendation: finding.recommendation,
      confidence: finding.confidence,
    }));
    onAlertsGenerated?.(alerts);
    setSentIds(prev => new Set([...prev, finding.id]));
  }

  function sendAllToAlerts() {
    findings.filter(f => !sentIds.has(f.id)).forEach(sendToAlerts);
  }

  // ── Chat follow-up ──────────────────────────────────────────────────────────
  async function sendChat() {
    if (!chatInput.trim() || chatLoading) return;
    const userMsg = { role: "user", content: chatInput.trim() };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setChatInput("");
    setChatLoading(true);

    try {
      const findingsSummary = findings.map(f =>
        `[${f.id}] ${f.pattern} — ${f.entities?.join(", ")} — ${f.risk} risk — ${f.description?.slice(0, 120)}…`
      ).join("\n");

      const system = `You are a senior BSA/AML compliance specialist. You just completed an AI-driven analysis of transaction data for ${company?.name || "a financial institution"} and identified the following findings:

${findingsSummary || "No findings yet — analysis not yet run."}

Answer follow-up questions from the compliance officer. Be specific, cite transaction IDs and entity names from the findings where relevant. You may also suggest additional investigation steps, regulatory guidance, or SAR/CTR filing recommendations.`;

      const reply = await callAI({
        system,
        messages: updated.map(m => ({ role: m.role, content: m.content })),
        maxTokens: 1500,
      });

      setMessages(prev => [...prev, { role: "assistant", content: reply }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", content: `Error: ${e.message}` }]);
    } finally {
      setChatLoading(false);
    }
  }

  const riskOrder = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
  const sorted = [...findings].sort((a, b) => (riskOrder[a.risk] ?? 4) - (riskOrder[b.risk] ?? 4));

  // ── Helper: render a findings list (shared between run + history view) ────
  function FindingsList({ items, sentSet, onSend }) {
    const sortedItems = [...items].sort((a, b) => (riskOrder[a.risk] ?? 4) - (riskOrder[b.risk] ?? 4));
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {sortedItems.map(f => {
          const rc = RISK_COLORS[f.risk] || RISK_COLORS.LOW;
          const sent = sentSet.has(f.id);
          return (
            <div key={f.id} style={{ background: C.surface, border: `1px solid ${rc.border}`, borderRadius: 10, padding: "16px 20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, background: rc.bg, color: rc.color, border: `1px solid ${rc.border}`, fontWeight: 700, letterSpacing: "0.06em" }}>{f.risk}</span>
                {f.confidence && <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, background: C.accentGlow, color: C.accent, border: `1px solid ${C.borderLight}` }}>{f.confidence} confidence</span>}
                <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, background: "rgba(167,139,250,0.1)", color: "#A78BFA", border: "1px solid rgba(167,139,250,0.2)" }}>✦ AI Agent</span>
                <span style={{ marginLeft: "auto", color: C.textDim, fontSize: 10 }}>{f.id}</span>
              </div>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{f.pattern}</div>
              {f.entities?.length > 0 && (
                <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                  {f.entities.map(e => <span key={e} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: C.accentGlow, color: C.accent, border: `1px solid ${C.borderLight}` }}>{e}</span>)}
                </div>
              )}
              <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.7, marginBottom: 12 }}>{f.description}</div>
              {f.transactions?.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ color: C.textDim, fontSize: 10, marginBottom: 5, letterSpacing: "0.07em" }}>AFFECTED TRANSACTIONS</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {f.transactions.map(txId => <span key={txId} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 3, background: C.bg, color: C.textMid, border: `1px solid ${C.border}` }}>{txId}</span>)}
                  </div>
                </div>
              )}
              {f.recommendation && (
                <div style={{ background: "rgba(0,212,255,0.05)", border: "1px solid rgba(0,212,255,0.15)", borderRadius: 6, padding: "8px 12px", marginBottom: 12 }}>
                  <span style={{ color: C.accent, fontSize: 10, fontWeight: 700, letterSpacing: "0.07em" }}>RECOMMENDATION </span>
                  <span style={{ color: C.textMid, fontSize: 12 }}>{f.recommendation}</span>
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ color: C.textDim, fontSize: 11 }}>{(f.regulatory_refs || []).join(" · ")}</div>
                {onSend && (
                  <button onClick={() => onSend(f)} disabled={sent} style={{ padding: "6px 14px", borderRadius: 6, fontSize: 11, fontWeight: 600, fontFamily: "'DM Mono',monospace", cursor: sent ? "default" : "pointer", background: sent ? "rgba(0,230,118,0.1)" : C.accentGlow, border: `1px solid ${sent ? "rgba(0,230,118,0.3)" : C.borderLight}`, color: sent ? C.success : C.accent }}>
                    {sent ? "✓ Sent to Alerts" : "↗ Send to Alerts"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div>
      {/* ── Sub-tab bar ──────────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 2, marginBottom: 20, borderBottom: `1px solid ${C.border}`, paddingBottom: 0 }}>
        {[
          { id: "run",     label: "✦ Run Analysis",  sub: findings.length ? `${findings.length} findings` : "ready" },
          { id: "history", label: "◷ History",        sub: `${history.length} saved run${history.length !== 1 ? "s" : ""}` },
        ].map(st => (
          <button key={st.id} onClick={() => { setSubTab(st.id); setViewingRun(null); }} style={{
            padding: "8px 18px", background: "transparent", border: "none", cursor: "pointer",
            borderBottom: `2px solid ${subTab === st.id ? "#A78BFA" : "transparent"}`,
            color: subTab === st.id ? "#A78BFA" : C.textMid,
            fontFamily: "'DM Mono',monospace", fontSize: 13, fontWeight: subTab === st.id ? 700 : 400,
            display: "flex", alignItems: "center", gap: 8, marginBottom: -1, transition: "all 0.12s",
          }}>
            {st.label}
            <span style={{ color: subTab === st.id ? "rgba(167,139,250,0.6)" : C.textDim, fontSize: 11 }}>({st.sub})</span>
          </button>
        ))}
      </div>

      {/* ── History sub-tab ───────────────────────────────────────────────── */}
      {subTab === "history" && !viewingRun && (
        <div>
          {histLoading ? (
            <div style={{ color: C.textDim, fontSize: 13, padding: "40px 0", textAlign: "center" }}>Loading history…</div>
          ) : history.length === 0 ? (
            <div style={{ textAlign: "center", padding: "50px 0", border: `2px dashed ${C.border}`, borderRadius: 12 }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>◷</div>
              <div style={{ color: C.textMid, fontSize: 14, marginBottom: 6 }}>No analysis history yet</div>
              <div style={{ color: C.textDim, fontSize: 12 }}>Each time you run the AI agent, results are saved here automatically.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {history.map(run => {
                const critCount = (run.findings || []).filter(f => f.risk === "CRITICAL").length;
                const highCount = (run.findings || []).filter(f => f.risk === "HIGH").length;
                return (
                  <div key={run.id} onClick={() => { setViewingRun(run); setHistSentIds(new Set()); }}
                    style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "16px 20px", cursor: "pointer", transition: "border-color 0.12s" }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = "#A78BFA"}
                    onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 5 }}>
                          <span style={{ color: C.text, fontWeight: 700, fontSize: 13 }}>
                            {new Date(run.run_at).toLocaleString()}
                          </span>
                          {critCount > 0 && <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: RISK_COLORS.CRITICAL.bg, color: RISK_COLORS.CRITICAL.color, border: `1px solid ${RISK_COLORS.CRITICAL.border}`, fontWeight: 700 }}>{critCount} CRITICAL</span>}
                          {highCount > 0 && <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: RISK_COLORS.HIGH.bg, color: RISK_COLORS.HIGH.color, border: `1px solid ${RISK_COLORS.HIGH.border}`, fontWeight: 700 }}>{highCount} HIGH</span>}
                        </div>
                        <div style={{ color: C.textDim, fontSize: 11, display: "flex", gap: 14, flexWrap: "wrap" }}>
                          <span>{run.findings_count} finding{run.findings_count !== 1 ? "s" : ""}</span>
                          <span>· {run.tx_count} transactions analyzed</span>
                          <span>· Scope: {run.scope}</span>
                          {run.focus && run.focus !== "all" && <span>· Focus: {run.focus}</span>}
                        </div>
                        {run.summary && <div style={{ color: C.textMid, fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>{run.summary.slice(0, 160)}{run.summary.length > 160 ? "…" : ""}</div>}
                      </div>
                      <span style={{ color: C.textDim, fontSize: 18, flexShrink: 0 }}>›</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── History: viewing a specific run ──────────────────────────────── */}
      {subTab === "history" && viewingRun && (
        <div>
          {/* Back + header */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
            <button onClick={() => setViewingRun(null)} style={{ background: "transparent", border: "none", color: C.textDim, cursor: "pointer", fontSize: 18, padding: 4 }}>←</button>
            <div style={{ flex: 1 }}>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>{new Date(viewingRun.run_at).toLocaleString()}</div>
              <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>
                {viewingRun.findings_count} findings · {viewingRun.tx_count} transactions · scope: {viewingRun.scope}{viewingRun.focus && viewingRun.focus !== "all" ? ` · focus: ${viewingRun.focus}` : ""}
              </div>
            </div>
            {(viewingRun.findings || []).length > 0 && (
              <button onClick={() => (viewingRun.findings || []).filter(f => !histSentIds.has(f.id)).forEach(f => {
                onAlertsGenerated?.((f.transactions?.length ? f.transactions : ["AI"]).map(txId => ({
                  id: `TM-AI-${Date.now()}-${Math.random().toString(36).slice(2,6).toUpperCase()}`,
                  txId, entity: f.entities?.[0] || "Multiple", amount: "", type: f.pattern,
                  ruleId: f.id, ruleCategory: "AI Agent", risk: f.risk, severity: f.risk,
                  status: "open", date: viewingRun.run_at?.slice(0,10), country: "",
                  notes: f.description, reference: (f.regulatory_refs||[]).join(", "),
                  tags: ["AI Agent"], source: "AI Agent (History)",
                })));
                setHistSentIds(prev => new Set([...prev, f.id]));
              })} style={{ padding: "7px 14px", background: "rgba(0,230,118,0.1)", border: "1px solid rgba(0,230,118,0.3)", borderRadius: 6, color: C.success, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Mono',monospace" }}>
                ↗ Send All to Alerts
              </button>
            )}
          </div>
          {viewingRun.summary && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 18px", marginBottom: 20 }}>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Summary</div>
              <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.7 }}>{viewingRun.summary}</div>
              {viewingRun.methodology && <div style={{ color: C.textDim, fontSize: 11, marginTop: 6, fontStyle: "italic" }}>Methodology: {viewingRun.methodology}</div>}
            </div>
          )}
          <FindingsList
            items={viewingRun.findings || []}
            sentSet={histSentIds}
            onSend={f => {
              onAlertsGenerated?.((f.transactions?.length ? f.transactions : ["AI"]).map(txId => ({
                id: `TM-AI-${Date.now()}-${Math.random().toString(36).slice(2,6).toUpperCase()}`,
                txId, entity: f.entities?.[0] || "Multiple", amount: "", type: f.pattern,
                ruleId: f.id, ruleCategory: "AI Agent", risk: f.risk, severity: f.risk,
                status: "open", date: viewingRun.run_at?.slice(0,10), country: "",
                notes: f.description, reference: (f.regulatory_refs||[]).join(", "),
                tags: ["AI Agent"], source: "AI Agent (History)",
              })));
              setHistSentIds(prev => new Set([...prev, f.id]));
            }}
          />
        </div>
      )}

      {/* ── Run Analysis sub-tab ──────────────────────────────────────────── */}
      {subTab === "run" && (
    <div style={{ display: "flex", gap: 20, minHeight: 500 }}>

      {/* ── Left: controls + findings ─────────────────────────────────────── */}
      <div style={{ flex: 1, minWidth: 0 }}>

        {/* Mode description banner */}
        <div style={{
          background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.25)",
          borderRadius: 10, padding: "14px 18px", marginBottom: 20,
          display: "flex", alignItems: "flex-start", gap: 14,
        }}>
          <span style={{ fontSize: 22, flexShrink: 0 }}>✦</span>
          <div>
            <div style={{ color: "#A78BFA", fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
              AI Agent Mode — Holistic AML/BSA Analysis
            </div>
            <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.6 }}>
              Instead of matching fixed rule thresholds, the AI agent analyzes your transactions the way a senior AML examiner would — looking for behavioral patterns, cross-entity connections, and contextual red flags that rule-based systems miss. Results complement your rule-based alerts and should be reviewed by a qualified compliance officer.
            </div>
          </div>
        </div>

        {/* Analysis controls */}
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
          padding: "16px 20px", marginBottom: 20,
          display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap",
        }}>
          <div>
            <div style={{ color: C.textDim, fontSize: 10, marginBottom: 6, letterSpacing: "0.08em" }}>TRANSACTION SCOPE</div>
            <select
              value={scope} onChange={e => setScope(e.target.value)}
              style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, padding: "7px 10px", fontFamily: "'DM Mono',monospace", cursor: "pointer", outline: "none" }}
            >
              <option key="recent" value="recent">Last 90 days</option>
              <option key="all" value="all">All transactions</option>
              <option key="flagged" value="flagged">Flagged / PEP only</option>
            </select>
          </div>
          <div>
            <div style={{ color: C.textDim, fontSize: 10, marginBottom: 6, letterSpacing: "0.08em" }}>FOCUS AREA</div>
            <select
              value={focus} onChange={e => setFocus(e.target.value)}
              style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, padding: "7px 10px", fontFamily: "'DM Mono',monospace", cursor: "pointer", outline: "none" }}
            >
              <option key="all" value="all">All suspicious patterns</option>
              <option key="structuring" value="structuring">Structuring / Smurfing</option>
              <option key="layering" value="layering">Layering / Rapid movement</option>
              <option key="highRisk" value="highRisk">High-risk jurisdictions</option>
              <option key="crypto" value="crypto">Crypto / Virtual assets</option>
              <option key="pep" value="pep">PEP / Watchlist exposure</option>
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ color: C.textDim, fontSize: 10, marginBottom: 6, letterSpacing: "0.08em" }}>
              {transactions.length} TRANSACTIONS LOADED
            </div>
            <button
              onClick={runAnalysis}
              disabled={analyzing || !transactions.length}
              style={{
                width: "100%", padding: "10px 0",
                background: analyzing ? C.surfaceAlt : "linear-gradient(135deg, rgba(167,139,250,0.2), rgba(0,212,255,0.15))",
                border: `1px solid ${analyzing ? C.border : "rgba(167,139,250,0.5)"}`,
                borderRadius: 8, color: analyzing ? C.textDim : "#A78BFA",
                fontFamily: "'DM Mono',monospace", fontWeight: 700, fontSize: 13,
                cursor: analyzing || !transactions.length ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                transition: "all 0.15s",
              }}
            >
              {analyzing ? (
                <>{progress || "Analyzing…"}</>
              ) : (
                <>✦ Run AI Agent Analysis</>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div style={{ background: "rgba(255,68,68,0.08)", border: "1px solid rgba(255,68,68,0.3)", borderRadius: 8, padding: "12px 16px", color: C.danger, fontSize: 12, marginBottom: 16 }}>
            {error}
          </div>
        )}

        {/* Summary bar */}
        {summary && (
          <div style={{
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
            padding: "14px 18px", marginBottom: 20,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 13 }}>AI Analysis Summary</div>
              {findings.length > 0 && (
                <button
                  onClick={sendAllToAlerts}
                  style={{
                    background: "rgba(0,230,118,0.1)", border: "1px solid rgba(0,230,118,0.3)",
                    borderRadius: 6, color: C.success, fontSize: 11, padding: "5px 12px",
                    cursor: "pointer", fontFamily: "'DM Mono',monospace", fontWeight: 600,
                  }}
                >
                  ↗ Send All to Alerts
                </button>
              )}
            </div>
            <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.7 }}>{summary}</div>
            {methodology && (
              <div style={{ color: C.textDim, fontSize: 11, marginTop: 6, fontStyle: "italic" }}>
                Methodology: {methodology}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
              {["CRITICAL","HIGH","MEDIUM","LOW"].map(r => {
                const count = findings.filter(f => f.risk === r).length;
                if (!count) return null;
                const rc = RISK_COLORS[r];
                return (
                  <span key={r} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 4, background: rc.bg, color: rc.color, border: `1px solid ${rc.border}`, fontWeight: 700 }}>
                    {count} {r}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Findings list */}
        {sorted.length === 0 && !analyzing && !error && (
          <div style={{
            textAlign: "center", padding: "50px 0",
            border: `2px dashed ${C.border}`, borderRadius: 12,
          }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>✦</div>
            <div style={{ color: C.textMid, fontSize: 14, marginBottom: 6 }}>No analysis run yet</div>
            <div style={{ color: C.textDim, fontSize: 12 }}>
              Load transactions and click "Run AI Agent Analysis" to begin
            </div>
          </div>
        )}

        <FindingsList items={sorted} sentSet={sentIds} onSend={sendToAlerts} />
      </div>

      {/* ── Right: AI chat panel ──────────────────────────────────────────── */}
      <div style={{
        width: 340, flexShrink: 0, background: C.surface,
        border: `1px solid ${C.border}`, borderRadius: 12,
        display: "flex", flexDirection: "column", overflow: "hidden",
        alignSelf: "flex-start", position: "sticky", top: 0,
        maxHeight: "calc(100vh - 120px)",
      }}>
        {/* Panel header */}
        <div style={{ padding: "14px 16px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ color: "#A78BFA", fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 14 }}>
            ✦ AML Specialist Chat
          </div>
          <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>
            Ask follow-up questions about findings
          </div>
        </div>

        {/* Quick prompts */}
        {messages.length === 0 && (
          <div style={{ padding: "12px 14px", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ color: C.textDim, fontSize: 10, marginBottom: 8, letterSpacing: "0.08em" }}>AFTER ANALYSIS, ASK:</div>
            {[
              "Which finding should I prioritize for SAR filing?",
              "Are any of these entities connected to each other?",
              "What additional documentation should I gather?",
              "Would these findings hold up in a regulatory exam?",
              "Summarize the highest-risk entity's full activity",
            ].map(q => (
              <button key={q} onClick={() => setChatInput(q)} style={{
                display: "block", width: "100%", textAlign: "left",
                background: C.bg, border: `1px solid ${C.border}`,
                borderRadius: 5, color: C.textMid, fontSize: 11,
                padding: "6px 10px", marginBottom: 4, cursor: "pointer",
                fontFamily: "'DM Mono',monospace", transition: "border-color 0.1s",
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "#A78BFA"}
              onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
              >{q}</button>
            ))}
          </div>
        )}

        {/* Messages */}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px" }}>
          {messages.map((m, i) => (
            <div key={i} style={{ marginBottom: 14 }}>
              <div style={{ color: m.role === "user" ? C.accent : "#A78BFA", fontSize: 10, marginBottom: 4, letterSpacing: "0.08em" }}>
                {m.role === "user" ? "YOU" : "✦ AML SPECIALIST"}
              </div>
              <div style={{
                background: m.role === "user" ? C.accentGlow : C.bg,
                border: `1px solid ${m.role === "user" ? C.borderLight : C.border}`,
                borderRadius: 8, padding: "10px 12px",
                color: C.text, fontSize: 12, lineHeight: 1.65,
                fontFamily: "'DM Mono',monospace",
                whiteSpace: "pre-wrap",
              }}>
                {m.content}
              </div>
            </div>
          ))}
          {chatLoading && (
            <div style={{ color: C.textDim, fontSize: 12, fontStyle: "italic" }}>Thinking…</div>
          )}
          <div ref={chatBottomRef} />
        </div>

        {/* Chat input */}
        <div style={{ padding: "10px 12px", borderTop: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", gap: 7 }}>
            <textarea
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
              placeholder="Ask about findings, patterns, next steps…"
              rows={2}
              style={{
                flex: 1, background: C.bg, border: `1px solid ${C.border}`,
                borderRadius: 7, color: C.text, fontSize: 12,
                padding: "8px 10px", resize: "none", outline: "none",
                fontFamily: "'DM Mono',monospace",
              }}
            />
            <button
              onClick={sendChat}
              disabled={chatLoading || !chatInput.trim()}
              style={{
                background: "rgba(167,139,250,0.2)", border: "1px solid rgba(167,139,250,0.4)",
                borderRadius: 7, color: "#A78BFA", padding: "0 12px",
                cursor: chatLoading || !chatInput.trim() ? "not-allowed" : "pointer",
                fontSize: 16, opacity: chatLoading || !chatInput.trim() ? 0.5 : 1,
              }}
            >↑</button>
          </div>
          <div style={{ color: C.textDim, fontSize: 10, marginTop: 5 }}>
            Enter to send · Shift+Enter for newline
          </div>
        </div>
      </div>
    </div>
      )}
    </div>
  );
}

// ─── MAIN EXPORT ──────────────────────────────────────────────────────────────
function MLModelTab({ company, transactions }) {
  const C = useTheme();
  const { t } = useTranslation();
  const token = localStorage.getItem('amlguard_token');

  const [models, setModels] = useState([]);
  const [activeModel, setActiveModel] = useState(null);
  const [training, setTraining] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [retraining, setRetraining] = useState(false);
  const [monitoring, setMonitoring] = useState(null);
  const [abTestResults, setAbTestResults] = useState(null);
  const [modelConfig, setModelConfig] = useState({
    name: "",
    description: "",
    modelType: "scoring",
    threshold: 0.5,
  });

  // Load models on mount
  useEffect(() => {
    loadModels();
  }, [company?.id]);

  const loadModels = async () => {
    if (!company?.id || !token) return;
    try {
      const res = await fetch('/api/tm/ml-model-config', {
        headers: getApiHeaders({ 'x-company-id': company.id, 'Authorization': `Bearer ${token}` }),
      });
      if (res.ok) {
        const data = await res.json();
        setModels(data.models || []);
        const active = data.models?.find(m => m.status === 'active');
        setActiveModel(active);
      }
    } catch (err) {
      console.warn('[ML] Error loading models:', err.message);
    }
  };

  const trainModel = async () => {
    if (!company?.id || !token || training) return;
    setTraining(true);
    try {
      const res = await fetch('/api/tm/train-model', {
        method: 'POST',
        headers: getApiHeaders({
          'Content-Type': 'application/json',
          'x-company-id': company.id,
          'Authorization': `Bearer ${token}`,
        }),
        body: JSON.stringify({ company_id: company.id }),
      });

      const data = await res.json();
      if (res.ok) {
        alert(`Model trained successfully!\nAccuracy: ${(data.metrics.accuracy * 100).toFixed(1)}%\nF1 Score: ${(data.metrics.f1Score * 100).toFixed(1)}%`);
        loadModels();
      } else {
        alert(`Training failed: ${data.error}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setTraining(false);
    }
  };

  const activateModel = async (modelId) => {
    if (!company?.id || !token) return;
    try {
      const res = await fetch('/api/tm/ml-model-config', {
        method: 'PUT',
        headers: getApiHeaders({
          'Content-Type': 'application/json',
          'x-company-id': company.id,
          'Authorization': `Bearer ${token}`,
        }),
        body: JSON.stringify({ modelId, status: 'active' }),
      });

      if (res.ok) {
        loadModels();
      }
    } catch (err) {
      console.warn('[ML] Error activating model:', err.message);
    }
  };

  const retrainModel = async () => {
    if (!company?.id || !token || retraining) return;
    setRetraining(true);
    try {
      const res = await fetch('/api/tm/retrain-scheduler', {
        method: 'POST',
        headers: getApiHeaders({
          'Content-Type': 'application/json',
          'x-company-id': company.id,
          'Authorization': `Bearer ${token}`,
        }),
        body: JSON.stringify({ company_id: company.id }),
      });

      const data = await res.json();
      if (res.ok) {
        alert(`Retraining successful!\nAccuracy improved from ${(data.retraining.accuracyBefore * 100).toFixed(1)}% to ${(data.retraining.accuracyAfter * 100).toFixed(1)}%`);
        loadModels();
      } else {
        alert(`Retraining failed: ${data.error}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setRetraining(false);
    }
  };

  const loadMonitoring = async () => {
    if (!company?.id || !token) return;
    try {
      const res = await fetch('/api/tm/model-monitoring?days=30', {
        headers: getApiHeaders({ 'x-company-id': company.id, 'Authorization': `Bearer ${token}` }),
      });
      if (res.ok) {
        const data = await res.json();
        setMonitoring(data.monitoring);
      }
    } catch (err) {
      console.warn('[ML] Error loading monitoring:', err.message);
    }
  };

  const loadABTests = async () => {
    if (!company?.id || !token) return;
    try {
      const res = await fetch('/api/tm/model-ab-test?range=30', {
        headers: getApiHeaders({ 'x-company-id': company.id, 'Authorization': `Bearer ${token}` }),
      });
      if (res.ok) {
        const data = await res.json();
        setAbTestResults(data.tests);
      }
    } catch (err) {
      console.warn('[ML] Error loading A/B tests:', err.message);
    }
  };

  useEffect(() => {
    loadMonitoring();
    loadABTests();
  }, [company?.id]);

  return (
    <div style={{ padding: "20px 0" }}>
      {/* Training Section */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ color: C.text, fontSize: 18, fontWeight: 700, margin: 0, marginBottom: 4 }}>🧠 Built-in ML Model Training</h2>
          <p style={{ color: C.textDim, fontSize: 13, margin: 0 }}>Train a model automatically on your historical transaction data</p>
        </div>

        <button
          onClick={trainModel}
          disabled={training || transactions.length < 50}
          style={{
            padding: "12px 24px",
            background: training ? `${C.accent}80` : C.accent,
            color: "#000",
            border: "none",
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: training || transactions.length < 50 ? "not-allowed" : "pointer",
            opacity: training || transactions.length < 50 ? 0.6 : 1,
          }}
        >
          {training ? "⏳ Training..." : "🚀 Train Model Now"}
        </button>
        <p style={{ color: C.textDim, fontSize: 11, margin: "12px 0 0" }}>
          {transactions.length < 50
            ? `Need at least 50 transactions (${transactions.length} available)`
            : `${transactions.length} transactions available for training`}
        </p>
      </div>

      {/* Models List */}
      {models.length > 0 && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 24 }}>
          <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: "0 0 16px" }}>📋 Models</h3>
          <div style={{ display: "grid", gap: 12 }}>
            {models.map(model => (
              <div key={model.id} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 12 }}>
                  <div>
                    <div style={{ color: C.text, fontWeight: 600, marginBottom: 4 }}>{model.model_name}</div>
                    <div style={{ color: C.textDim, fontSize: 12, marginBottom: 8 }}>{model.model_description}</div>
                    <div style={{ display: "flex", gap: 16, fontSize: 11, color: C.textMid }}>
                      <span>Type: {model.model_type}</span>
                      <span>Status: {model.status}</span>
                      <span>Updated: {new Date(model.updated_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  {model.status !== 'active' && (
                    <button
                      onClick={() => activateModel(model.id)}
                      style={{
                        padding: "8px 16px",
                        background: C.success,
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Activate
                    </button>
                  )}
                  {model.status === 'active' && (
                    <div style={{ color: C.success, fontSize: 12, fontWeight: 600 }}>✓ Active</div>
                  )}
                </div>

                {model.metrics && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: 12, marginTop: 12 }}>
                    <div style={{ background: C.surface, padding: 10, borderRadius: 6, textAlign: "center" }}>
                      <div style={{ color: C.textDim, fontSize: 10 }}>Accuracy</div>
                      <div style={{ color: C.text, fontSize: 16, fontWeight: 700 }}>{(model.metrics.accuracy * 100).toFixed(1)}%</div>
                    </div>
                    <div style={{ background: C.surface, padding: 10, borderRadius: 6, textAlign: "center" }}>
                      <div style={{ color: C.textDim, fontSize: 10 }}>Precision</div>
                      <div style={{ color: C.text, fontSize: 16, fontWeight: 700 }}>{(model.metrics.precision * 100).toFixed(1)}%</div>
                    </div>
                    <div style={{ background: C.surface, padding: 10, borderRadius: 6, textAlign: "center" }}>
                      <div style={{ color: C.textDim, fontSize: 10 }}>Recall</div>
                      <div style={{ color: C.text, fontSize: 16, fontWeight: 700 }}>{(model.metrics.recall * 100).toFixed(1)}%</div>
                    </div>
                    <div style={{ background: C.surface, padding: 10, borderRadius: 6, textAlign: "center" }}>
                      <div style={{ color: C.textDim, fontSize: 10 }}>F1 Score</div>
                      <div style={{ color: C.text, fontSize: 16, fontWeight: 700 }}>{(model.metrics.f1Score * 100).toFixed(1)}%</div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Auto-Retraining */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ color: C.text, fontSize: 18, fontWeight: 700, margin: 0, marginBottom: 4 }}>🔄 Auto-Retraining</h2>
          <p style={{ color: C.textDim, fontSize: 13, margin: 0 }}>Automatically retrain models based on analyst feedback</p>
        </div>

        <button
          onClick={retrainModel}
          disabled={retraining}
          style={{
            padding: "12px 24px",
            background: retraining ? `${C.accent}80` : C.accent,
            color: "#000",
            border: "none",
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: retraining ? "not-allowed" : "pointer",
            opacity: retraining ? 0.6 : 1,
            marginBottom: 12,
          }}
        >
          {retraining ? "⏳ Retraining..." : "🚀 Retrain Now"}
        </button>
        <p style={{ color: C.textDim, fontSize: 11, margin: 0 }}>Weekly automated retraining based on accumulated feedback</p>
      </div>

      {/* Model Monitoring */}
      {monitoring && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 24 }}>
          <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: "0 0 16px" }}>📊 Model Performance Monitoring</h3>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 16 }}>
            <div style={{ background: C.bg, padding: 12, borderRadius: 8 }}>
              <div style={{ color: C.textDim, fontSize: 10 }}>Overall Accuracy</div>
              <div style={{ color: C.text, fontSize: 18, fontWeight: 700 }}>{(monitoring.summary.overallAccuracy * 100).toFixed(1)}%</div>
            </div>
            <div style={{ background: C.bg, padding: 12, borderRadius: 8 }}>
              <div style={{ color: C.textDim, fontSize: 10 }}>Recent Trend</div>
              <div style={{ color: monitoring.summary.accuracyTrendPercent >= 0 ? C.success : C.danger, fontSize: 18, fontWeight: 700 }}>
                {monitoring.summary.accuracyTrendPercent >= 0 ? '+' : ''}{monitoring.summary.accuracyTrendPercent}%
              </div>
            </div>
            <div style={{ background: C.bg, padding: 12, borderRadius: 8 }}>
              <div style={{ color: C.textDim, fontSize: 10 }}>Drift Status</div>
              <div style={{ color: monitoring.summary.driftDetected ? C.danger : C.success, fontSize: 14, fontWeight: 700 }}>
                {monitoring.summary.driftDetected ? '⚠️ High' : '✓ Normal'}
              </div>
            </div>
          </div>

          <div style={{ background: C.bg, padding: 12, borderRadius: 8, fontSize: 12, color: C.textMid }}>
            <strong>Recommendation:</strong> {monitoring.summary.recommendation}
          </div>
        </div>
      )}

      {/* A/B Testing Results */}
      {abTestResults && abTestResults.length > 0 && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 24 }}>
          <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: "0 0 16px" }}>⚔️ A/B Testing Results (Last 30 Days)</h3>

          {abTestResults.map((test, i) => (
            <div key={i} style={{ background: C.bg, padding: 12, borderRadius: 8, marginBottom: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ color: C.textDim, fontSize: 10 }}>Model A</div>
                  <div style={{ color: C.text, fontSize: 16, fontWeight: 700 }}>{(test.modelAAccuracy * 100).toFixed(1)}%</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ color: C.textDim, fontSize: 10 }}>Tests</div>
                  <div style={{ color: C.text, fontSize: 16, fontWeight: 700 }}>{test.totalTests}</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ color: C.textDim, fontSize: 10 }}>Model B</div>
                  <div style={{ color: C.text, fontSize: 16, fontWeight: 700 }}>{(test.modelBAccuracy * 100).toFixed(1)}%</div>
                </div>
              </div>
              <div style={{ marginTop: 8, fontSize: 11, color: test.winner === 'A' ? C.success : C.danger }}>
                <strong>Winner:</strong> Model {test.winner}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Custom Model Upload */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ color: C.text, fontSize: 18, fontWeight: 700, margin: 0, marginBottom: 4 }}>📤 Upload Custom Model</h2>
          <p style={{ color: C.textDim, fontSize: 13, margin: 0 }}>Upload your own trained ML model (optional)</p>
        </div>

        <div style={{ background: C.bg, border: `2px dashed ${C.border}`, borderRadius: 8, padding: 20, textAlign: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🤖</div>
          <div style={{ color: C.text, fontWeight: 600, marginBottom: 4 }}>Choose Model File</div>
          <p style={{ color: C.textDim, fontSize: 12, margin: "0 0 12px" }}>Supported: .pkl, .joblib, .onnx, .tflite, .h5</p>
          <label style={{ cursor: "pointer" }}>
            <input
              type="file"
              accept=".pkl,.joblib,.onnx,.tflite,.h5"
              style={{ display: "none" }}
            />
            <span style={{ background: C.accent, color: "#000", padding: "10px 20px", borderRadius: 6, fontSize: 13, fontWeight: 600, display: "inline-block" }}>
              Choose File
            </span>
          </label>
        </div>

        <div style={{ color: "rgba(100,200,255,0.8)", fontSize: 12, background: "rgba(100,200,255,0.08)", border: `1px solid rgba(100,200,255,0.25)`, borderRadius: 8, padding: 12 }}>
          <strong>💡 Tip:</strong> Models trained on your data will outperform generic models. Use the training feature above for best results.
        </div>
      </div>
    </div>
  );
}

export default function TransactionMonitoring({ onNewCases, cases, company, currentUser }) {
  const C = useTheme();
  const { t } = useTranslation();
  const { log } = useAuditLog(company, currentUser);
  const [activeTab, setActiveTab]     = useState("transactions");
  const [realtimeScreening, setRealtimeScreening] = useState(() => {
    try { return JSON.parse(localStorage.getItem("crg_realtime_screening") || "false"); } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("crg_realtime_screening", JSON.stringify(realtimeScreening)); } catch {}
  }, [realtimeScreening]);
  const [transactions, setTransactions] = useState(() => {
    try {
      const saved = localStorage.getItem("crossregguard_tm_transactions");
      if (saved) {
        const parsed = JSON.parse(saved);
        // If all IDs are sample data, clear it and start fresh
        const isAllSamples = parsed.length > 0 && parsed.every(t => t.id?.startsWith("TX-SAMPLE"));
        if (!isAllSamples) return parsed;
      }
      return []; // start empty — real data loads from Supabase
    } catch { return []; }
  });
  const [rules, setRules]             = useState(() => {
    try {
      const saved = localStorage.getItem("crossregguard_tm_rules");
      return saved ? JSON.parse(saved) : DEFAULT_RULES;
    } catch { return DEFAULT_RULES; }
  });
  const [tmAlerts, setTmAlerts] = useState(() => {
    try {
      const saved = localStorage.getItem("crossregguard_tm_alerts");
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [companyKeywords, setCompanyKeywords] = useState(null);
  const tmAlertsRef = useRef([]);
  useEffect(() => { tmAlertsRef.current = tmAlerts; }, [tmAlerts]);

  // ── Validate transaction schema ──────────────────────────────────────────────
  const validateTransactionSchema = (tx) => {
    const requiredFields = ['tx_reference', 'tx_date', 'tx_type', 'entity_name', 'amount', 'currency', 'direction'];
    const missing = requiredFields.filter(f => !tx[f]);
    if (missing.length > 0) {
      console.warn(`[TM] Transaction ${tx.id} missing fields:`, missing);
    }
    return missing.length === 0;
  };

  // Load transactions, alerts, and customers from Neon on mount
  useEffect(() => {
    if (!company?.id) return;

    const loadFromNeon = async () => {
      const token = localStorage.getItem('amlguard_token');
      if (!token) return;

      try {
        // Load customers for entity matching
        try {
          const { data } = await db.from("customers")
            .select("id,name")
            .eq("company_id", company.id)
            .execute();
          if (data?.length > 0) {
            _customerDatabase = data.map(c => ({ ...c, full_name: c.name }));
            console.log(`[TM] Loaded ${data.length} customers for entity matching`);
          }
        } catch (err) {
          console.warn('[TM] Error loading customers:', err.message);
        }

        // Load custom red flags/keywords
        try {
          const keywords = await loadCompanyKeywords(company.id, token);
          if (keywords && Object.keys(keywords).length > 0) {
            setCompanyKeywords(keywords);
            console.log(`[TM] Loaded red flag keywords for company: ${Object.keys(keywords).map(k => `${k}(${keywords[k].length})`).join(', ')}`);
          }
        } catch (err) {
          console.warn('[TM] Error loading keywords:', err.message);
        }

        // Load transactions
        const txRes = await fetch(`/api/tm/transactions?company_id=${company.id}`, {
          headers: getApiHeaders({ Authorization: `Bearer ${token}` }),
        });
        if (txRes.ok) {
          const data = await txRes.json();
          if (data.transactions?.length > 0) {
            console.log(`[TM] Loaded ${data.transactions.length} transactions from Neon`);
            setTransactions(data.transactions);
          }
        }

        // Load alerts
        const alertRes = await fetch(`/api/tm/alerts?company_id=${company.id}`, {
          headers: getApiHeaders({ Authorization: `Bearer ${token}` }),
        });
        if (alertRes.ok) {
          const data = await alertRes.json();
          if (data.alerts?.length > 0) {
            console.log(`[TM] Loaded ${data.alerts.length} alerts from Neon`);
            setTmAlerts(data.alerts);
          }
        }
      } catch (error) {
        console.warn('[TM] Error loading from Neon:', error.message);
      }
    };

    loadFromNeon();
  }, [company?.id]);

  // ── AML Model state ─────────────────────────────────────────────────────────
  const [amlModel] = useState(() => new AMLModel());
  const [txScores, setTxScores] = useState(() => {
    try {
      const saved = localStorage.getItem("crossregguard_tx_scores");
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [selectedTxForModel, setSelectedTxForModel] = useState(null);
  const [showModelManager, setShowModelManager] = useState(false);

  // Persist txScores to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem("crossregguard_tx_scores", JSON.stringify(txScores));
    } catch (error) {
      console.error("[TM] Failed to save scores to localStorage:", error);
    }
  }, [txScores]);

  // ── AML Model functions ──────────────────────────────────────────────────────
  const scoreTransactionWithModel = useCallback(async (tx) => {
    if (!tx || !amlModel) return null;

    const recentTxs = transactions.filter(t =>
      t.entity === tx.entity &&
      new Date(t.date) > new Date(Date.now() - 7 * 86400000)
    );

    const context = {
      recentTransactions: recentTxs,
      txCountPastDay: transactions.filter(t =>
        t.entity === tx.entity &&
        new Date(t.date) > new Date(Date.now() - 86400000)
      ).length,
      recentTransactionCount: recentTxs.length,
      isFirstTimeEntity: !transactions.some(t => t.entity === tx.entity && t.id !== tx.id),
    };

    const score = amlModel.scoreTransaction(tx, context);
    setTxScores(prev => ({ ...prev, [tx.id]: score }));

    try {
      await fetch(`${PROXY}/api/aml/score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: company?.id,
          transaction: tx,
          historicalContext: context,
        }),
      });
    } catch (error) {
      console.error('[TM] Model scoring failed:', error);
    }

    return score;
  }, [amlModel, transactions, company?.id]);

  const handleModelFeedback = useCallback(async (feedback) => {
    try {
      const response = await fetch(`${PROXY}/api/aml/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: company?.id,
          transactionId: feedback.transactionId,
          analystId: company?.id,
          isTruePositive: feedback.isTruePositive,
          notes: feedback.notes,
          originalScore: feedback.originalScore,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.updatedWeights) {
          amlModel.weights = result.updatedWeights;
        }
        alert('✓ Feedback submitted. Model updated.');
      }
    } catch (error) {
      console.error('[TM] Feedback submission failed:', error);
      alert('Failed to submit feedback');
    }
  }, [company?.id, amlModel]);

  // ── Scheduler state ─────────────────────────────────────────────────────────
  const [schedule, setSchedule] = useState(() => {
    try {
      const saved = localStorage.getItem("crossregguard_tm_schedule");
      return saved ? JSON.parse(saved) : {
        enabled: false,
        time: "09:00",
        days: [1,2,3,4,5],
        lastRun: null,
        nextRun: null,
      };
    } catch { return { enabled: false, time: "09:00", days: [1,2,3,4,5], lastRun: null, nextRun: null }; }
  });
  const [scheduleLog, setScheduleLog] = useState(() => {
    try { return JSON.parse(localStorage.getItem("crossregguard_tm_sched_log") || "[]"); } catch { return []; }
  });
  const [schedulerStatus, setSchedulerStatus] = useState("idle");
  const [flBadgeHovered, setFlBadgeHovered]   = useState(false);

  // Persist state
  useEffect(() => {
    try { localStorage.setItem("crossregguard_tm_transactions", JSON.stringify(transactions)); } catch {}
  }, [transactions]);

  useEffect(() => {
    try { localStorage.setItem("crossregguard_tm_rules", JSON.stringify(rules)); } catch {}
    // Rules persisted to localStorage only
  }, [rules]);

  useEffect(() => {
    try { localStorage.setItem("crossregguard_tm_alerts", JSON.stringify(tmAlerts)); } catch {}
  }, [tmAlerts]);

  useEffect(() => {
    try { localStorage.setItem("crossregguard_tm_schedule", JSON.stringify(schedule)); } catch {}
  }, [schedule]);

  useEffect(() => {
    try { localStorage.setItem("crossregguard_tm_sched_log", JSON.stringify(scheduleLog.slice(-50))); } catch {}
  }, [scheduleLog]);

  // ── Item 18: Load rules from Supabase on mount (authoritative over localStorage) ──
  useEffect(() => {
    if (!company?.id) return;
    db.from("tm_rules")
      .select("*")
      .eq("company_id", company.id)
      .order("created_at", { ascending: true })
      .then(({ data: dbRules }) => {
        if (!dbRules?.length) return; // keep localStorage/default rules
        setRules(dbRules.map(r => ({
          id: r.id, name: r.name, category: r.category,
          threshold: r.threshold, thresholdCurrency: r.threshold_currency || "USD",
          windowDays: r.window_days || 1, conditions: r.conditions || null,
          enabled: r.enabled !== false,
        })));
      });
  }, [company?.id]);

  // ── Load transactions + alerts from Neon on mount ──────────────────────
  useEffect(() => {
    if (!company?.id) return;

    // Load transactions with comprehensive field mapping and validation
    db
      .from("transactions")
      .select("*")
      .eq("company_id", company.id)
      .order("tx_date", false)
      .then(({ data: dbTxs }) => {
        if (!dbTxs?.length) {
          console.log("[TM] No transactions found");
          return;
        }

        const dbMapped = dbTxs.map(r => {
          // Comprehensive field mapping with fallbacks
          const tx = {
            id: r.tx_reference || r.id || `TX-${Date.now()}`,
            tx_reference: r.tx_reference || r.id,
            tx_date: r.tx_date || (r.created_at ? r.created_at.slice(0, 10) : new Date().toISOString().slice(0, 10)),
            entity_name: r.entity_name || r.entity || r.sender || r.receiver || "Unknown",
            date: r.tx_date?.slice(0, 10) || (r.created_at ? r.created_at.slice(0, 10) : ""),
            entity: r.entity_name || r.entity || r.sender || r.receiver || "Unknown",
            amount: parseFloat(r.amount) || 0,
            currency: (r.currency || "USD").toUpperCase(),
            tx_type: r.tx_type || r.transaction_type || "Wire",
            type: r.tx_type || r.transaction_type || "Wire",
            direction: (r.direction || "out").toLowerCase() === "in" ? "in" : "out",
            counterparty_country: r.counterparty_country || r.country || "",
            country: r.counterparty_country || r.country || "",
            description: r.description || "",
            payment_method: r.payment_method || "wire",
            paymentMethod: r.payment_method || "wire",
            account_age: r.account_age || 365,
            accountAge: r.account_age || 365,
            pep_flag: r.pep_flag || false,
            pepFlag: r.pep_flag || false,
            wallet_from: r.wallet_from || "",
            walletFrom: r.wallet_from || "",
            wallet_to: r.wallet_to || "",
            walletTo: r.wallet_to || "",
            crypto_type: r.crypto_type || "",
            cryptoEntity: r.crypto_type || "",
            status: r.status || "completed",
            risk_score: r.risk_score || 0,
          };

          // Validate required fields
          if (!validateTransactionSchema(tx)) {
            console.warn(`[TM] Transaction ${tx.id} has missing required fields:`, tx);
          }

          return tx;
        });

        console.log(`[TM] Loaded ${dbMapped.length} transactions from database`);
        setTransactions(prev => {
          const existingIds = new Set(prev.map(t => t.id));
          const fresh = dbMapped.filter(t => !existingIds.has(t.id));
          return fresh.length ? [...prev, ...fresh] : prev;
        });
      });

    // Load alerts — Neon status (sent_to_case) takes priority over localStorage
    db
      .from("alerts")
      .select("*")
      .eq("company_id", company.id)
      .order("created_at", false)
      .then(({ data: dbAlerts }) => {
        if (!dbAlerts?.length) return;
        const mapped = dbAlerts.map(r => ({
          id: r.id, txId: r.tx_id, entity: r.entity, amount: r.amount,
          type: r.type, risk: r.risk, severity: r.risk,
          status: r.status || "open",
          date: r.date, country: r.country || "", notes: r.notes || "",
          source: r.source || "TM Engine", ruleId: r.rule_id || null, ruleCategory: null,
        }));
        // Merge: Supabase status wins over localStorage status
        setTmAlerts(prev => {
          const byId = {};
          prev.forEach(a => { byId[a.id] = a; });
          mapped.forEach(a => { byId[a.id] = { ...(byId[a.id] || {}), ...a }; });
          return Object.values(byId).sort((a, b) => new Date(b.date||0) - new Date(a.date||0));
        });
      });
  }, [company?.id]);

  async function handleAlertsGenerated(newAlerts) {
    if (!newAlerts.length) return;
    setActiveTab("alerts");

    // ── In-memory dedup: compare against current state synchronously ─────────
    // Handle both camelCase (in-memory) and snake_case (freshly loaded from DB)

    // Permanently closed statuses — never recreate these
    const closedStatuses = new Set(["sent_to_case", "dismissed", "false_positive"]);
    const closedTxIds = new Set(
      tmAlertsRef.current
        .filter(a => closedStatuses.has(a.status))
        .map(a => a.txId || a.tx_id)
    );

    // NEW: Also track open/under_review alerts to prevent multiple alerts per transaction
    const openTxIds = new Map(); // txId -> { highestAlert, status }
    tmAlertsRef.current
      .filter(a => !closedStatuses.has(a.status))
      .forEach(a => {
        const txId = a.txId || a.tx_id;
        const severity = (a.severity || a.risk || "LOW").toUpperCase();
        const severityRank = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 }[severity] || 0;

        if (!openTxIds.has(txId) || severityRank > (openTxIds.get(txId).severityRank || 0)) {
          openTxIds.set(txId, { highestAlert: a, severityRank });
        }
      });

    // Filter out alerts for transactions that are closed or already have alerts
    const fresh = newAlerts.filter(a => {
      const txId = a.txId;
      return !closedTxIds.has(txId) && !openTxIds.has(txId);
    });

    if (!fresh.length) return;

    // CRITICAL: Keep ONLY ONE alert per transaction — the highest severity one
    // This prevents multiple rule triggers from creating multiple alerts per tx
    const byTxId = {};
    for (const alert of fresh) {
      const txId = alert.txId;
      const severity = (alert.severity || alert.risk || "LOW").toUpperCase();
      const severityRank = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 }[severity] || 0;

      if (!byTxId[txId]) {
        // First alert for this tx
        byTxId[txId] = { alert, severityRank };
      } else if (severityRank > byTxId[txId].severityRank) {
        // New alert has higher severity — replace
        byTxId[txId] = { alert, severityRank };
      }
      // else: keep the existing alert (either same or lower severity, so keep first)
    }
    const dedupedAlerts = Object.values(byTxId).map(({ alert }) => alert);

    console.log(`[TM] Alert generation: ${fresh.length} fresh alerts → ${dedupedAlerts.length} deduped (1 per tx max)`);

    // Save to local state regardless of company (for local auth)
    setTmAlerts(prev => [...prev, ...dedupedAlerts]);

    // Only sync to DB if company exists
    if (!company?.id) return;

    const token = localStorage.getItem('amlguard_token');
    if (!token) return;

    // Save AI-generated alerts to Neon
    for (const alert of dedupedAlerts) {
      try {
        await fetch('/api/tm/alerts', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            id: alert.id,
            company_id: company.id,
            alert_type: alert.type || 'suspicious_activity',
            severity: alert.severity || alert.risk || 'high',
            status: alert.status || 'open',
            type: alert.type,
            entity: alert.entity,
            amount: alert.amount,
            risk: alert.risk,
            country: alert.country,
            date: alert.date,
            notes: alert.notes,
            rule: alert.ruleName,
            tx_id: alert.txId,
          }),
        });
      } catch (error) {
        console.warn('[TM] Failed to save AI alert to Neon:', error.message);
      }
    }
  }

  async function handleSendToCase(alert) {
    // Build comprehensive case notes from alert and transaction details
    const txDescription = alert.txDescription || alert.description || "";
    const caseNotes = [
      alert.notes,
      txDescription ? `Transaction Description: ${txDescription}` : "",
      alert.ruleName ? `Rule Triggered: ${alert.ruleName}` : "",
      alert.ruleCategory ? `Category: ${alert.ruleCategory}` : "",
    ].filter(Boolean).join(" | ");

    const caseData = {
      entity:   alert.entity,
      amount: alert.amount ? parseFloat(String(alert.amount).replace(/[^0-9.]/g, '')) : 0,
      type:     alert.type,
      risk:     alert.risk,
      status:   "open",
      tx_count: 1,
      notes:    caseNotes || "",
      date:     alert.date || new Date().toISOString().split('T')[0],
      country:  alert.country || "",
    };

    if (company?.id) {
      const token = localStorage.getItem('amlguard_token');
      if (token) {
        try {
          const payload = { ...caseData, company_id: company.id };
          console.log('[TM] Sending case to Neon:', payload);
          // Save case to Neon
          const res = await fetch('/api/query?resource=cases', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          });
          if (!res.ok) {
            try {
              const err = await res.json();
              console.error('[TM] Failed to save case to Neon:', err.error, err.detail);
            } catch (parseErr) {
              console.error('[TM] Failed to save case to Neon, no JSON response');
            }
          } else {
            console.log('[TM] Case saved to Neon');
            // Update alert status to sent_to_case
            await changeDisposition(alert.id, 'sent_to_case');
          }
        } catch (error) {
          console.error('[TM] Case save exception:', error.message);
        }
      }
    } else {
      // For local auth users, save to localStorage
      try {
        const localCases = JSON.parse(localStorage.getItem("crossregguard_cases") || "[]");
        const newCase = { id: alert.id, ...caseData };
        const existingCase = localCases.find(c => c.id === newCase.id);
        if (!existingCase) {
          localCases.push(newCase);
          localStorage.setItem("crossregguard_cases", JSON.stringify(localCases));
        }
      } catch (error) {
        console.error("[TM] Failed to save case to localStorage:", error);
      }
    }
  }

  // Batch version — single insert for all open alerts
  async function handleSendAllToCase(alerts) {
    console.log('[TM] handleSendAllToCase called with', alerts?.length, 'alerts, company.id:', company?.id);
    if (!alerts?.length) return;

    if (company?.id) {
      const token = localStorage.getItem('amlguard_token');
      console.log('[TM] token exists?', !!token);
      if (token) {
        try {
          // Insert each case individually
          let created = 0;
          console.log('[TM] Starting to create cases for', alerts.length, 'alerts');
          for (const alert of alerts) {
            // Build comprehensive case notes from alert and transaction details
            const txDescription = alert.txDescription || alert.description || "";
            const caseNotes = [
              alert.notes,
              txDescription ? `Transaction Description: ${txDescription}` : "",
              alert.ruleName ? `Rule Triggered: ${alert.ruleName}` : "",
              alert.ruleCategory ? `Category: ${alert.ruleCategory}` : "",
            ].filter(Boolean).join(" | ");

            const caseData = {
              entity: alert.entity,
              amount: alert.amount ? parseFloat(String(alert.amount).replace(/[^0-9.]/g, '')) : 0,
              type: alert.type,
              risk: alert.risk,
              status: "open",
              tx_count: 1,
              notes: caseNotes || "",
              date: alert.date || new Date().toISOString().split('T')[0],
              country: alert.country || "",
              company_id: company.id,
              trigger_transaction_ref: alert.txReference || null,
            };

            console.log('[TM] Posting case:', caseData.entity, 'company_id:', caseData.company_id);
            const res = await fetch('/api/query?resource=cases', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify(caseData),
            });

            console.log('[TM] POST /api/query?resource=cases response status:', res.status);
            if (res.ok) {
              created++;
              const caseResult = await res.json();
              console.log('[TM] ✓ Case created:', caseResult.case?.case_number);
              // Update alert status directly without triggering onSendToCase (which would create duplicate cases)
              // Try to update in Neon, but fall back to localStorage if it fails
              fetch('/api/tm/alerts', {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                  alert_id: alert.id,
                  status: 'sent_to_case',
                }),
              }).then(r => {
                if (r.ok) {
                  console.log('[TM] ✓ Alert status updated in Neon');
                } else {
                  console.warn('[TM] Alert update failed in Neon (expected - alerts only in localStorage)');
                }
              }).catch(e => {
                console.warn('[TM] Failed to update alert in Neon:', e.message);
              });
              // Always update in localStorage and UI state
              setTmAlerts(prev => prev.map(a => a.id === alert.id ? { ...a, status: 'sent_to_case' } : a));
            } else {
              const errData = await res.json().catch(() => ({}));
              console.error('[TM] ✗ Failed to create case:', res.status, errData);
              if (created === 0) {
                console.error('[TM] First case failed, stopping batch:', JSON.stringify(caseData));
              }
            }
          }
          console.log(`[TM] ✓ Finished: Created ${created}/${alerts.length} cases in Neon`);
        } catch (error) {
          console.error("[TM] ✗ Batch case save exception:", error.message);
        }
      } else {
        console.warn('[TM] No token in localStorage');
      }
    } else {
      console.warn('[TM] No company.id, falling back to localStorage');
      // For local auth users, save to localStorage
      try {
        const localCases = JSON.parse(localStorage.getItem("crossregguard_cases") || "[]");
        const existingIds = new Set(localCases.map(c => c.id));
        const toInsert = alerts.filter(a => !existingIds.has(a.id))
          .map(alert => ({
            id: alert.id,
            entity: alert.entity,
            amount: alert.amount ? parseFloat(String(alert.amount).replace(/[^0-9.]/g, '')) : 0,
            type: alert.type,
            risk: alert.risk,
            status: "open",
            tx_count: 1,
            notes: alert.notes || "",
            date: alert.date || new Date().toISOString().split('T')[0],
            country: alert.country || "",
          }));
        if (toInsert.length > 0) {
          localCases.push(...toInsert);
          localStorage.setItem("crossregguard_cases", JSON.stringify(localCases));
        }
      } catch (error) {
        console.error("[TM] Failed to save cases to localStorage:", error);
      }
    }

    if (onNewCases) await onNewCases(alerts);
  }

  // ── Scheduled screening runner ──────────────────────────────────────────────
  function runScheduledScreening(triggeredBy = "scheduler") {
    if (schedulerStatus === "running") return;
    setSchedulerStatus("running");

    setTimeout(() => {
      const newAlerts = [];
      setTransactions(currentTx => {
        setRules(currentRules => {
          // Build entity history for velocity rules
          const schedEntityHistory = {};
          currentTx.forEach(tx => {
            const key = (tx.entity || "unknown").toLowerCase().trim();
            if (!schedEntityHistory[key]) schedEntityHistory[key] = [];
            schedEntityHistory[key].push(tx);
          });
          Object.values(schedEntityHistory).forEach(txs =>
            txs.sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0))
          );
          currentTx.forEach(tx => {
            const hits = evaluateTransaction(tx, currentRules, new Set(), new Set(), schedEntityHistory);

            // Analyze transaction for red flags
            let redFlagAnalysis = null;
            if (companyKeywords) {
              redFlagAnalysis = analyzeTransactionForRedFlags(tx, companyKeywords);
            }

            hits.forEach(hit => {
              const alert = {
                id: `TM-${Date.now()}-${Math.random().toString(36).slice(2,7).toUpperCase()}`,
                txId: tx.id, entity: tx.entity,
                amount: `${tx.currency} ${tx.amount.toLocaleString()}`,
                type: hit.ruleName, ruleId: hit.ruleId, ruleCategory: hit.ruleCategory,
                risk: hit.severity === "CRITICAL" ? "HIGH" : hit.severity,
                severity: hit.severity, status: "open", date: tx.date, txCount: 1,
                country: tx.country, notes: hit.detail, assignee: "",
                reference: hit.reference, tags: hit.tags, source: "TM Engine (Scheduled)",
                redFlags: redFlagAnalysis?.redFlags || [],
                riskIncrease: redFlagAnalysis?.riskIncrease || 0,
                requiresEscalation: redFlagAnalysis?.hasEscalation || false,
              };
              newAlerts.push(alert);
            });
          });

          const now = new Date();
          const logEntry = {
            id: Date.now(),
            timestamp: now.toISOString(),
            triggeredBy,
            txCount: currentTx.length,
            alertsGenerated: newAlerts.length,
          };

          handleAlertsGenerated(newAlerts);
          setScheduleLog(prev => [logEntry, ...prev].slice(0, 50));
          setSchedule(prev => ({ ...prev, lastRun: now.toISOString() }));
          setSchedulerStatus("idle");

          return currentRules;
        });
        return currentTx;
      });
    }, 300);
  }

  // ── Keep a stable ref to runScheduledScreening so the interval never goes stale ──
  const runScreeningRef = useRef(runScheduledScreening);
  useEffect(() => { runScreeningRef.current = runScheduledScreening; });

  // ── Clock tick: check every 30 seconds if a scheduled run is due ─────────────
  useEffect(() => {
    if (!schedule.enabled) return;

    const tick = () => {
      const now = new Date();
      const dayOfWeek = now.getDay();
      if (!schedule.days.includes(dayOfWeek)) return;

      const [hh, mm] = schedule.time.split(":").map(Number);
      const nowMins  = now.getHours() * 60 + now.getMinutes();
      const targMins = hh * 60 + mm;

      // ✅ FIX: accept any tick within a 1-minute window (was > 0, i.e. exact match only)
      if (Math.abs(nowMins - targMins) > 1) return;

      // Avoid double-firing within the same minute
      if (schedule.lastRun) {
        const last = new Date(schedule.lastRun);
        const diffMins = (now - last) / 60000;
        if (diffMins < 1) return;
      }

      // ✅ FIX: call via ref so interval always uses fresh function, no stale closure
      runScreeningRef.current("scheduler");
    };

    tick(); // check immediately on mount / schedule change
    const interval = setInterval(tick, 30000);
    return () => clearInterval(interval);

  // ✅ FIX: removed runScheduledScreening from deps (it was re-created every render,
  //    causing the interval to constantly tear down and rebuild). The ref handles this.
  }, [schedule.enabled, schedule.time, schedule.days, schedule.lastRun]);

  const openAlertsCount = tmAlerts.filter(a => a.status === "open").length;
  const activeRulesCount = rules.filter(r => r.enabled).length;

  const tabs = [
    { id: "transactions", label: t('tm.transactionsTab'), icon: "⇄", sub: t('tm.transactionsLoaded', { count: transactions.length }) },
    { id: "rules",        label: t('tm.rulesTab'),        icon: "⚙", sub: `${activeRulesCount} ${t('tm.rules.active')}` },
    { id: "model",        label: "Quantitative Model",    icon: "📊", sub: "AML risk scoring", accent: true },
    { id: "mlmodel",      label: "ML Model",              icon: "🤖", sub: "Custom model configuration" },
    { id: "alerts",       label: t('tm.alertsTab'),       icon: "◈", sub: openAlertsCount > 0 ? `${openAlertsCount} ${t('tm.alertStatus.open')}` : `0 ${t('tm.alertStatus.open')}`, badge: openAlertsCount },
    { id: "schedule",     label: t('tm.scheduleTab'),     icon: "⏱", sub: schedule.enabled ? `${schedule.time} ${t('tm.schedule.daily')}` : t('tm.schedule.off') },
  ];

  return (
    <div style={{ fontFamily: "'DM Mono', monospace" }}>
      <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Mono:wght@300;400;500&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 26, fontWeight: 700, margin: 0 }}>{t('tm.title')}</h1>
          <p style={{ color: C.textMid, margin: "4px 0 0", fontSize: 14 }}>
            {t('tm.subtitleStats', { rules: activeRulesCount, transactions: transactions.length, alerts: openAlertsCount })}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ padding: "6px 14px", background: C.accentGlow, border: `1px solid ${C.borderLight}`, borderRadius: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.success, boxShadow: `0 0 6px ${C.success}` }} />
            <span style={{ color: C.textMid, fontSize: 12 }}>{t('tm.engineActive')}</span>
          </div>
          {schedule.enabled && (
            <div style={{ padding: "6px 14px", background: `${C.gold}15`, border: `1px solid ${C.gold}40`, borderRadius: 6, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 13 }}>{schedulerStatus === "running" ? "⏳" : "⏱"}</span>
              <span style={{ color: C.gold, fontSize: 12 }}>
                {schedulerStatus === "running" ? t('tm.schedule.running') : `${t('tm.scheduleActive')} · ${schedule.time}`}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: `1px solid ${C.border}`, paddingBottom: 0 }}>
        {tabs.map(tab => {
          const isActive = activeTab === tab.id;
          const tabColor = tab.accent ? "#A78BFA" : C.accent;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              padding: "10px 20px", background: "transparent", border: "none", cursor: "pointer",
              borderBottom: `2px solid ${isActive ? tabColor : "transparent"}`,
              color: isActive ? tabColor : C.textMid,
              fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: isActive ? 700 : 400,
              display: "flex", alignItems: "center", gap: 8, marginBottom: -1, transition: "all 0.12s",
            }}>
              <span>{tab.icon}</span>
              {tab.label}
              <span style={{ color: isActive ? (tab.accent ? "rgba(167,139,250,0.6)" : C.accentDim) : C.textDim, fontSize: 11 }}>({tab.sub})</span>
              {tab.badge > 0 && (
                <span style={{ background: C.danger, color: "#fff", fontSize: 10, padding: "2px 6px", borderRadius: 10, fontWeight: 700 }}>{tab.badge}</span>
              )}
            </button>
          );
        })}
        {/* Coming Soon badge — sits inline after the Schedule tab */}
        <div style={{ display: "flex", alignItems: "center", marginLeft: 8, position: "relative" }}
          onMouseEnter={() => setFlBadgeHovered(true)}
          onMouseLeave={() => setFlBadgeHovered(false)}>
          <div style={{ padding: "4px 10px", background: "rgba(167,139,250,0.1)",
            border: "1px solid rgba(167,139,250,0.35)", borderRadius: 20,
            color: "#A78BFA", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
            cursor: "default", whiteSpace: "nowrap" }}>
            ✦ {t('tm.comingSoon')}
          </div>
          {flBadgeHovered && (
            <div style={{ position: "absolute", bottom: "calc(100% + 8px)", left: "50%",
              transform: "translateX(-50%)", background: "#1E2D45",
              border: "1px solid rgba(167,139,250,0.4)", borderRadius: 7,
              padding: "7px 12px", whiteSpace: "nowrap", color: "#E8EDF5",
              fontSize: 11, fontFamily: "'DM Mono',monospace", zIndex: 999,
              pointerEvents: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.5)" }}>
              {t('tm.comingSoonTooltip')}
              <div style={{ position: "absolute", top: "100%", left: "50%",
                transform: "translateX(-50%)", borderWidth: 5, borderStyle: "solid",
                borderColor: "#1E2D45 transparent transparent transparent" }} />
            </div>
          )}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === "transactions" && (
        <TransactionsTab transactions={transactions} setTransactions={setTransactions} rules={rules} onAlertsGenerated={handleAlertsGenerated} realtimeScreening={realtimeScreening} onRunNow={() => runScreeningRef.current("realtime")} company={company} log={log} companyKeywords={companyKeywords} />
      )}
      {activeTab === "rules" && (
        <RulesTab rules={rules} setRules={setRules} transactions={transactions} tmAlerts={tmAlerts} cases={cases} log={log} />
      )}
      {activeTab === "model" && (
        <ModelTab
          transactions={transactions}
          company={company}
          amlModel={amlModel}
          txScores={txScores}
          selectedTxForModel={selectedTxForModel}
          setSelectedTxForModel={setSelectedTxForModel}
          scoreTransactionWithModel={scoreTransactionWithModel}
          handleModelFeedback={handleModelFeedback}
        />
      )}
      {activeTab === "mlmodel" && (
        <MLModelTab company={company} transactions={transactions} />
      )}
      {activeTab === "alerts" && (
        <AlertsTab tmAlerts={tmAlerts} setTmAlerts={setTmAlerts} onSendToCase={handleSendToCase} onSendAllToCase={handleSendAllToCase} company={company} log={log} />
      )}
      {activeTab === "schedule" && (
        <ScheduleTab
          schedule={schedule}
          setSchedule={setSchedule}
          scheduleLog={scheduleLog}
          schedulerStatus={schedulerStatus}
          onRunNow={() => runScreeningRef.current("manual")}
          realtimeScreening={realtimeScreening}
          setRealtimeScreening={setRealtimeScreening}
          transactions={transactions}
        />
      )}
    </div>
  );
}